import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Search,
  RefreshCw,
  Printer,
  CreditCard,
  FileCheck2,
  CheckCircle2,
  Clock3,
  AlertCircle,
  X,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SearchButton } from "@/components/Shared";
import { useSearchParams } from "react-router-dom";

const sortFinanceRowsByTransactionNewest = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) return rows;

  const hasTransaction = rows.some((row) =>
    row &&
    typeof row === "object" &&
    String(
      row.id_transaksi ||
      row.transaction_id ||
      ""
    ).trim()
  );

  if (!hasTransaction) return rows;

  return [...rows].sort((a, b) => {
    const idA = String(
      a?.id_transaksi ||
      a?.transaction_id ||
      ""
    ).trim();
    const idB = String(
      b?.id_transaksi ||
      b?.transaction_id ||
      ""
    ).trim();

    if (!idA && !idB) return 0;
    if (!idA) return 1;
    if (!idB) return -1;

    return idB.localeCompare(
      idA,
      undefined,
      { numeric: true, sensitivity: "base" }
    );
  });
};


const MASTER_FEE_REGIONS = [
  "JAWA",
  "BALI",
  "NTB_NTT",
  "SUMATERA",
  "KALIMANTAN",
  "SULAWESI",
  "MALUKU",
  "PAPUA",
];

const MASTER_FEE_REGION_LABELS = {
  JAWA: "Jawa",
  BALI: "Bali",
  NTB_NTT: "NTB & NTT",
  SUMATERA: "Sumatera",
  KALIMANTAN: "Kalimantan",
  SULAWESI: "Sulawesi",
  MALUKU: "Maluku",
  PAPUA: "Papua",
};
const rupiah = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const statusLabel = (status) => {
  if (status === "LUNAS") return "LUNAS";
  if (status === "DIBAYAR_SEBAGIAN") return "SEBAGIAN";
  return "BELUM DIBAYAR";
};

function StatusBadge({ status }) {
  const cls =
    status === "LUNAS"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : status === "DIBAYAR_SEBAGIAN"
        ? "bg-amber-100 text-amber-700 border-amber-200"
        : "bg-red-100 text-red-700 border-red-200";

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${cls}`}
    >
      {status === "LUNAS" && <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
      {status === "DIBAYAR_SEBAGIAN" && (
        <Clock3 className="mr-1 h-3.5 w-3.5" />
      )}
      {status !== "LUNAS" && status !== "DIBAYAR_SEBAGIAN" && (
        <AlertCircle className="mr-1 h-3.5 w-3.5" />
      )}
      {statusLabel(status)}
    </span>
  );
}

function Invoice({ transaction, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const batchNo = transaction?.nomor_pembayaran_korda || transaction?.nomor_referensi || '-';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!batchNo || batchNo === '-') {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get('/finance/korda-payment-batch/' + encodeURIComponent(batchNo));
        if (!cancelled) setData(res.data || null);
      } catch (error) {
        console.error('Gagal mengambil batch pembayaran Korda:', error);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [batchNo]);

  if (!transaction) return null;

  const items = Array.isArray(data?.items) && data.items.length
    ? data.items
    : [transaction].filter(Boolean);

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return String(value).slice(0, 10);
    }
  };

  const term = data?.term || transaction.term || '';
  const termLabel = term === 'PELUNASAN' ? 'PELUNASAN 50%' : 'TERMIN 1 50%';
  const korwil = data?.korwil || transaction.korwil || '-';
  const korda = data?.korda || transaction.korda || '-';
  const tanggal = data?.tanggal || transaction.tanggal_pembayaran;

  const handleDownloadPdf = () => {
    const ascii = (value) => String(value ?? '-').replace(/[^\x20-\x7E]/g, (ch) => ch === '→' ? '->' : '?');
    const esc = (value) => ascii(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
    const textWidth = (value, size, bold = false) => ascii(value).length * size * (bold ? 0.56 : 0.54);
    const fit = (value, width, size, bold = false) => {
      let text = ascii(value).trim() || '-';
      while (text.length > 1 && textWidth(text, size, bold) > width) text = text.slice(0, -1);
      return text;
    };
    const W = 595.28, H = 841.89, M = 30, PW = W - M * 2;
    const cols = [34, 100, 62, 180, 88, PW - 34 - 100 - 62 - 180 - 88];
    const titles = ['No.', 'ID TRANSAKSI', 'NPSN', 'NAMA SEKOLAH', 'PROV.', 'STATUS'];
    const cmds = [];
    const text = (value, x, y, size = 8, bold = false) => cmds.push('BT /F' + (bold ? 'F2' : 'F1') + ' ' + size + ' Tf ' + x.toFixed(2) + ' ' + y.toFixed(2) + ' Td (' + esc(value) + ') Tj ET');
    const line = (x1, y1, x2, y2) => cmds.push(x1 + ' ' + y1 + ' m ' + x2 + ' ' + y2 + ' l S');
    const rect = (x, y, w, h) => cmds.push(x + ' ' + y + ' ' + w + ' ' + h + ' re S');
    const cell = (value, x, y, w, size, bold = false) => { const t = fit(value, w - 10, size, bold); text(t, x + (w - textWidth(t, size, bold)) / 2, y, size, bold); };

    let y = H - 45;
    text('INVOICE PEMBAYARAN KORDA', M, y, 18, true); y -= 18;
    text('Pembayaran Korwil -> Korda', M, y, 9); y -= 10; line(M, y, W - M, y); y -= 25;
    const no = data?.nomor_transaksi || batchNo || '-';
    const date = formatDate(tanggal);
    const info = [['No. Transaksi', no, 'Tanggal', date], ['Korwil', korwil, 'Korda', korda], ['Termin', termLabel, 'Status', 'SUDAH DIBAYAR']];
    info.forEach((r) => { text(r[0], M, y, 8, true); text(': ' + fit(r[1], 170, 8), M + 68, y, 8); text(r[2], 315, y, 8, true); text(': ' + fit(r[3], 150, 8), 360, y, 8); y -= 15; });
    line(M, y + 5, W - M, y + 5); y -= 20; text('RINCIAN BAPP DI BAYAR', M, y, 9, true); y -= 16;

    let x = M, headerH = 19;
    cmds.push('0.94 g ' + M + ' ' + (y - headerH + 4) + ' ' + PW + ' ' + headerH + ' re f 0 g');
    cols.forEach((w, i) => { rect(x, y - headerH + 4, w, headerH); cell(titles[i], x, y - 9, w, 6.6, true); x += w; });
    y -= headerH;

    items.forEach((item, index) => {
      const values = [String(index + 1), item?.id_transaksi || '-', item?.npsn || '-', item?.nama_sekolah || item?.sekolah || '-', item?.provinsi || '-', termLabel];
      const rowH = 18;
      x = M;
      cols.forEach((w, i) => {
        rect(x, y - rowH + 4, w, rowH);
        cell(values[i], x, y - 10, w, i === 1 ? 5.4 : (i === 3 ? 5.8 : (i === 5 ? 5.8 : 6.1)), i === 5);
        x += w;
      });
      y -= rowH;
    });

    y -= 22; text('Jumlah BAPP:', M, y, 8, true); text(String(items.length) + ' transaksi', M + 62, y, 8); y -= 55;
    text('Korwil,', M + 105, y, 8); text('Finance,', W - M - 115, y, 8); y -= 38;
    line(M + 20, y, M + 220, y); line(W - M - 220, y, W - M - 20, y); y -= 12;
    text(korwil, M + 105, y, 8); text('Finance', W - M - 115, y, 8);

    const stream = cmds.join('\n');
    const objects = [
      '<< /Type /Catalog /Pages 2 0 R >>',
      '<< /Type /Pages /Kids [5 0 R] /Count 1 >>',
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
      '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>',
      '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + W + ' ' + H + '] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents 6 0 R >>',
      '<< /Length ' + new TextEncoder().encode(stream).length + ' >>\nstream\n' + stream + '\nendstream'
    ];
    let pdf = '%PDF-1.4\n%PDF\n';
    const offsets = [0];
    objects.forEach((obj, i) => { offsets.push(new TextEncoder().encode(pdf).length); pdf += (i + 1) + ' 0 obj\n' + obj + '\nendobj\n'; });
    const xref = new TextEncoder().encode(pdf).length;
    pdf += 'xref\n0 ' + (objects.length + 1) + '\n0000000000 65535 f \n';
    offsets.slice(1).forEach((off) => { pdf += String(off).padStart(10, '0') + ' 00000 n \n'; });
    pdf += 'trailer\n<< /Size ' + (objects.length + 1) + ' /Root 1 0 R >>\nstartxref\n' + xref + '\n%%EOF';
    const blob = new Blob([new TextEncoder().encode(pdf)], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Invoice-' + String(no).replace(/[^a-zA-Z0-9_-]/g, '_') + '.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const handlePrint = () => {
    const area = document.querySelector('.invoice-print-area');
    if (!area) return;
    const printWindow = window.open('', '_blank', 'width=900,height=1200');
    if (!printWindow) {
      window.alert('Popup print diblokir browser. Izinkan popup untuk Web Korda lalu coba lagi.');
      return;
    }
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((node) => {
        if (node.tagName === 'STYLE') return '<style>' + node.textContent + '</style>';
        const href = node.href || node.getAttribute('href');
        return href ? '<link rel="stylesheet" href="' + href + '">' : '';
      }).join('');
    const printHtml = '<!doctype html><html><head><meta charset="utf-8" />' +
      '<title> </title>' +
      styles + '<style>' +
      '@page { size: A4 portrait; margin: 0; }' +
      'html, body { margin: 0; padding: 0; background: #fff; }' +
      'body { color: #111827; font-family: Arial, Helvetica, sans-serif; }' +
      '.print-root { width: 100%; box-sizing: border-box; padding: 8mm; background: #fff; }' +
      '.invoice-print-area { width: 100% !important; max-width: none !important; box-sizing: border-box; background: #fff !important; margin: 0 !important; padding: 0 !important; }' +
      '.invoice-print-area table { width: 100%; table-layout: fixed; border-collapse: collapse; page-break-inside: auto; font-size: 6.6px !important; line-height: 1 !important; }' +
      '.invoice-print-area thead { display: table-header-group; }' +
      '.invoice-print-area tr { page-break-inside: avoid; page-break-after: auto; }' +
      '.invoice-print-area th, .invoice-print-area td { vertical-align: middle !important; padding: 1px 1.5px !important; line-height: 1 !important; white-space: nowrap !important; overflow: hidden !important; text-overflow: clip !important; }' +
      '.invoice-print-area th:nth-child(1), .invoice-print-area td:nth-child(1) { width: 5%; text-align: center !important; }' +
      '.invoice-print-area th:nth-child(2), .invoice-print-area td:nth-child(2) { width: 21%; font-size: 5.8px !important; }' +
      '.invoice-print-area th:nth-child(3), .invoice-print-area td:nth-child(3) { width: 9%; }' +
      '.invoice-print-area th:nth-child(4), .invoice-print-area td:nth-child(4) { width: 34%; }' +
      '.invoice-print-area th:nth-child(5), .invoice-print-area td:nth-child(5) { width: 16%; }' +
      '.invoice-print-area th:nth-child(6), .invoice-print-area td:nth-child(6) { width: 15%; text-align: center !important; font-size: 5.8px !important; }' +
      '.invoice-print-area th { text-align: center !important; }' +
      '.invoice-print-area .mt-7 { margin-top: 0.8rem !important; }' +
      '.invoice-print-area .mt-8 { margin-top: 0.7rem !important; }' +
      '.invoice-print-area .mt-12 { margin-top: 0.8rem !important; }' +
      '.invoice-print-area .mb-16 { margin-bottom: 1.2rem !important; }' +
      '.invoice-print-area * { box-shadow: none !important; }' +
      '@media print { html, body { width: 210mm; min-height: 297mm; } .print-root { width: 100%; } }' +
      '</style></head><body><div class="print-root">' + area.innerHTML + '</div></body></html>';
    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
    const doPrint = () => {
      printWindow.focus();
      printWindow.print();
      setTimeout(() => printWindow.close(), 250);
    };
    if (printWindow.document.readyState === 'complete') setTimeout(doPrint, 300);
    else printWindow.onload = () => setTimeout(doPrint, 300);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4">
      <div className="mx-auto flex min-h-full max-w-6xl items-center justify-center">
        <div className="w-full rounded-xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b px-6 py-4 print:hidden">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Invoice Pembayaran Korda</h2>
              <p className="text-xs text-slate-500">Satu invoice untuk satu batch pembayaran Korda.</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={handleDownloadPdf} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">PDF</button><button type="button" onClick={handlePrint} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white">Print</button>
              <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Tutup</button>
            </div>
          </div>
          <div className="invoice-print-area p-8 text-neutral-900">
            <div className="border-b-2 border-neutral-900 pb-5">
              <div className="text-2xl font-bold">INVOICE PEMBAYARAN KORDA</div>
              <div className="mt-1 text-sm text-neutral-500">Pembayaran Korwil → Korda</div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-x-10 gap-y-2 border-b pb-5 text-sm">
              <div><b>No. Transaksi</b> : <span className="font-mono">{data?.nomor_transaksi || batchNo}</span></div>
              <div><b>Tanggal</b> : {formatDate(tanggal)}</div>
              <div><b>Korwil</b> : {korwil}</div>
              <div><b>Korda</b> : {korda}</div>
              <div><b>Termin</b> : <b>{termLabel}</b></div>
              <div><b>Status</b> : <b>SUDAH DIBAYAR</b></div>
            </div>
            <div className="mt-7 mb-3 text-sm font-bold">RINCIAN BAPP DI BAYAR</div>
            {loading ? <div className="py-10 text-center text-sm">Memuat rincian pembayaran...</div> : (
              <table className="w-full border-collapse text-xs">
                <thead><tr className="bg-slate-100">
                  <th className="border border-slate-400 px-2 py-2">No.</th>
                  <th className="border border-slate-400 px-2 py-2">ID TRANSAKSI</th>
                  <th className="border border-slate-400 px-2 py-2">NPSN</th>
                  <th className="border border-slate-400 px-2 py-2">NAMA SEKOLAH</th>
                  <th className="border border-slate-400 px-2 py-2">PROV.</th>
                  <th className="border border-slate-400 px-2 py-2">STATUS</th>
                </tr></thead>
                <tbody>{sortFinanceRowsByTransactionNewest(items).map((item, index) => (
                  <tr key={item?.id_transaksi || index}>
                    <td className="border border-slate-400 px-2 py-2 text-center">{index + 1}</td>
                    <td className="border border-slate-400 px-2 py-2 font-mono">{item?.id_transaksi || '-'}</td>
                    <td className="border border-slate-400 px-2 py-2">{item?.npsn || '-'}</td>
                    <td className="border border-slate-400 px-2 py-2">{item?.nama_sekolah || item?.sekolah || '-'}</td>
                    <td className="border border-slate-400 px-2 py-2">{item?.provinsi || '-'}</td>
                    <td className="border border-slate-400 px-2 py-2 font-semibold">{termLabel}</td>
                  </tr>
                ))}</tbody>
              </table>
            )}
            <div className="mt-8 text-sm"><b>Jumlah BAPP:</b> {items.length} transaksi</div>
            <div className="mt-8 grid grid-cols-2 gap-12 text-center text-sm">
              <div><div className="mb-8">Korwil,</div><div className="border-t border-neutral-700 pt-2">{korwil}</div></div>
              <div><div className="mb-8">Finance,</div><div className="border-t border-neutral-700 pt-2">Finance</div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KordaPaymentBatchPrint({ batch, onClose }) {
  if (!batch) return null;

  const items = Array.isArray(batch.items) ? batch.items : [];
  const total = Number(batch.total || 0);
  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("id-ID", {
        day: "2-digit", month: "long", year: "numeric",
      });
    } catch {
      return String(value).slice(0, 10);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4">
      <div className="mx-auto min-h-full max-w-5xl py-6">
        <div className="rounded-xl bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b px-6 py-4 print:hidden">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Bukti Pembayaran Korda</h2>
              <p className="text-xs text-slate-500">Bukti pembayaran Korwil → Korda per batch.</p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white">
                <Printer className="h-4 w-4" /> Print
              </button>
              <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">Tutup</button>
            </div>
          </div>

          <div className="korda-batch-print-area p-8 text-neutral-900">
            <div className="border-b-2 border-neutral-900 pb-5">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="text-2xl font-bold">BUKTI PEMBAYARAN KORDA</div>
                  <div className="mt-1 text-sm text-neutral-500">Korwil → Korda</div>
                </div>
                <div className="text-right text-sm">
                  <div className="font-semibold">No. Pembayaran</div>
                  <div className="font-mono text-base">{batch.no_pembayaran || "-"}</div>
                  <div className="mt-1">Tanggal: {formatDate(items[0]?.tanggal_pembayaran)}</div>
                </div>
              </div>
            </div>

            <div className="grid gap-6 border-b py-5 md:grid-cols-3 text-sm">
              <div><div className="text-xs font-bold uppercase text-neutral-500">Korwil</div><div className="mt-1 font-semibold">{items[0]?.korwil || "-"}</div></div>
              <div><div className="text-xs font-bold uppercase text-neutral-500">Korda</div><div className="mt-1 font-semibold">{items[0]?.korda || "-"}</div></div>
              <div><div className="text-xs font-bold uppercase text-neutral-500">Jumlah Transaksi</div><div className="mt-1 font-semibold">{items.length} ID Transaksi</div></div>
            </div>

            <table className="w-full text-sm">
              <thead><tr className="border-b-2 border-neutral-900"><th className="py-3 text-left">No</th><th className="py-3 text-left">ID Transaksi</th><th className="py-3 text-left">Teknisi</th><th className="py-3 text-left">No. Setoran</th><th className="py-3 text-left">Termin</th><th className="py-3 text-right">Nominal</th></tr></thead>
              <tbody>
                {sortFinanceRowsByTransactionNewest(items).map((item, index) => (
                  <tr key={`${item.id_transaksi}-${item.term}-${index}`} className="border-b">
                    <td className="py-2">{index + 1}</td>
                    <td className="py-2 font-mono">{item.id_transaksi}</td>
                    <td className="py-2">{item.teknisi_nama || "-"}<div className="text-xs text-neutral-500">{item.teknisi_kode || "-"}</div></td>
                    <td className="py-2 font-mono text-xs">{item.no_setoran || "-"}</td>
                    <td className="py-2">{item.term === "TERMIN_1" ? "TERMIN 1" : item.term === "PELUNASAN" ? "PELUNASAN" : item.term || "-"}</td>
                    <td className="py-2 text-right font-semibold">{rupiah(item.jumlah_dibayar)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan="5" className="pt-4 text-right text-base font-bold">TOTAL PEMBAYARAN</td><td className="pt-4 text-right text-lg font-bold">{rupiah(total)}</td></tr></tfoot>
            </table>

            <div className="mt-8 rounded-lg border border-neutral-300 p-4 text-sm">
              <div className="font-bold">Keterangan</div>
              <div className="mt-1">Pembayaran di atas merupakan pembayaran Korwil kepada Korda untuk ID transaksi yang tercantum dalam batch ini.</div>
            </div>

            <div className="mt-12 grid grid-cols-2 gap-12 text-center text-sm">
              <div><div className="mb-16">KORWIL</div><div className="border-t pt-2">Yang Membayar</div></div>
              <div><div className="mb-16">KORDA</div><div className="border-t pt-2">Yang Menerima</div></div>
            </div>
          </div>
        </div>
      </div>
      <style>{`@media print { body * { visibility: hidden; } .korda-batch-print-area, .korda-batch-print-area * { visibility: visible; } .korda-batch-print-area { position: absolute; left: 0; top: 0; width: 100%; background: white; padding: 10mm; } @page { size: A4 landscape; margin: 8mm; } }`}</style>
    </div>
  );
}

function FinancePage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const financeRole = String(user?.role || "")
    .trim()
    .toUpperCase();

  const isSuperAdmin =
    financeRole === "SUPER_ADMIN";

  const canReviewKordaBapp =
    ["KORWIL", "ADMIN_KORWIL", "SUPER_ADMIN"].includes(financeRole);

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [kordaSetoranStatusFilter, setKordaSetoranStatusFilter] = useState("ALL");
  const [kordaSetoranSearch, setKordaSetoranSearch] = useState("");
  const [appliedKordaSetoranSearch, setAppliedKordaSetoranSearch] = useState("");
  const [appliedKordaSetoranStatusFilter, setAppliedKordaSetoranStatusFilter] = useState("ALL");

  // Dashboard Finance menggunakan endpoint khusus.
  // Tidak bergantung pada pagination transactions.
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [status, setStatus] = useState("ALL");
  const [appliedStatus, setAppliedStatus] = useState("ALL");

  // Filter Korwil khusus Dashboard Finance.
  // SUPER_ADMIN dapat memilih semua / Korwil tertentu.
  // KORWIL otomatis terkunci pada Korwil miliknya.
  // KORDA menggunakan menu Finance khusus BAPP.
  const [dashboardKorwil, setDashboardKorwil] = useState("ALL");

  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionPerPage, setTransactionPerPage] = useState(25);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionTotalPages, setTransactionTotalPages] = useState(0);

  // Finance menu
  const [korwilReviewLoading, setKorwilReviewLoading] = useState({});
  const [korwilRevisionOpen, setKorwilRevisionOpen] = useState(null);
  const [korwilRevisionNote, setKorwilRevisionNote] = useState("");

  const [activeMenu, setActiveMenu] = useState(() => {
    const role = String(user?.role || "")
      .trim()
      .toUpperCase();

    if (["KORDA", "KOPROV"].includes(role)) {
      return "korda_bapp";
    }

    if (role === "ADMIN_KORWIL") {
      return "setoran_bapp_pusat";
    }

    return "dashboard";
  });

  const [kordaBappDashboard, setKordaBappDashboard] = useState(null);
  const [kordaBappLoading, setKordaBappLoading] = useState(false);
  const [incomingKordaBapp, setIncomingKordaBapp] = useState([]);
  const [incomingKordaBappLoading, setIncomingKordaBappLoading] = useState(false);
  const [incomingKordaBappProcessing, setIncomingKordaBappProcessing] = useState("");
  const [incomingKordaBappCatatan, setIncomingKordaBappCatatan] = useState({});

  const [kordaBappAvailable, setKordaBappAvailable] = useState([]);
  const [kordaBappAvailableLoading, setKordaBappAvailableLoading] = useState(false);
  const [kordaBappSelected, setKordaBappSelected] = useState([]);
  const [kordaBappMessage, setKordaBappMessage] = useState("");

  // Sidebar Finance menggunakan query ?menu=... agar submenu dapat langsung membuka panel terkait.
  useEffect(() => {
    const requestedMenu = searchParams.get("menu");
    const role = String(user?.role || "")
      .trim()
      .toUpperCase();

    if (["KORDA", "KOPROV"].includes(role)) {
      setActiveMenu("korda_bapp");
      return;
    }

    if (requestedMenu) {
      setActiveMenu(requestedMenu);
    }
  }, [searchParams, user?.role]);


  // ============================================================
  // DEFAULT MENU FINANCE BERDASARKAN ROLE
  // KORDA -> langsung Dashboard BAPP Korda
  // KORWIL / SUPER_ADMIN -> Dashboard Finance existing
  // ============================================================
  useEffect(() => {
    const role = String(user?.role || "")
      .trim()
      .toUpperCase();

    // Jika Finance dibuka dari submenu melalui ?menu=...,
    // query menu harus menjadi sumber kebenaran. Jangan ditimpa
    // oleh default menu role saat mount.
    const requestedMenu = searchParams.get("menu");
    if (requestedMenu) {
      return;
    }

    if (["KORDA", "KOPROV"].includes(role)) {
      setActiveMenu("korda_bapp");
      return;
    }

    if (role === "ADMIN_KORWIL") {
      setActiveMenu("setoran_bapp_pusat");
      return;
    }

    if (
      role === "KORWIL" ||
      role === "SUPER_ADMIN"
    ) {
      setActiveMenu((current) =>
        current === "korda_bapp" ||
        current === "setoran_bapp_pusat" &&
        role === "SUPER_ADMIN"
          ? "dashboard"
          : current
      );
    }
  }, [user?.role, searchParams]);



  // ============================================================
  // SETORAN BAPP PUSAT
  // ============================================================
  const [bappData, setBappData] = useState([]);
  const [bappLoading, setBappLoading] = useState(false);
  const [bappSearch, setBappSearch] = useState("");
  const [bappStatusFilter, setBappStatusFilter] = useState("ALL");
  const [bappDirektoratFilter, setBappDirektoratFilter] = useState("ALL");
  const [appliedBappSearch, setAppliedBappSearch] = useState("");
  const [appliedBappStatusFilter, setAppliedBappStatusFilter] = useState("ALL");
  const [appliedBappDirektoratFilter, setAppliedBappDirektoratFilter] = useState("ALL");

  // Pagination Setoran BAPP
  const [bappPage, setBappPage] = useState(1);
  const [bappPerPage, setBappPerPage] = useState(20);
  const [bappTotal, setBappTotal] = useState(0);
  const [bappTotalPages, setBappTotalPages] = useState(0);

  const [bappModalOpen, setBappModalOpen] = useState(false);
  const [bappSaving, setBappSaving] = useState(false);
  const [bappAvailable, setBappAvailable] = useState([]);
  const [bappForm, setBappForm] = useState({
    id_transaksi: "",
    direktorat: "",
    nomor_bapp: "",
    tanggal_setor: "",
    keterangan: "",
  });

  const [selected, setSelected] = useState(null);
  const [selectedKordaBatch, setSelectedKordaBatch] = useState(null);
  const [paying, setPaying] = useState(null);

  // Bulk payment
  // PUSAT -> KORWIL
  // KORWIL -> KORDA
  const [bulkPaymentOpen, setBulkPaymentOpen] = useState(false);
  const [bulkPaymentMode, setBulkPaymentMode] = useState("PUSAT");
  const [bulkSelectedIds, setBulkSelectedIds] = useState([]);
  const [bulkPaymentSearch, setBulkPaymentSearch] = useState("");
  const [bulkPaidTermIds, setBulkPaidTermIds] = useState([]);
  const [bulkPaymentHistoryLoading, setBulkPaymentHistoryLoading] = useState(false);

  const [bulkTransactions, setBulkTransactions] = useState([]);
  const [bulkPage, setBulkPage] = useState(1);
  const [bulkPerPage, setBulkPerPage] = useState(25);
  const [bulkTotal, setBulkTotal] = useState(0);
  const [bulkTotalPages, setBulkTotalPages] = useState(0);
  const [bulkTransactionsLoading, setBulkTransactionsLoading] = useState(false);
  const [bulkTerm, setBulkTerm] = useState("TERMIN_1");
  const [bulkKordaFilter, setBulkKordaFilter] = useState("");

  const bulkKordaOptions = useMemo(() => {
    const values = new Map();
    for (const item of bulkTransactions || []) {
      const raw = String(item?.korda || "").trim();
      if (!raw) continue;
      const key = raw.toUpperCase();
      if (!values.has(key)) values.set(key, raw);
    }
    return Array.from(values.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([value, label]) => ({ value, label }));
  }, [bulkTransactions]);

  const filteredBulkTransactions = useMemo(() => {
    const selected = String(bulkKordaFilter || "").trim().toUpperCase();
    if (!selected) return [];
    return (bulkTransactions || []).filter(
      (item) => String(item?.korda || "").trim().toUpperCase() === selected
    );
  }, [bulkTransactions, bulkKordaFilter]);

  const visibleBulkTransactions =
    bulkPaymentMode === "KORDA" && bulkKordaFilter
      ? filteredBulkTransactions
      : bulkTransactions;

  useEffect(() => {
    // PUSAT payment does not require a Korda selection.
    if (bulkPaymentMode !== "KORDA") return;

    if (!bulkKordaFilter) {
      if ((bulkSelectedIds || []).length) setBulkSelectedIds([]);
      return;
    }

    const allowed = new Set(
      filteredBulkTransactions.map((item) => item?.id_transaksi).filter(Boolean)
    );
    const current = bulkSelectedIds || [];
    const next = current.filter((id) => allowed.has(id));
    if (next.length !== current.length) setBulkSelectedIds(next);
  }, [bulkPaymentMode, bulkKordaFilter, filteredBulkTransactions, bulkSelectedIds]);
  const [bulkPaymentDate, setBulkPaymentDate] = useState("");
  const [bulkReference, setBulkReference] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [savingBulkPayment, setSavingBulkPayment] = useState(false);
  const [bulkFailureDetails, setBulkFailureDetails] = useState([]);

  // Manual payment status
  const [statusEditing, setStatusEditing] = useState(null);
  const [manualStatus, setManualStatus] = useState("");
  const [manualStatusNote, setManualStatusNote] = useState("");
  const [savingManualStatus, setSavingManualStatus] = useState(false);

  const [message, setMessage] = useState("");

  // Master Fee
  const [feeSettings, setFeeSettings] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [feeEditing, setFeeEditing] = useState(null);
  const [feeForm, setFeeForm] = useState({
    kabupaten_kota: "",
    korda: "",
    fee_pusat: "",
    fee_korda: "",
    active: true,
  });
  const [feeSaving, setFeeSaving] = useState(false);

  // ==========================================================
  // MASTER FEE MATRIX
  // ==========================================================

  const [feeCellEditing, setFeeCellEditing] =
    useState(null);

  const [feeCellValue, setFeeCellValue] =
    useState("");

  const [addKordaOpen, setAddKordaOpen] =
    useState(false);

  const [newKordaName, setNewKordaName] =
    useState("");

  const [newKordaDefaultFee, setNewKordaDefaultFee] =
    useState("");

  const feeMatrix = useMemo(() => {
    const rows = {};

    MASTER_FEE_REGIONS.forEach(
      (regional) => {
        rows[regional] = {
          pusat: null,
          korda: {},
        };
      }
    );

    for (const fee of feeSettings) {
      if (fee.active === false) continue;

      const regional = String(
        fee.regional || ""
      )
        .trim()
        .toUpperCase();

      if (!rows[regional]) continue;

      const jenis = String(
        fee.jenis_fee || ""
      )
        .trim()
        .toUpperCase();

      const nominal = Number(
        fee.nominal_fee ??
        fee.fee_korda?.nominal ??
        fee.fee_pusat?.nominal ??
        fee.fee_korda ??
        fee.fee_pusat ??
        0
      );

      if (
        jenis === "PUSAT" &&
        !fee.korda
      ) {
        rows[regional].pusat = {
          ...fee,
          nominal,
        };
      }

      if (
        jenis === "KORDA" &&
        fee.korda
      ) {
        rows[regional].korda[
          String(
            fee.korda
          )
            .trim()
            .toUpperCase()
        ] = {
          ...fee,
          nominal,
        };
      }
    }

    return rows;
  }, [feeSettings]);

  const kordaColumns = useMemo(() => {
    const names = new Set();

    Object.values(
      feeMatrix
    ).forEach((row) => {
      Object.keys(
        row.korda || {}
      ).forEach((korda) => {
        names.add(korda);
      });
    });

    return Array.from(
      names
    ).sort();
  }, [feeMatrix]);

  const startFeeCellEdit = (
    regional,
    jenis,
    korda,
    nominal
  ) => {
    setFeeCellEditing({
      regional,
      jenis,
      korda: korda || null,
    });

    setFeeCellValue(
      nominal === undefined ||
      nominal === null
        ? ""
        : String(nominal)
    );
  };

  const saveFeeCell = async () => {
    if (!feeCellEditing) return;

    const nominal = Number(
      feeCellValue || 0
    );

    if (nominal < 0) {
      setMessage(
        "Nominal fee tidak boleh negatif."
      );
      return;
    }

    const {
      regional,
      jenis,
      korda,
    } = feeCellEditing;

    const existing =
      jenis === "PUSAT"
        ? feeMatrix[
            regional
          ]?.pusat
        : feeMatrix[
            regional
          ]?.korda?.[
            String(
              korda || ""
            ).toUpperCase()
          ];

    const payload = {
      nama_rule:
        `${jenis} ${regional}${
          korda
            ? ` - ${korda}`
            : ""
        }`,

      jenis_fee:
        jenis,

      regional:
        regional,

      korda:
        jenis === "KORDA"
          ? String(
              korda || ""
            ).toUpperCase()
          : null,

      nominal_fee:
        nominal,

      aktif: true,

      prioritas:
        jenis === "PUSAT"
          ? 10
          : 20,
    };

    try {
      setFeeSaving(true);

      if (existing?.id) {
        await api.put(
          `/finance/fee-settings/${existing.id}`,
          payload
        );
      } else {
        await api.post(
          "/finance/fee-settings",
          payload
        );
      }

      setFeeCellEditing(null);
      setFeeCellValue("");

      await loadFeeSettings();

      setMessage(
        `${
          jenis === "PUSAT"
            ? "Tarif Pusat"
            : `Fee ${korda}`
        } untuk ${
          MASTER_FEE_REGION_LABELS[
            regional
          ]
        } berhasil disimpan.`
      );
    } catch (error) {
      console.error(error);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal menyimpan Master Fee."
      );
    } finally {
      setFeeSaving(false);
    }
  };

  const handleAddKorda = async () => {
    const korda =
      newKordaName
        .trim()
        .toUpperCase();

    const nominal = Number(
      newKordaDefaultFee || 0
    );

    if (!korda) {
      setMessage(
        "Nama Korda wajib diisi."
      );
      return;
    }

    if (nominal < 0) {
      setMessage(
        "Fee default tidak boleh negatif."
      );
      return;
    }

    try {
      setFeeSaving(true);

      for (
        const regional
        of MASTER_FEE_REGIONS
      ) {
        const existing =
          feeMatrix[
            regional
          ]?.korda?.[
            korda
          ];

        if (existing?.id) {
          continue;
        }

        await api.post(
          "/finance/fee-settings",
          {
            nama_rule:
              `KORDA ${regional} - ${korda}`,

            jenis_fee:
              "KORDA",

            regional:

              regional,

            korda:
              korda,

            nominal_fee:
              nominal,

            aktif:
              true,

            prioritas:
              20,
          }
        );
      }

      await loadFeeSettings();

      setAddKordaOpen(false);
      setNewKordaName("");
      setNewKordaDefaultFee("");

      setMessage(
        `Korda ${korda} berhasil ditambahkan ke seluruh regional.`
      );
    } catch (error) {
      console.error(error);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal menambahkan Korda."
      );
    } finally {
      setFeeSaving(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);

      const isPaymentMenu =
        activeMenu === "pusat" ||
        activeMenu === "korda";

      const params = isPaymentMenu
        ? {
            page: transactionPage,
            per_page: transactionPerPage,
            term: bulkTerm,
            q: search.trim() || undefined,
          }
        : {
            page: transactionPage,
            per_page: transactionPerPage,
            q: search.trim() || undefined,
            status_pembayaran:
              status !== "ALL"
                ? status
                : undefined,
          };

      const endpoint =
        activeMenu === "korda"
          ? "/finance/korda-payment-eligible-ids"
          : activeMenu === "pusat"
            ? "/finance/payment-eligible-ids"
            : "/finance/transactions";

      const res = await api.get(
        endpoint,
        { params }
      );

      const data = res.data || {};

      setTransactions(
        Array.isArray(data.items)
          ? data.items
          : []
      );

      setTransactionTotal(
        Number(data.total || 0)
      );

      setTransactionTotalPages(
        Number(data.total_pages || 0)
      );
    } catch (error) {
      console.error(error);

      setTransactions([]);
      setTransactionTotal(0);
      setTransactionTotalPages(0);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil data Finance."
      );
    } finally {
      setLoading(false);
    }
  };

  const applyTransactionFilters = () => {
    setTransactionPage(1);
    setAppliedSearch(search);
    setAppliedStatus(status);
  };

  const loadFeeSettings = async () => {
    try {
      setFeeLoading(true);

      const res = await api.get("/finance/fee-settings");

      setFeeSettings(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      setMessage("Gagal mengambil Master Fee.");
    } finally {
      setFeeLoading(false);
    }
  };

  const openFeeCreate = () => {
    setFeeEditing(null);
    setFeeForm({
      kabupaten_kota: "",
      korda: "",
      fee_pusat: "",
      fee_korda: "",
      active: true,
    });
    setFeeModalOpen(true);
  };

  const openFeeEdit = (fee) => {
    setFeeEditing(fee);

    setFeeForm({
      kabupaten_kota:
        fee.kabupaten_kota ||
        fee.daerah ||
        fee.kabupaten ||
        "",
      korda: fee.korda || "",
      fee_pusat:
        fee.fee_pusat?.nominal ??
        fee.fee_pusat ??
        "",
      fee_korda:
        fee.fee_korda?.nominal ??
        fee.fee_korda ??
        "",
      active:
        fee.active !== false &&
        fee.status !== "NONAKTIF",
    });

    setFeeModalOpen(true);
  };

  const handleFeeSave = async () => {
    if (
      !feeForm.kabupaten_kota.trim() ||
      !feeForm.korda.trim() ||
      !feeForm.fee_korda
    ) {
      setMessage(
        "Daerah, Korda, dan Fee Korda wajib diisi."
      );
      return;
    }

    try {
      setFeeSaving(true);

      const payload = {
        kabupaten_kota:
          feeForm.kabupaten_kota.trim().toUpperCase(),
        korda:
          feeForm.korda.trim().toUpperCase(),
        fee_pusat: Number(feeForm.fee_pusat || 0),
        fee_korda: Number(feeForm.fee_korda || 0),
        active: Boolean(feeForm.active),
      };

      if (feeEditing?.id) {
        await api.put(
          `/finance/fee-settings/${feeEditing.id}`,
          payload
        );
        setMessage("Master Fee berhasil diperbarui.");
      } else {
        await api.post(
          "/finance/fee-settings",
          payload
        );
        setMessage("Master Fee berhasil ditambahkan.");
      }

      setFeeModalOpen(false);
      setFeeEditing(null);

      await loadFeeSettings();
    } catch (error) {
      console.error(error);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal menyimpan Master Fee."
      );
    } finally {
      setFeeSaving(false);
    }
  };

  const handleFeeToggle = async (fee) => {
    if (!fee?.id) return;

    try {
      await api.patch(
        `/finance/fee-settings/${fee.id}/status`,
        {
          active: !(
            fee.active !== false &&
            fee.status !== "NONAKTIF"
          ),
        }
      );

      setMessage(
        "Status Master Fee berhasil diperbarui."
      );

      await loadFeeSettings();
    } catch (error) {
      console.error(error);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengubah status Master Fee."
      );
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        const isPaymentMenu =
          activeMenu === "pusat" ||
          activeMenu === "korda";

        const params = isPaymentMenu
          ? {
              page: transactionPage,
              per_page: transactionPerPage,
              q: search.trim() || undefined,
            }
          : {
              page: transactionPage,
              per_page: transactionPerPage,
              q: appliedSearch.trim() || undefined,
              status_pembayaran:
                appliedStatus !== "ALL"
                  ? appliedStatus
                  : undefined,
            };

        const endpoint =
          activeMenu === "korda"
            ? "/finance/korda-payment-eligible-ids"
            : activeMenu === "pusat"
              ? "/finance/payment-eligible-ids"
              : "/finance/transactions";

        const res = await api.get(
          endpoint,
          { params }
        );

        if (cancelled) return;

        const data = res.data || {};

        setTransactions(
          Array.isArray(data.items)
            ? data.items
            : []
        );

        setTransactionTotal(
          Number(data.total || 0)
        );

        setTransactionTotalPages(
          Number(data.total_pages || 0)
        );
      } catch (error) {
        if (cancelled) return;

        console.error(error);

        setTransactions([]);
        setTransactionTotal(0);
        setTransactionTotalPages(0);

        setMessage(
          error?.response?.data?.detail ||
          "Gagal mengambil data Finance."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    transactionPage,
    transactionPerPage,
    appliedSearch,
    appliedStatus,
    activeMenu,
  ]);

  useEffect(() => {
    if (activeMenu === "master_fee") {
      loadFeeSettings();
    }
  }, [activeMenu]);



  const loadIncomingKordaBapp = useCallback(async () => {
    try {
      setIncomingKordaBappLoading(true);

      const res = await api.get(
        "/finance/bapp-korda/incoming"
      );

      const data = res?.data || {};

      setIncomingKordaBapp(
        Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.items)
            ? data.items
            : []
      );
    } catch (error) {
      console.error(
        "Gagal mengambil incoming BAPP KORDA:",
        error
      );

      setIncomingKordaBapp([]);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil BAPP dari KORDA."
      );
    } finally {
      setIncomingKordaBappLoading(false);
    }
  }, []);

  const reviewIncomingKordaBapp = async (
    idTransaksi,
    action
  ) => {
    const id = String(idTransaksi || "").trim();

    if (!id) {
      setMessage("ID transaksi tidak tersedia.");
      return;
    }

    let catatan = "";

    if (action === "REVISI") {
      catatan =
        String(
          incomingKordaBappCatatan[id] || ""
        ).trim();

      if (!catatan) {
        setMessage(
          `Catatan revisi wajib diisi untuk ${id}.`
        );
        return;
      }
    }

    try {
      setIncomingKordaBappProcessing(id);
      setMessage("");

      await api.patch(
        `/finance/bapp-korda/incoming/${encodeURIComponent(id)}/review`,
        {
          action,
          catatan,
        }
      );

      await loadIncomingKordaBapp();

      setIncomingKordaBappCatatan((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });

      setMessage(
        action === "TERIMA"
          ? `BAPP ${id} berhasil diterima.`
          : `BAPP ${id} dikembalikan untuk revisi.`
      );

      if (typeof loadKordaBappDashboard === "function") {
        await loadKordaBappDashboard();
      }
    } catch (error) {
      console.error(
        "Gagal review BAPP KORDA:",
        error
      );

      setMessage(
        error?.response?.data?.detail ||
        `Gagal memproses ${id}.`
      );
    } finally {
      setIncomingKordaBappProcessing("");
    }
  };

const loadKordaBappDashboard = useCallback(async () => {
    try {
      setKordaBappLoading(true);

      const res = await api.get(
        "/finance/bapp-korda/dashboard",
        { params: { _t: Date.now() } }
      );

      setKordaBappDashboard(
        res.data || null
      );
    } catch (error) {
      console.error(
        "Gagal mengambil Dashboard BAPP Korda:",
        error
      );

      setKordaBappDashboard(null);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil Dashboard BAPP Korda."
      );
    } finally {
      setKordaBappLoading(false);
    }
  }, []);

  const loadKordaBappAvailable = useCallback(async () => {
    try {
      setKordaBappAvailableLoading(true);

      const res = await api.get(
        "/finance/bapp-korda/available",
        { params: { _t: Date.now() } }
      );

      const data = res.data;

      if (Array.isArray(data)) {
        setKordaBappAvailable(data);
      } else if (
        Array.isArray(data?.data)
      ) {
        setKordaBappAvailable(data.data);
      } else if (
        Array.isArray(data?.items)
      ) {
        setKordaBappAvailable(data.items);
      } else {
        setKordaBappAvailable([]);
      }
    } catch (error) {
      console.error(
        "Gagal mengambil BAPP Korda:",
        error
      );

      setKordaBappAvailable([]);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil daftar BAPP."
      );
    } finally {
      setKordaBappAvailableLoading(false);
    }
  }, []);

  useEffect(() => {
    if (
      ["KORDA", "KOPROV"].includes(user?.role) &&
      activeMenu === "korda_bapp"
    ) {
      loadKordaBappDashboard();
      loadKordaBappAvailable();
    }
  }, [
    user?.role,
    activeMenu,
    loadKordaBappDashboard,
    loadKordaBappAvailable,
  ]);


  useEffect(() => {
    if (
      activeMenu === "setoran_bapp_pusat" &&
      canReviewKordaBapp
    ) {
      loadIncomingKordaBapp();
    }
  }, [
    activeMenu,
    canReviewKordaBapp,
    loadIncomingKordaBapp,
  ]);

  const reviewKordaBapp = async (
    noSetoran,
    idTransaksi,
    action,
    catatanRevisi = ""
  ) => {
    const key = `${noSetoran}__${idTransaksi}`;

    if (
      action === "REVISI" &&
      !String(catatanRevisi || "").trim()
    ) {
      setMessage(
        "Catatan revisi wajib diisi."
      );
      return;
    }

    try {
      setKordaBappSelected((prev) => prev);

      setKorwilReviewLoading((prev) => ({
        ...prev,
        [key]: true,
      }));

      const res = await api.post(
        `/finance/bapp-korda/setoran/${encodeURIComponent(noSetoran)}/review`,
        {
          id_transaksi: idTransaksi,
          action,
          catatan_revisi:
            action === "REVISI"
              ? String(catatanRevisi).trim()
              : "",
        }
      );

      setMessage(
        res?.data?.message ||
        `ID ${idTransaksi} berhasil diproses.`
      );

      setKorwilRevisionOpen(null);
      setKorwilRevisionNote("");

      await loadKordaBappDashboard();
    } catch (error) {
      console.error(
        "Gagal melakukan review BAPP KORWIL:",
        error
      );

      setMessage(
        error?.response?.data?.detail ||
        "Gagal memproses review BAPP."
      );
    } finally {
      setKorwilReviewLoading((prev) => ({
        ...prev,
        [key]: false,
      }));
    }
  };

  const getKordaBappId = (item) =>
    String(
      item?.id_transaksi ||
      item?.id ||
      ""
    );

  const getKordaBappNpsn = (item) =>
    String(
      item?.npsn ||
      item?.NPSN ||
      item?.npsn_sekolah ||
      "NPSN BELUM ADA"
    );

  const getKordaBappSchool = (item) =>
    String(
      item?.nama_sekolah ||
      item?.sekolah ||
      item?.nama ||
      "-"
    );

  const getSubmittedData = () => {
    const data =
      kordaBappDashboard?.data ||
      kordaBappDashboard?.items ||
      [];

    return Array.isArray(data) ? data : [];
  };

  const kordaBappGroups = Object.values(
    kordaBappAvailable.reduce(
      (groups, item) => {
        const npsn = getKordaBappNpsn(item);

        if (!groups[npsn]) {
          groups[npsn] = {
            npsn,
            sekolah: getKordaBappSchool(item),
            items: [],
          };
        }

        groups[npsn].items.push(item);

        return groups;
      },
      {}
    )
  ).sort((a, b) =>
    a.npsn.localeCompare(
      b.npsn,
      undefined,
      { numeric: true }
    )
  );

  const toggleKordaBapp = (id) => {
    const key = String(id);

    setKordaBappSelected((prev) =>
      prev.includes(key)
        ? prev.filter((x) => x !== key)
        : [...prev, key]
    );
  };

  const toggleKordaBappGroup = (items) => {
    const ids = items
      .map(getKordaBappId)
      .filter(Boolean)
      .map(String);

    setKordaBappSelected((prev) => {
      const allSelected =
        ids.length > 0 &&
        ids.every((id) =>
          prev.includes(id)
        );

      if (allSelected) {
        return prev.filter(
          (id) => !ids.includes(id)
        );
      }

      return Array.from(
        new Set([...prev, ...ids])
      );
    });
  };

  const loadDashboard = useCallback(async () => {
    try {
      setDashboardLoading(true);

      const params = {};

      if (
        isSuperAdmin &&
        dashboardKorwil !== "ALL"
      ) {
        params.korwil = dashboardKorwil;
      }

      const res = await api.get(
        "/finance/dashboard",
        {
          params,
        }
      );

      setDashboardData(res.data || null);
    } catch (error) {
      console.error(
        "Gagal mengambil Dashboard Finance:",
        error
      );

      setDashboardData(null);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil data Dashboard Finance."
      );
    } finally {
      setDashboardLoading(false);
    }
  }, [
    isSuperAdmin,
    dashboardKorwil,
  ]);

  useEffect(() => {
    if (activeMenu === "dashboard") {
      loadDashboard();
    }
  }, [
    activeMenu,
    loadDashboard,
  ]);

  // ============================================================
  // LOAD SETORAN BAPP PUSAT
  // ============================================================
  const loadBappSetoran = useCallback(async (pageOverride = bappPage) => {
    try {
      setBappLoading(true);

      const params = {
          page: pageOverride,
          per_page: bappPerPage,
          _t: Date.now(),
        };

      if (appliedBappSearch.trim()) {
        params.q = appliedBappSearch.trim();
      }

      if (appliedBappStatusFilter !== "ALL") {
        params.status_setoran = appliedBappStatusFilter;
      }

      if (appliedBappDirektoratFilter !== "ALL") {
        params.direktorat = appliedBappDirektoratFilter;
      }

      const res = await api.get("/finance/bapp-setoran", { params });

      setBappData(res.data?.data || []);

        setBappTotal(
          Number(res.data?.total || 0)
        );

        setBappTotalPages(
          Number(
            res.data?.total_pages ||
            Math.ceil(
              Number(res.data?.total || 0) /
              bappPerPage
            )
          )
        );
    } catch (error) {
      console.error("Gagal mengambil data Setoran BAPP:", error);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil data Setoran BAPP Pusat."
      );

      setBappData([]);
    } finally {
      setBappLoading(false);
    }
  }, [
      appliedBappSearch,
      appliedBappStatusFilter,
      appliedBappDirektoratFilter,
      bappPage,
      bappPerPage,
    ]);

  const applyBappFilters = () => {
    setBappPage(1);
    setAppliedBappSearch(bappSearch);
    setAppliedBappStatusFilter(bappStatusFilter);
    setAppliedBappDirektoratFilter(bappDirektoratFilter);
  };

  // ID transaksi yang dipilih untuk Setoran BAPP
  const [selectedBappIds, setSelectedBappIds] = useState([]);

  const loadBappAvailable = async () => {
    try {
      const params = {};

      if (bappSearch.trim()) {
        params.q = bappSearch.trim();
      }

      const res = await api.get(
        "/finance/bapp-setoran/available",
        { params }
      );

      setBappAvailable(res.data?.data || []);
    } catch (error) {
      console.error("Gagal mengambil ID transaksi BAPP:", error);

      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil ID transaksi yang tersedia."
      );

      setBappAvailable([]);
    }
  };

  const openBappModal = async () => {
    setSelectedBappIds([]);

    setBappForm({
      id_transaksi: "",
      direktorat: "",
      nomor_bapp: "",
      tanggal_setor: new Date().toISOString().slice(0, 10),
      keterangan: "",
    });

    setBappModalOpen(true);
    await loadBappAvailable();
  };

  const toggleBappId = (id) => {
    const trxId = String(id || "").trim();

    if (!trxId) return;

    setSelectedBappIds((prev) =>
      prev.includes(trxId)
        ? prev.filter((x) => x !== trxId)
        : [...prev, trxId]
    );
  };

  const toggleBappNpsn = (npsn, items) => {
    const ids = items
      .map((x) => String(x.id_transaksi || "").trim())
      .filter(Boolean);

    setSelectedBappIds((prev) => {
      const allSelected = ids.every((id) => prev.includes(id));

      if (allSelected) {
        return prev.filter((id) => !ids.includes(id));
      }

      return [
        ...prev,
        ...ids.filter((id) => !prev.includes(id)),
      ];
    });
  };

const printBappPusatSetoran = async (noSetoran) => {
    const no = String(noSetoran || "").trim();
    if (!no) { setMessage("Nomor setoran tidak tersedia."); return; }

    try {
      const res = await api.get("/finance/bapp-setoran", { params: { q: no, page: 1, per_page: 100 } });
      const rows = Array.isArray(res?.data?.data)
        ? res.data.data.filter((x) => String(x?.no_setoran || "") === no)
        : [];
      if (!rows.length) throw new Error(`Data setoran ${no} tidak ditemukan.`);

      const unique = (items) => [...new Set(sortFinanceRowsByTransactionNewest(items).map((x) => String(x ?? "").trim()).filter(Boolean))];
      const fmtDate = (value) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
        return d.toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
      };

      const koordinator = unique(sortFinanceRowsByTransactionNewest(rows).map((x) => x?.korwil || x?.koordinator))[0] || "-";
      const direktorat = unique(sortFinanceRowsByTransactionNewest(rows).map((x) => x?.direktorat || x?.jenjang)).join(", ") || "-";
      const tanggal = fmtDate(rows[0]?.tanggal_setor || rows[0]?.tanggal_setoran || rows[0]?.created_at);

      // Render satu halaman A4 ke canvas agar seluruh karakter UTF-8 tetap benar.
      const canvas = document.createElement("canvas");
      const scale = 2;
      canvas.width = 595 * scale;
      canvas.height = 842 * scale;
      const ctx = canvas.getContext("2d");
      ctx.scale(scale, scale);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 595, 842);
      ctx.fillStyle = "#111827";
      ctx.textBaseline = "alphabetic";

      const text = (value, x, y, size = 9, weight = "400", align = "left", color = "#111827") => {
        ctx.font = `${weight} ${size}px Arial, sans-serif`;
        ctx.textAlign = align;
        ctx.fillStyle = color;
        ctx.fillText(String(value ?? "-"), x, y);
      };
      const line = (x1, y1, x2, y2, width = 0.7) => {
        ctx.strokeStyle = "#374151";
        ctx.lineWidth = width;
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      };
      const rect = (x, y, w, h, fill, stroke = null) => {
        ctx.fillStyle = fill; ctx.fillRect(x, y, w, h);
        if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 0.6; ctx.strokeRect(x, y, w, h); }
      };
      const clipText = (value, maxWidth, size = 7.2) => {
        const v = String(value ?? "-");
        ctx.font = `400 ${size}px Arial, sans-serif`;
        if (ctx.measureText(v).width <= maxWidth) return v;
        let s = v;
        while (s.length > 3 && ctx.measureText(`${s}...`).width > maxWidth) s = s.slice(0, -1);
        return `${s}...`;
      };

      // Gunakan asset logo PYXSCREEN yang sama dengan aplikasi, bukan menggambar ulang logo.
      // Asset diambil dari public/pyxscreen-logo.svg agar bentuk dan proporsinya konsisten.
      const logo = new Image();
      logo.src = `${window.location.origin}/pyxscreen-logo.svg`;
      await new Promise((resolve) => {
        logo.onload = resolve;
        logo.onerror = resolve;
      });
      if (logo.complete && logo.naturalWidth > 0) {
        ctx.drawImage(logo, 40, 34, 72, 72);
      }

      text("FORMULIR BAPP", 297.5, 54, 19, "700", "center", "#6b7280");
      text("DAFTAR SEKOLAH PENGIRIMAN KOORDINATOR", 297.5, 82, 11, "700", "center");
      // Tidak ada garis di bawah kop.

      const info = [["Direktorat", direktorat], ["Koordinator", koordinator], ["Tanggal Pengiriman", tanggal], ["Jumlah Dokumen", String(rows.length)]];
      info.forEach(([label, value], i) => {
        const y = 130 + i * 18;
        text(label, 40, y, 8.5); text(":", 138, y, 8.5); text(value, 148, y, 8.5);
      });

      const left = 40, tableW = 515, top = 250, rowH = 21;
      const cols = [31, 132, 82, 180, 90];
      const headers = ["No", "ID Transaksi", "NPSN", "Nama Sekolah", "Provinsi"];
      rect(left, top, tableW, rowH, "#dbeafe", "#374151");
      let x = left;
      cols.forEach((w, i) => {
        if (i > 0) line(x, top, x, top + rowH, 0.5);
        text(headers[i], x + w / 2, top + 14, 7.2, "700", "center"); x += w;
      });
      rows.forEach((row, i) => {
        const y = top + rowH + i * rowH;
        rect(left, y, tableW, rowH, "#ffffff", "#374151");
        let xx = left;
        const vals = [String(i + 1), row?.id_transaksi || "-", row?.npsn || "-", row?.sekolah || row?.nama_sekolah || "-", row?.provinsi || row?.propinsi || "-"];
        cols.forEach((w, ci) => {
          if (ci > 0) line(xx, y, xx, y + rowH, 0.5);
          const v = clipText(vals[ci], w - (ci === 3 ? 8 : 6), ci === 1 ? 6.2 : 6.8);
          text(v, ci === 3 ? xx + 4 : xx + w / 2, y + 14, ci === 1 ? 6.2 : 6.8, "400", ci === 3 ? "left" : "center");
          xx += w;
        });
      });

      // Footer tetap ada, tetapi area tanda tangan KORWIL sengaja dikosongkan.
      text("Lampiran Setoran BAPP KORWIL -> Pusat PYX", 40, 795, 7.5, "400", "left", "#4b5563");
      text(`Jakarta, ${tanggal}`, 555, 795, 7.5, "400", "right", "#4b5563");

      const jpeg = canvas.toDataURL("image/jpeg", 0.95);
      const b64 = jpeg.split(",")[1];
      const binary = atob(b64);
      const imgBytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) imgBytes[i] = binary.charCodeAt(i);

      // PDF satu halaman A4 berisi satu JPEG. Tidak memakai window.print().
      const enc = new TextEncoder();
      const chunks = [];
      const offsets = [0];
      let byteLength = 0;
      const pushText = (s) => { const b = enc.encode(s); chunks.push(b); byteLength += b.length; };
      const pushBytes = (b) => { chunks.push(b); byteLength += b.length; };
      pushText("%PDF-1.4\n");
      const addObj = (id, body, streamBytes = null) => {
        offsets[id] = byteLength;
        pushText(`${id} 0 obj\n${body}\n`);
        if (streamBytes) { pushText("stream\n"); pushBytes(streamBytes); pushText("\nendstream\n"); }
        pushText("endobj\n");
      };
      addObj(1, "<< /Type /Catalog /Pages 2 0 R >>");
      addObj(2, "<< /Type /Pages /Kids [3 0 R] /Count 1 >>");
      addObj(3, "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /XObject << /Im1 5 0 R >> >> /Contents 4 0 R >>");
      const pageStream = enc.encode("q\n595 0 0 842 0 0 cm\n/Im1 Do\nQ\n");
      addObj(4, `<< /Length ${pageStream.length} >>`, pageStream);
      addObj(5, `<< /Type /XObject /Subtype /Image /Width ${canvas.width} /Height ${canvas.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${imgBytes.length} >>`, imgBytes);
      const xref = byteLength;
      pushText(`xref\n0 6\n0000000000 65535 f \n`);
      for (let i = 1; i <= 5; i++) pushText(`${String(offsets[i]).padStart(10, "0")} 00000 n \n`);
      pushText(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`);

      const pdfBytes = new Uint8Array(byteLength);
      let pos = 0;
      for (const chunk of chunks) { pdfBytes.set(chunk, pos); pos += chunk.length; }
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const pdfWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (!pdfWindow) {
        const a = document.createElement("a"); a.href = url; a.download = `Formulir-BAPP-${no}.pdf`; a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error(err);
      setMessage(err?.response?.data?.detail || err?.message || "Gagal membuat PDF formulir BAPP.");
    }
  };

  const saveBappSetoran = async () => {
    const ids =
      selectedBappIds.length > 0
        ? selectedBappIds
        : bappForm.id_transaksi
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);

    if (!ids.length) {
      setMessage("Silakan pilih minimal satu ID transaksi.");
      return;
    }

    setBappSaving(true);

    // Only the POST request belongs to the failure path.
    let res;
    try {
      res = await api.post("/finance/bapp-setoran", {
        id_transaksi: ids,
        direktorat: bappForm.direktorat,
        nomor_bapp: bappForm.nomor_bapp,
        tanggal_setor: bappForm.tanggal_setor,
        keterangan: bappForm.keterangan,
      });
    } catch (error) {
      console.error("Gagal menyimpan Setoran BAPP:", error);
      setMessage(
        error?.response?.data?.detail?.message ||
        error?.response?.data?.detail ||
        "Gagal menyimpan Setoran BAPP."
      );
      setBappSaving(false);
      return;
    }

    const responseData = Array.isArray(res?.data?.data)
      ? res.data.data
      : [];

    const savedData =
      responseData.length > 0
        ? responseData
        : bappAvailable.filter((item) =>
            ids.includes(String(item?.id_transaksi || "").trim())
          );

    const savedIds = new Set(
      savedData
        .map((item) => String(item?.id_transaksi || "").trim())
        .filter(Boolean)
    );

    setBappData((prev) => {
      const rows = Array.isArray(prev) ? prev : [];
      const existingIds = new Set(
        rows
          .map((item) => String(item?.id_transaksi || "").trim())
          .filter(Boolean)
      );

      const updated = sortFinanceRowsByTransactionNewest(rows).map((item) => {
        const trxId = String(item?.id_transaksi || "").trim();
        if (!savedIds.has(trxId)) return item;

        const savedItem =
          savedData.find(
            (x) => String(x?.id_transaksi || "").trim() === trxId
          ) || {};

        return {
          ...item,
          ...savedItem,
          status_setoran: "SUDAH_SETOR",
          tanggal_setor:
            savedItem.tanggal_setor || bappForm.tanggal_setor,
          nomor_bapp:
            savedItem.nomor_bapp || bappForm.nomor_bapp,
          keterangan:
            savedItem.keterangan || bappForm.keterangan,
        };
      });

      const newRows = savedData.filter(
        (item) =>
          !existingIds.has(
            String(item?.id_transaksi || "").trim()
          )
      );

      return [...newRows, ...updated];
    });

    setBappModalOpen(false);
    setSelectedBappIds([]);
    setBappAvailable([]);

    setBappForm({
      id_transaksi: "",
      direktorat: "",
      nomor_bapp: "",
      tanggal_setor: new Date()
        .toISOString()
        .slice(0, 10),
      keterangan: "",
    });

    setMessage(
      ids.length + " ID transaksi berhasil disetor ke pusat. Status: SUDAH SETOR."
    );

    // Refresh is best-effort only and cannot turn a successful POST into an error.
    void loadBappSetoran().catch((refreshError) => {
      console.warn(
        "Refresh tabel Setoran BAPP setelah POST:",
        refreshError
      );
    });

    setBappSaving(false);
  };

  useEffect(() => {
    if (activeMenu === "setoran_bapp_pusat") {
      loadBappSetoran();
    }
  }, [
    activeMenu,
    bappStatusFilter,
    bappDirektoratFilter,
    loadBappSetoran,
  ]);

  // Data sudah difilter oleh backend.
  // Jangan filter ulang di browser karena pagination
  // dilakukan secara server-side.
  const filtered = transactions;

  const summary = useMemo(() => {
    let belum = 0;
    let sebagian = 0;
    let lunas = 0;
    let feePusat = 0;
    let dibayarPusat = 0;
    let sisaPusat = 0;

    for (const x of transactions) {
      const st =
        x.status_pembayaran_pusat ||
        "BELUM_DIBAYAR";

      if (st === "LUNAS") {
        lunas++;
      } else if (
        st === "DIBAYAR_SEBAGIAN"
      ) {
        sebagian++;
      } else {
        belum++;
      }

      feePusat += Number(
        x.fee_pusat?.nominal ??
        x.fee_pusat ??
        0
      );

      dibayarPusat += Number(
        x.total_diterima_pusat ||
        0
      );

      sisaPusat += Number(
        x.sisa_pembayaran_pusat ??
        Math.max(
          0,
          Number(
            x.fee_pusat?.nominal ??
            x.fee_pusat ??
            0
          ) -
          Number(
            x.total_diterima_pusat ||
            0
          )
        )
      );
    }

    return {
      total: transactionTotal,
      belum,
      sebagian,
      lunas,
      feePusat,
      dibayarPusat,
      sisaPusat,
    };
  }, [
    transactions,
    transactionTotal,
  ]);

  const handleManualStatus = async () => {
    if (!statusEditing?.id_transaksi || !manualStatus) return;

    try {
      setSavingManualStatus(true);

      await api.patch(
        `/finance/transactions/${statusEditing.id_transaksi}/status`,
        {
          status: manualStatus,
          catatan:
            manualStatusNote.trim() ||
            "Perubahan status pembayaran secara manual",
        }
      );

      setMessage(
        `Status pembayaran ${statusEditing.id_transaksi} berhasil diubah menjadi ${manualStatus}.`
      );

      setStatusEditing(null);
      setManualStatus("");
      setManualStatusNote("");

      await loadTransactions();
    } catch (error) {
      console.error(error);

      setMessage(
        error?.response?.data?.detail ||
          "Gagal mengubah status pembayaran."
      );
    } finally {
      setSavingManualStatus(false);
    }
  };

  useEffect(() => {
    setBulkPage(1);
  }, [bulkPaymentSearch]);

  useEffect(() => {
    if (!bulkPaymentOpen) return;

    let cancelled = false;

    const load = async () => {
      try {
        setBulkTransactionsLoading(true);

        const endpoint =
          bulkPaymentMode === "KORDA"
            ? "/finance/korda-payment-eligible-ids"
            : "/finance/payment-eligible-ids";

        const res = await api.get(
          endpoint,
          {
            params: {
              term: bulkTerm,
              page: bulkPage,
              per_page: bulkPerPage,
              q:
                bulkPaymentSearch.trim() ||
                undefined,
            },
          }
        );

        if (cancelled) return;

        const data = res.data || {};

        const rows = Array.isArray(data.items)
          ? data.items
          : [];

        setBulkTransactions(rows);

        setBulkTotal(
          Number(data.total || 0)
        );

        setBulkTotalPages(
          Number(data.total_pages || 0)
        );

        setBulkPaidTermIds(
          rows
            .filter(
              (item) =>
                item.sudah_dibayar === true
            )
            .map(
              (item) =>
                item.id_transaksi
            )
        );

        setBulkSelectedIds((prev) =>
          prev.filter(
            (id) =>
              !rows.some(
                (item) =>
                  item.id_transaksi === id &&
                  item.sudah_dibayar === true
              )
          )
        );
      } catch (err) {
        if (cancelled) return;

        console.error(err);

        setBulkTransactions([]);
        setBulkTotal(0);
        setBulkTotalPages(0);
        setBulkPaidTermIds([]);

        setMessage(
          err?.response?.data?.detail ||
          "Gagal mengambil daftar ID transaksi."
        );
      } finally {
        if (!cancelled) {
          setBulkTransactionsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    bulkPaymentOpen,
    bulkPaymentMode,
    bulkTerm,
    bulkPage,
    bulkPerPage,
    bulkPaymentSearch,
  ]);

  const toggleBulkPaymentId = (id) => {
    setBulkSelectedIds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const isBulkPusatTermPaid = (item, term) => {
    return term === "PELUNASAN"
      ? item?.pelunasan_sudah_dibayar === true
      : item?.termin_1_sudah_dibayar === true;
  };

  const isKordaTermEligible = (item) => {
    const pusatTermin1Paid = item?.termin_1_pusat_sudah_dibayar === true;
    const pusatPelunasanPaid = item?.pelunasan_pusat_sudah_dibayar === true;
    const kordaTermin1Paid = item?.termin_1_sudah_dibayar === true;
    const kordaPelunasanPaid = item?.pelunasan_sudah_dibayar === true;

    if (bulkTerm === "PELUNASAN") {
      return pusatPelunasanPaid && kordaTermin1Paid && !kordaPelunasanPaid;
    }

    return pusatTermin1Paid && !kordaTermin1Paid;
  };

  const isBulkPaymentEligible = (item) => {
    if (bulkPaymentMode === "KORDA") {
      return isKordaTermEligible(item);
    }

    const termin1Paid = isBulkPusatTermPaid(item, "TERMIN_1");
    const pelunasanPaid = isBulkPusatTermPaid(item, "PELUNASAN");
    return bulkTerm === "PELUNASAN"
      ? termin1Paid && !pelunasanPaid
      : !termin1Paid;
  };

  const toggleSelectAllBulkPayment = () => {
    const eligibleIds = bulkTransactions
      .filter((x) => {
        if (bulkPaymentMode === "KORDA") {
          return x.boleh_dibayar === true;
        }

        return (
          isBulkPaymentEligible(x) &&
          !bulkPaidTermIds.includes(
            x.id_transaksi
          )
        );
      })
      .map(
        (x) => x.id_transaksi
      );

    if (eligibleIds.length === 0) {
      return;
    }

    setBulkSelectedIds((prev) => {
      const pageAlreadySelected =
        eligibleIds.every((id) =>
          prev.includes(id)
        );

      if (pageAlreadySelected) {
        // Hapus hanya pilihan di halaman ini.
        // Pilihan dari halaman lain tetap dipertahankan.
        return prev.filter(
          (id) =>
            !eligibleIds.includes(id)
        );
      }

      // Tambahkan pilihan halaman ini ke
      // pilihan yang sudah ada dari halaman lain.
      return Array.from(
        new Set([
          ...prev,
          ...eligibleIds,
        ])
      );
    });
  };

  const handleBulkPayment = async () => {
    if (bulkSelectedIds.length === 0) {
      setMessage("Pilih minimal satu ID transaksi.");
      return;
    }

    try {
      setSavingBulkPayment(true);
      setMessage("");

      const endpoint =
        bulkPaymentMode === "KORDA"
          ? "/finance/korda-payments/bulk"
          : "/finance/payments/bulk";

      const res = await api.post(
        endpoint,
        {
          id_transaksi: bulkSelectedIds,
          term: bulkTerm,
          tanggal_pembayaran:
            bulkPaymentDate || undefined,
          nomor_referensi:
            bulkReference.trim() || undefined,
          catatan:
            bulkNote.trim() || undefined,
        }
      );

      const data = res.data || {};

      const gagal = Array.isArray(data.gagal) ? data.gagal : [];
      const failureDetails = sortFinanceRowsByTransactionNewest(gagal).map((item) => {
        const detail = typeof item?.detail === "object"
          ? (item.detail?.message || JSON.stringify(item.detail))
          : (item?.detail || "Alasan tidak tersedia");
        return {
          id_transaksi: item?.id_transaksi || "ID",
          detail,
        };
      });

      setBulkFailureDetails(failureDetails);

      setMessage(
        failureDetails.length > 0
          ? `Pembayaran selesai: ${data.total_berhasil || 0} berhasil, ${data.total_gagal || 0} gagal.`
          : `Pembayaran berhasil diproses: ${data.total_berhasil || 0} berhasil, ${data.total_gagal || 0} gagal.`
      );

      // The bulk payment endpoint can finish the database writes even when
      // the response reports row-level failures. Treat a completed POST as
      // a completed form submission: close/reset immediately, then refresh
      // the active PUSAT/KORDA table in the background.
      setBulkPaymentOpen(false);
      setBulkSelectedIds([]);
      setBulkPaymentSearch("");
      setBulkPaidTermIds([]);
      setBulkPaymentDate("");
      setBulkReference("");
      setBulkNote("");

      // Refresh the same main payment endpoint used by the Finance table.
      // loadTransactions() uses /finance/transactions, while the PUSAT/KORDA
      // table is loaded from the payment-eligible endpoints.
      const refreshEndpoint =
        bulkPaymentMode === "KORDA"
          ? "/finance/korda-payment-eligible-ids"
          : "/finance/payment-eligible-ids";

      const refreshRes = await api.get(
        refreshEndpoint,
        {
          params: {
            page: transactionPage,
            per_page: transactionPerPage,
            q: search.trim() || undefined,
          },
        }
      );

      const refreshData = refreshRes.data || {};

      setTransactions(
        Array.isArray(refreshData.items)
          ? refreshData.items
          : []
      );

      setTransactionTotal(
        Number(refreshData.total || 0)
      );

      setTransactionTotalPages(
        Number(refreshData.total_pages || 0)
      );
    } catch (error) {
      console.error(error);

      setMessage(
        error?.response?.data?.detail ||
          "Pembayaran bulk gagal diproses."
      );
    } finally {
      setSavingBulkPayment(false);
    }
  };

  const handlePrintKordaBatch = async (transaction) => {
    const noPembayaran = String(transaction?.no_pembayaran || "").trim();
    if (!noPembayaran) {
      setMessage("Bukti pembayaran belum memiliki nomor pembayaran batch.");
      return;
    }

    try {
      const res = await api.get(
        `/finance/korda-payment-batch/${encodeURIComponent(noPembayaran)}`
      );
      setSelectedKordaBatch(res.data || null);
    } catch (error) {
      console.error(error);
      setMessage(
        error?.response?.data?.detail ||
        "Gagal mengambil bukti pembayaran Korda."
      );
    }
  };

  const handlePayment = async (transaction) => {
    if (!transaction?.id_transaksi) return;

    const sisa = Number(
      transaction.sisa_pembayaran_korda || 0
    );

    if (sisa <= 0) {
      setMessage("Transaksi ini sudah lunas.");
      return;
    }

    const ok = window.confirm(
      `Bayar Korda ${transaction.korda || ""} sebesar ${rupiah(sisa)}?`
    );

    if (!ok) return;

    try {
      setPaying(transaction.id_transaksi);

      await api.post(
        `/finance/korda-payments/${transaction.id_transaksi}`,
        {
          term: bulkTerm,
          jumlah_dibayar: sisa,
          catatan: "Pembayaran Korda melalui Finance - " + (
            bulkTerm === "PELUNASAN" ? "Pelunasan" : "Termin 1"
          ),
        }
      );

      setMessage(
        `Pembayaran Korda untuk ${transaction.id_transaksi} berhasil dicatat.`
      );

      await loadTransactions();
    } catch (error) {
      console.error(error);

      setMessage(
        error?.response?.data?.detail ||
          "Pembayaran gagal diproses."
      );
    } finally {
      setPaying(null);
    }
  };

  // ============================================================
  // ROLE RESTRICTION
  // ============================================================
  // KORDA:
  //   hanya KordaFinancePage
  //
  // ADMIN_KORWIL:
  //   hanya Setoran BAPP Pusat pada Finance utama.
  //   Menu Finance lainnya disembunyikan / tidak dirender
  //   pada bagian menu di bawah.
  // ============================================================

  // ============================================================
  // FINANCE KORDA
  // ============================================================
  // KORDA menggunakan halaman Finance yang terpisah.
  // Finance KORWIL / SUPER_ADMIN tidak disentuh.
  // ============================================================

  if (user?.role === "KORDA") {
    return (
      <KordaFinanceErrorBoundary>
        <KordaFinancePage
          user={user}
          api={api}
          dashboard={kordaBappDashboard}
          setDashboard={setKordaBappDashboard}
          dashboardLoading={kordaBappLoading}
          available={kordaBappAvailable}
          setAvailable={setKordaBappAvailable}
          availableLoading={kordaBappAvailableLoading}
          selected={kordaBappSelected}
          setSelected={setKordaBappSelected}
          message={kordaBappMessage}
          setMessage={setKordaBappMessage}
          loadDashboard={loadKordaBappDashboard}
          loadAvailable={loadKordaBappAvailable}
          getId={getKordaBappId}
          getNpsn={getKordaBappNpsn}
          activeMenu={activeMenu}
        />
      </KordaFinanceErrorBoundary>
    );
  }

  // ADMIN_KORWIL hanya boleh melihat Setoran BAPP Pusat.
  // Jika state aktif pernah terbawa dari role sebelumnya,
  // paksa kembali ke menu yang diizinkan.
  const effectiveActiveMenu =
    user?.role === "ADMIN_KORWIL"
      ? "setoran_bapp_pusat"
      : (user?.role !== "KORDA" && activeMenu === "pembayaran_bapp_korda"
          ? "dashboard"
          : activeMenu);

  return (
    <div className="space-y-6 p-6 text-slate-100">
      <style>{`
        /* ======================================================
           FINANCE TABLE SCROLL
           Setiap tabel data Finance memiliki area scroll sendiri.
           Header tabel tetap terlihat saat data di-scroll.
           Pagination/filter di luar wrapper tetap berada di halaman.
           ====================================================== */
        .finance-table-scroll {
          height: clamp(320px, calc(100vh - 390px), 620px) !important;
          max-height: calc(100vh - 390px) !important;
          min-height: 320px !important;
          overflow-x: auto !important;
          overflow-y: auto !important;
          overscroll-behavior: contain;
          position: relative;
          scrollbar-width: thin;
          scrollbar-gutter: stable;
        }

        /* Tabel Pembayaran Pusat/Korda: tinggi mengikuti jumlah baris,
           tetapi dibatasi dan memakai scroll jika datanya banyak. */
        .finance-payment-table-scroll {
          height: auto !important;
          max-height: min(620px, calc(100vh - 390px)) !important;
          min-height: 0 !important;
          overflow-y: auto !important;
        }

        .finance-korwil-review-scroll {
          height: 200px !important;
          min-height: 200px !important;
          max-height: 200px !important;
          overflow-y: auto !important;
        }

        .finance-korwil-review-scroll table {
          min-height: 0 !important;
        }

        .finance-table-scroll thead {
          position: sticky;
          top: 0;
          z-index: 30;
        }

        .finance-table-scroll thead th {
          position: sticky;
          top: 0;
          z-index: 31;
          background: #e2f3ea;
        }

        .finance-table-scroll table {
          border-collapse: separate;
          border-spacing: 0;
        }
      `}</style>
      {(() => {
        const menuHeader = {
          dashboard: { title: "Dashboard", description: "Ringkasan aktivitas dan kondisi keuangan Korwil." },
          setoran_bapp_pusat: { title: "Setoran BAPP Pusat", description: "Pencatatan BAPP yang sudah diterima KORWIL untuk disetor ke Pusat." },
          pusat: { title: "Pembayaran dari Pusat", description: "Pencatatan pembayaran Pusat kepada KORWIL." },
          korda: { title: "Pembayaran ke Korda", description: "Pencatatan pembayaran KORWIL kepada KORDA." },
          riwayat: { title: "Riwayat Pembayaran", description: "Riwayat seluruh pembayaran yang tercatat di Finance." },
          master_fee: { title: "Master Fee & Pembagian Margin", description: "Pengaturan fee dan pembagian margin berdasarkan wilayah dan koordinator." },
          operasional: { title: "Biaya Operasional", description: "Pencatatan dan pengelolaan biaya operasional Korwil." },
          laporan: { title: "Laporan Finance", description: "Laporan transaksi, pembayaran, kewajiban, dan margin Finance." },
        };
        const header = menuHeader[effectiveActiveMenu] || menuHeader.dashboard;
        return (
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div>
              <h1 className="text-2xl font-bold">{header.title}</h1>
              <p className="mt-1 text-sm text-slate-400">{header.description}</p>
            </div>

        {(effectiveActiveMenu === "pusat" ||
          effectiveActiveMenu === "korda") && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                const mode =
                  effectiveActiveMenu === "korda"
                    ? "KORDA"
                    : "PUSAT";

                setBulkPaymentMode(mode);
                setBulkSelectedIds([]);
                setBulkPaymentSearch("");
                setBulkPage(1);
                setBulkTransactions([]);
                setBulkPaidTermIds([]);
                setBulkTotal(0);
                setBulkTotalPages(0);
                setBulkPaymentOpen(true);
              }}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <CreditCard className="h-4 w-4" />
              {effectiveActiveMenu === "pusat"
                ? "Tambah Pembayaran Pusat"
                : "Pembayaran Korwil → Korda"}
            </button>

            <button
              onClick={loadTransactions}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  loading ? "animate-spin" : ""
                }`}
              />
              Refresh
            </button>
          </div>
        )}
          </div>
        );
      })()}

      {message && (
        <div className="rounded-lg border border-blue-900 bg-blue-950/40 px-4 py-3 text-sm text-blue-300">
          <div className="flex items-start justify-between gap-4">
            <span>{message}</span>

            <button
              onClick={() => {
                setMessage("");
                setBulkFailureDetails([]);
              }}
              className="font-bold"
            >
              ×
            </button>
          </div>

          {bulkFailureDetails.length > 0 && (
            <details className="mt-3 rounded-lg border border-blue-800/70 bg-slate-950/30 p-3">
              <summary className="cursor-pointer font-semibold">
                Lihat detail {bulkFailureDetails.length} transaksi gagal
              </summary>

              <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1 text-xs">
                {sortFinanceRowsByTransactionNewest(bulkFailureDetails).map((item, index) => (
                  <div
                    key={`${item.id_transaksi}-${index}`}
                    className="rounded border border-blue-900/70 px-3 py-2"
                  >
                    <div className="font-semibold text-blue-200">
                      {item.id_transaksi}
                    </div>
                    <div className="mt-1 text-blue-300/90">
                      {item.detail}
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}


      {effectiveActiveMenu === "korda_bapp" && ["KORDA", "KOPROV"].includes(user?.role) && (
        <div className="space-y-6">

          <div className="rounded-2xl border border-slate-800 bg-slate-950 p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">

              <div>
                <h2 className="text-xl font-semibold text-white">
                  Finance Korda
                </h2>

                <p className="mt-1 text-sm text-slate-400">
                  Dashboard dan setoran BAPP Korda ke Korwil
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  loadKordaBappDashboard();
                  loadKordaBappAvailable();
                }}
                disabled={
                  kordaBappLoading ||
                  kordaBappAvailableLoading
                }
                className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50"
              >
                ↻ Refresh
              </button>

            </div>
          </div>

          <div>
            <h3 className="mb-3 text-base font-semibold text-white">
              Dashboard BAPP
            </h3>

            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">

              {[
                [
                  "TOTAL",
                  kordaBappDashboard?.total ??
                  kordaBappDashboard?.total_id ??
                  0,
                ],
                [
                  "SUDAH SETOR",
                  kordaBappDashboard?.sudah_setor ??
                  kordaBappDashboard?.total_sudah_setor ??
                  0,
                ],
                [
                  "BELUM SETOR",
                  kordaBappDashboard?.belum_setor ??
                  kordaBappDashboard?.total_belum_setor ??
                  0,
                ],
                [
                  "MENUNGGU REVIEW",
                  kordaBappDashboard?.menunggu_review ??
                  kordaBappDashboard?.total_menunggu_review ??
                  0,
                ],
                [
                  "DITERIMA",
                  kordaBappDashboard?.diterima ??
                  kordaBappDashboard?.total_diterima ??
                  0,
                ],
                [
                  "REVISI",
                  kordaBappDashboard?.revisi ??
                  kordaBappDashboard?.total_revisi ??
                  0,
                ],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-xl border border-slate-800 bg-slate-950 p-4"
                >
                  <div className="text-xs font-medium text-slate-500">
                    {label}
                  </div>

                  <div className="mt-2 text-2xl font-bold text-white">
                    {kordaBappLoading
                      ? "..."
                      : value}
                  </div>

                  <div className="mt-1 text-xs text-slate-500">
                    ID transaksi
                  </div>
                </div>
              ))}

            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950">

            <div className="border-b border-slate-800 p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">

                <div>
                  <h3 className="text-base font-semibold text-white">
                    BAPP Belum Disetor ke Korwil
                  </h3>

                  <p className="mt-1 text-sm text-slate-400">
                    Pilih ID transaksi berdasarkan kelompok NPSN
                  </p>
                </div>

                <div className="text-sm text-slate-400">
                  Terpilih:
                  <span className="ml-1 font-semibold text-blue-400">
                    {kordaBappSelected.length}
                  </span>
                  {" "}ID
                </div>

              </div>
            </div>

            {kordaBappAvailableLoading ? (
              <div className="p-8 text-center text-sm text-slate-400">
                Memuat data BAPP...
              </div>
            ) : kordaBappGroups.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">
                Tidak ada BAPP yang belum disetor.
              </div>
            ) : (
              <div className="divide-y divide-slate-800">

                {sortFinanceRowsByTransactionNewest(kordaBappGroups).map((group) => {

                  const ids = group.items
                    .map(getKordaBappId)
                    .filter(Boolean)
                    .map(String);

                  const selectedCount =
                    ids.filter((id) =>
                      kordaBappSelected.includes(id)
                    ).length;

                  const allSelected =
                    ids.length > 0 &&
                    selectedCount === ids.length;

                  return (
                    <div
                      key={group.npsn}
                      className="bg-slate-950"
                    >

                      <div className="flex flex-col gap-3 bg-slate-900/80 px-5 py-4 md:flex-row md:items-center md:justify-between">

                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-base font-semibold text-white">
                              NPSN {group.npsn}
                            </span>

                            <span className="rounded-full bg-slate-700 px-2 py-1 text-xs text-slate-300">
                              {group.items.length} transaksi
                            </span>
                          </div>

                          <div className="mt-1 text-sm text-slate-400">
                            {group.sekolah}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            toggleKordaBappGroup(
                              group.items
                            )
                          }
                          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 hover:bg-slate-800"
                        >
                          {allSelected
                            ? "Batal Semua"
                            : "Pilih Semua"}
                        </button>

                      </div>

                      <div className="divide-y divide-slate-800/80">

                        {sortFinanceRowsByTransactionNewest(group.items).map((item) => {

                          const id =
                            getKordaBappId(item);

                          const checked =
                            kordaBappSelected.includes(
                              String(id)
                            );

                          return (
                            <label
                              key={String(id)}
                              className="flex cursor-pointer items-center gap-3 px-5 py-4 hover:bg-slate-900/50"
                            >

                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  toggleKordaBapp(id)
                                }
                                className="h-4 w-4 rounded border-slate-600 bg-slate-900"
                              />

                              <div>
                                <div className="font-mono text-sm font-semibold text-blue-400">
                                  {id}
                                </div>

                                {String(
                                  item?.status_review ||
                                  item?.status_setoran ||
                                  ""
                                ).trim().toUpperCase() === "REVISI_KORDA" && (
                                  <div className="mt-1 text-xs font-semibold text-red-400">
                                    REVISI — dapat dibatch ulang
                                  </div>
                                )}

                                <div className="mt-1 text-xs text-slate-500">
                                  {getKordaBappSchool(item)}
                                </div>
                              </div>

                            </label>
                          );
                        })}

                      </div>

                    </div>
                  );
                })}

              </div>
            )}

            {kordaBappSelected.length > 0 && (
              <div className="sticky bottom-0 border-t border-slate-800 bg-slate-950 p-5">

                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">

                  <div>
                    <div className="text-sm font-semibold text-white">
                      {kordaBappSelected.length} ID transaksi dipilih
                    </div>

                    <div className="mt-1 text-xs text-slate-500">
                      Fitur pembuatan No. Setoran akan diaktifkan
                      pada tahap berikutnya.
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white opacity-50"
                  >
                    Buat No. Setoran
                  </button>

                </div>

              </div>
            )}

          </div>

        </div>
      )}


      {/* ======================================================
          SETORAN BAPP - KORWIL
          REVIEW PER ID TRANSAKSI
          ====================================================== */}

      {effectiveActiveMenu === "korda_bapp" &&
        user?.role === "KORWIL" && (
          <div className="space-y-6">

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">

              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">

                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-600">
                    FINANCE
                  </div>

                  <h2 className="mt-1 text-2xl font-bold text-slate-900">
                    Setoran BAPP
                  </h2>

                  <p className="mt-1 text-sm text-slate-500">
                    Review BAPP dari KORDA berdasarkan setiap ID transaksi.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={loadKordaBappDashboard}
                  disabled={kordaBappLoading}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                >
                  <RefreshCw
                    size={15}
                    className={`mr-2 inline ${
                      kordaBappLoading
                        ? "animate-spin"
                        : ""
                    }`}
                  />
                  Refresh
                </button>

              </div>

              <div className="mt-6 finance-table-scroll overflow-x-auto">

                <table className="min-w-full text-sm">

                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-3">
                        No. Setoran
                      </th>

                      <th className="px-3 py-3">
                        ID Transaksi
                      </th>

                      <th className="px-3 py-3">
                        NPSN
                      </th>

                      <th className="px-3 py-3">
                        Sekolah
                      </th>

                      <th className="px-3 py-3">
                        KORDA
                      </th>

                      <th className="px-3 py-3">
                        Direktorat
                      </th>

                      <th className="px-3 py-3">
                        Status
                      </th>

                      <th className="px-3 py-3 text-center">
                        Aksi
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">

                    {getSubmittedData().length === 0 && (
                      <tr>
                        <td
                          colSpan={8}
                          className="px-4 py-12 text-center text-slate-500"
                        >
                          Belum ada Setoran BAPP dari KORDA.
                        </td>
                      </tr>
                    )}

                    {getSubmittedData().map((item) => {

                      const noSetoran =
                        String(
                          item?.no_setoran || ""
                        );

                      const idTransaksi =
                        String(
                          item?.id_transaksi || ""
                        );

                      const status =
                        String(
                          item?.status_review ||
                          item?.status_setoran ||
                          "MENUNGGU_REVIEW"
                        ).toUpperCase();

                      const rowKey =
                        `${noSetoran}__${idTransaksi}`;

                      const busy =
                        !!korwilReviewLoading[rowKey];

                      const revisiOpen =
                        korwilRevisionOpen === rowKey;

                      return (
                        <React.Fragment
                          key={rowKey}
                        >

                          <tr className="hover:bg-slate-50">

                            <td className="px-3 py-3 font-mono text-xs text-blue-600">
                              {noSetoran || "-"}
                            </td>

                            <td className="px-3 py-3 font-mono text-xs font-semibold text-slate-900">
                              {idTransaksi || "-"}
                            </td>

                            <td className="px-3 py-3 text-slate-700">
                              {item?.npsn || "-"}
                            </td>

                            <td className="px-3 py-3 font-medium text-slate-900">
                              {item?.nama_sekolah ||
                                item?.sekolah ||
                                "-"}
                            </td>

                            <td className="px-3 py-3 text-slate-700">
                              {item?.korda || "-"}
                            </td>

                            <td className="px-3 py-3 text-slate-700">
                              {item?.direktorat || "-"}
                            </td>

                            <td className="px-3 py-3">

                              <span
                                className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${
                                  status === "DITERIMA"
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : status === "REVISI"
                                      ? "border-red-200 bg-red-50 text-red-700"
                                      : "border-amber-200 bg-amber-50 text-amber-700"
                                }`}
                              >
                                {status === "MENUNGGU_REVIEW"
                                  ? "MENUNGGU REVIEW"
                                  : status}
                              </span>

                            </td>

                            <td className="px-3 py-3">

                              {status ===
                                "MENUNGGU_REVIEW" && (
                                <div className="flex flex-wrap justify-center gap-2">

                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() =>
                                      reviewKordaBapp(
                                        noSetoran,
                                        idTransaksi,
                                        "DITERIMA"
                                      )
                                    }
                                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                                  >
                                    {busy
                                      ? "..."
                                      : "Terima"}
                                  </button>

                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => {
                                      setKorwilRevisionOpen(
                                        rowKey
                                      );
                                      setKorwilRevisionNote(
                                        ""
                                      );
                                    }}
                                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
                                  >
                                    Revisi
                                  </button>

                                </div>
                              )}

                              {status === "REVISI" &&
                                item?.catatan_revisi && (
                                  <div className="max-w-xs text-xs text-red-600">
                                    {item.catatan_revisi}
                                  </div>
                                )}

                            </td>

                          </tr>

                          {revisiOpen && (
                            <tr>
                              <td
                                colSpan={8}
                                className="bg-red-50 px-4 py-4"
                              >

                                <div className="mx-auto max-w-3xl">

                                  <div className="mb-2 text-sm font-bold text-red-800">
                                    Catatan Revisi — {idTransaksi}
                                  </div>

                                  <textarea
                                    value={
                                      korwilRevisionNote
                                    }
                                    onChange={(e) =>
                                      setKorwilRevisionNote(
                                        e.target.value
                                      )
                                    }
                                    rows={3}
                                    placeholder="Jelaskan bagian BAPP yang harus diperbaiki..."
                                    className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
                                  />

                                  <div className="mt-3 flex justify-end gap-2">

                                    <button
                                      type="button"
                                      disabled={busy}
                                      onClick={() => {
                                        setKorwilRevisionOpen(
                                          null
                                        );
                                        setKorwilRevisionNote(
                                          ""
                                        );
                                      }}
                                      className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                    >
                                      Batal
                                    </button>

                                    <button
                                      type="button"
                                      disabled={
                                        busy ||
                                        !String(
                                          korwilRevisionNote
                                        ).trim()
                                      }
                                      onClick={() =>
                                        reviewKordaBapp(
                                          noSetoran,
                                          idTransaksi,
                                          "REVISI",
                                          korwilRevisionNote
                                        )
                                      }
                                      className="rounded-lg bg-red-600 px-4 py-2 text-xs font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      Simpan Revisi
                                    </button>

                                  </div>

                                </div>

                              </td>
                            </tr>
                          )}

                        </React.Fragment>
                      );
                    })}

                  </tbody>

                </table>

              </div>

            </div>

          </div>
        )}

{effectiveActiveMenu === "dashboard" && (
        <div className="space-y-4">

          {dashboardLoading && !dashboardData ? (
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-8 text-center">
              <div className="text-sm font-medium text-slate-300">
                Memuat Dashboard Finance...
              </div>
            </div>
          ) : (
            (() => {
              const d = dashboardData || {};
              const a = d.alokasi || {};
              const p = d.pembayaran_pusat || {};
              const k = d.pembayaran_korda || {};
              const m = d.margin || {};
              const s = d.status_pembayaran || {};
              const rows = Array.isArray(d.breakdown_korda)
                ? d.breakdown_korda
                : [];

              const rupiah = (value) => {
                const n = Number(value || 0);
                return new Intl.NumberFormat("id-ID", {
                  style: "currency",
                  currency: "IDR",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(n);
              };

              const number = (value) =>
                new Intl.NumberFormat("id-ID").format(Number(value || 0));

              const percent = (value, total) => {
                const v = Number(value || 0);
                const t = Number(total || 0);
                if (!t) return "0%";
                return `${((v / t) * 100).toFixed(1)}%`;
              };

              const totalId = Number(a.total || 0);
              const sudahSetor = Number(a.sudah_masuk_finance || 0);
              const belumSetor = Number(a.belum_masuk_finance || 0);

              const feePusat = Number(p.total_fee || 0);
              const dibayarPusat = Number(p.dibayar || 0);
              const outstandingPusat = Number(p.outstanding || 0);

              const feeKorda = Number(
                k.total_kewajiban_realisasi ??
                k.total_tagihan ??
                0
              );
              const dibayarKorda = Number(k.dibayar || 0);
              const outstandingKorda = Number(k.outstanding || 0);

              const grossKorwil = Number(m.margin_kotor || 0);
              const biayaOperasional = Number(
                d.biaya_operasional ||
                d.operasional?.total ||
                0
              );
              const nettKorwil =
                dibayarPusat - feeKorda - biayaOperasional;

              const paidCenterIds = Number(
                p.jumlah_id ||
                p.total_id ||
                (
                  Number(p.termin_1?.jumlah_id || 0) +
                  Number(p.pelunasan?.jumlah_id || 0)
                )
              );

              const paidKordaIds = Number(
                k.jumlah_id ||
                k.total_id ||
                0
              );

              return (
                <>
                  {/* ==================================================
                      HEADER
                     ================================================== */}
                  <div className="finance-dashboard-header rounded-2xl border border-slate-800 bg-slate-950 px-5 py-4 shadow-xl">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-400">
                          Finance Control Center
                        </div>

                        <h1 className="mt-1 text-xl font-bold tracking-tight text-white">
                          Dashboard Finance
                        </h1>

                        <p className="mt-1 text-xs text-slate-400">
                          Monitoring transaksi, pembayaran dan profit Korwil

              {isSuperAdmin && (
                <div
                  style={{
                    marginTop: "16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#8fa3c7",
                      fontWeight: 600,
                    }}
                  >
                    KORWIL
                  </span>

                  <select
                    value={dashboardKorwil}
                    onChange={(e) =>
                      setDashboardKorwil(e.target.value)
                    }
                    style={{
                      minWidth: "180px",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid #263653",
                      background: "#0b1220",
                      color: "#e5edf9",
                      fontSize: "13px",
                      outline: "none",
                    }}
                  >
                    <option value="ALL">
                      Semua Korwil
                    </option>

                    {(
                      dashboardData?.filter?.available_korwil ||
                      []
                    ).map((item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item}
                      </option>
                    ))}
                  </select>

                  {dashboardKorwil !== "ALL" && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#5eead4",
                        fontWeight: 600,
                      }}
                    >
                      Menampilkan data Korwil: {dashboardKorwil}
                    </span>
                  )}
                </div>
              )}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={loadDashboard}
                        disabled={dashboardLoading}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-xs font-semibold text-slate-200 transition hover:border-blue-500 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className={dashboardLoading ? "animate-spin" : ""}>
                          ↻
                        </span>
                        Refresh
                      </button>
                    </div>
                  </div>


                  {/* ==================================================
                      SIMPLE BAPP + FINANCE SUMMARY
                     ================================================== */}
                  <div className="space-y-4">

                    {/* STATUS BAPP */}
                    <div className="rounded-2xl border border-slate-800 bg-slate-950/90 p-5 shadow-xl">
                      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-[0.18em] text-blue-400">
                            Status BAPP
                          </div>
                          <div className="mt-1 text-sm text-slate-400">
                            Ringkasan BAPP yang sudah selesai, sudah setor, dan belum setor.
                          </div>
                        </div>
                        <div className="text-xs text-slate-500">
                          Total BAPP selesai: <span className="font-bold text-white">{number(totalId)}</span>
                        </div>
                      </div>

                      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-blue-400">
                            BAPP Selesai
                          </div>
                          <div className="mt-2 text-3xl font-bold text-white">
                            {number(totalId)}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            Total BAPP / instalasi selesai
                          </div>
                        </div>

                        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-400">
                              Sudah Setor
                            </div>
                            <div className="text-xs font-bold text-emerald-400">
                              {percent(sudahSetor, totalId)}
                            </div>
                          </div>
                          <div className="mt-2 text-3xl font-bold text-emerald-400">
                            {number(sudahSetor)}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            BAPP sudah masuk Finance
                          </div>
                          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-800">
                            <div
                              className="h-full rounded-full bg-emerald-500 transition-all"
                              style={{
                                width: percent(sudahSetor, totalId),
                              }}
                            />
                          </div>
                        </div>

                        <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-[10px] font-bold uppercase tracking-wide text-rose-400">
                              Belum Setor
                            </div>
                            <div className="text-xs font-bold text-rose-400">
                              {percent(belumSetor, totalId)}
                            </div>
                          </div>
                          <div className="mt-2 text-3xl font-bold text-rose-400">
                            {number(belumSetor)}
                          </div>
                          <div className="mt-1 text-xs text-slate-500">
                            BAPP belum masuk Finance
                          </div>
                          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-800">
                            <div
                              className="h-full rounded-full bg-rose-500 transition-all"
                              style={{
                                width: percent(belumSetor, totalId),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* RINGKASAN KEUANGAN */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-4">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          Uang Masuk
                        </div>
                        <div className="mt-2 text-xl font-bold text-emerald-400">
                          {rupiah(dibayarPusat)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Pusat → Korwil
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-4">
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                          Kewajiban Korda
                        </div>
                        <div className="mt-2 text-xl font-bold text-amber-400">
                          {rupiah(feeKorda)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Kewajiban yang sudah terealisasi dari pembayaran Pusat
                        </div>
                      </div>

                      <div className="rounded-xl border border-slate-800 bg-slate-950/80 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
                            Profit Bersih
                          </div>
                          <div className="text-[10px] text-slate-600">
                            setelah operasional
                          </div>
                        </div>
                        <div className="mt-2 text-xl font-bold text-cyan-400">
                          {rupiah(nettKorwil)}
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                          Margin kotor − biaya operasional
                        </div>
                      </div>
                    </div>

                  </div>

                </>
              );
            })()
          )}

        </div>
      )}
      {effectiveActiveMenu === "setoran_bapp_pusat" && (
        <div className="space-y-6">

{effectiveActiveMenu === "setoran_bapp_pusat" &&
        canReviewKordaBapp && (
          <div className="mt-6 rounded-xl border border-slate-700 bg-slate-950 shadow-xl">

            <div className="flex flex-col gap-3 border-b border-slate-800 bg-slate-900 p-5 md:flex-row md:items-center md:justify-between">

              <div>
                <div className="text-lg font-bold text-white">
                  Konfirmasi BAPP dari KORDA
                </div>

                <div className="mt-1 text-sm text-slate-400">
                  Review BAPP KORDA sebelum masuk ke Setoran BAPP Pusat.
                  Review dilakukan per ID transaksi.
                </div>
              </div>

              <button
                type="button"
                onClick={loadIncomingKordaBapp}
                disabled={incomingKordaBappLoading}
                className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-800 disabled:opacity-50"
              >
                <RefreshCw
                  size={15}
                  className={`mr-2 inline ${
                    incomingKordaBappLoading
                      ? "animate-spin"
                      : ""
                  }`}
                />
                Refresh
              </button>

            </div>

            {incomingKordaBappLoading ? (
              <div className="p-8 text-center text-sm text-slate-400">
                Memuat setoran BAPP dari KORDA...
              </div>
            ) : incomingKordaBapp.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-400">
                Tidak ada setoran BAPP KORDA yang menunggu review.
              </div>
            ) : (
              <div className="finance-table-scroll finance-korwil-review-scroll !h-[200px] !min-h-0 !max-h-[200px] overflow-auto rounded-b-lg" style={{ height: "200px", minHeight: 0, maxHeight: "200px" }}>

                <table className="min-w-full text-sm">

                  <thead className="sticky top-0 z-10 bg-slate-900 text-xs uppercase tracking-wide text-slate-400">

                    <tr>

                      <th className="px-3 py-2 text-left">
                        No Setoran
                      </th>

                      <th className="px-3 py-2 text-left">
                        ID Transaksi
                      </th>

                      <th className="px-3 py-2 text-left">
                        NPSN
                      </th>

                      <th className="px-3 py-2 text-left">
                        Sekolah
                      </th>

                      <th className="px-3 py-2 text-left">
                        KORDA
                      </th>

                      <th className="px-3 py-2 text-left">
                        KORWIL
                      </th>

                      <th className="px-3 py-2 text-left">
                        Status
                      </th>

                      <th className="px-3 py-2 text-left">
                        Catatan Revisi
                      </th>

                      <th className="px-3 py-2 text-center">
                        Aksi
                      </th>

                    </tr>

                  </thead>

                  <tbody className="divide-y divide-slate-800">

                    {sortFinanceRowsByTransactionNewest(incomingKordaBapp).map((item) => {

                      const id = String(
                        item?.id_transaksi || ""
                      );

                      const status = String(
                        item?.status_review ||
                        item?.status_setoran ||
                        "MENUNGGU_REVIEW"
                      ).toUpperCase();

                      const processing =
                        incomingKordaBappProcessing === id;

                      return (
                        <tr
                          key={`${item?.no_setoran || ""}-${id}`}
                          className="hover:bg-slate-900/70"
                        >

                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-blue-300">
                            {item?.no_setoran || "-"}
                          </td>

                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs font-semibold text-white">
                            {id || "-"}
                          </td>

                          <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-300">
                            {item?.npsn || "-"}
                          </td>

                          <td className="min-w-[240px] px-3 py-2 font-medium text-white">
                            {item?.nama_sekolah ||
                              item?.sekolah ||
                              item?.nama ||
                              "-"}
                          </td>

                          <td className="px-3 py-2 text-slate-300">
                            {item?.korda || "-"}
                          </td>

                          <td className="px-3 py-2 text-slate-300">
                            {item?.korwil || "-"}
                          </td>

                          <td className="px-3 py-2">

                            <span
                              className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                                status === "DITERIMA_KORWIL"
                                  ? "border-green-500/30 bg-green-500/10 text-green-400"
                                  : status === "REVISI"
                                    ? "border-red-500/30 bg-red-500/10 text-red-400"
                                    : "border-yellow-500/30 bg-yellow-500/10 text-yellow-400"
                              }`}
                            >
                              {status === "MENUNGGU_REVIEW"
                                ? "MENUNGGU REVIEW"
                                : status}
                            </span>

                          </td>

                          <td className="min-w-[220px] px-3 py-2">

                            <input
                              type="text"
                              value={
                                incomingKordaBappCatatan[id] || ""
                              }
                              onChange={(e) => {
                                const value =
                                  e.target.value;

                                setIncomingKordaBappCatatan(
                                  (prev) => ({
                                    ...prev,
                                    [id]: value,
                                  })
                                );
                              }}
                              placeholder="Isi jika revisi..."
                              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-white outline-none placeholder:text-slate-500 focus:border-blue-500"
                            />

                          </td>

                          <td className="px-3 py-2">

                            <div className="flex min-w-[190px] justify-center gap-2">

                              <button
                                type="button"
                                disabled={
                                  processing ||
                                  status === "DITERIMA_KORWIL"
                                }
                                onClick={() =>
                                  reviewIncomingKordaBapp(
                                    id,
                                    "TERIMA"
                                  )
                                }
                                className="rounded-lg bg-green-600 px-3 py-2 text-xs font-bold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                {processing
                                  ? "Proses..."
                                  : "✓ Terima"}
                              </button>

                              <button
                                type="button"
                                disabled={
                                  processing ||
                                  status === "DITERIMA_KORWIL"
                                }
                                onClick={() =>
                                  reviewIncomingKordaBapp(
                                    id,
                                    "REVISI"
                                  )
                                }
                                className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                ↻ Revisi
                              </button>

                            </div>

                          </td>

                        </tr>
                      );
                    })}

                  </tbody>

                </table>

              </div>
            )}

          </div>
        )}

          {/* HEADER */}
          <div className="flex flex-col gap-4 rounded-xl border border-slate-700 bg-[#0d121c] p-5 md:flex-row md:items-center md:justify-between">

            <div>
              <h2 className="text-xl font-bold text-white">
                Setoran BAPP Pusat
              </h2>

              <p className="mt-1 text-sm text-slate-400">
                Pencatatan BAPP yang sudah diterima KORWIL untuk disetor ke Pusat.
              </p>
            </div>

            <button
              type="button"
              onClick={openBappModal}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-500"
            >
              <FileCheck2 className="h-4 w-4" />
              Catat Setoran BAPP
            </button>

          </div>

          {/* FILTER */}
          <div className="rounded-xl border border-slate-700 bg-[#0d121c] p-5">

            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">

              {/* SEARCH */}
              <div className="md:col-span-2">

                <label className="mb-2 block text-sm font-medium text-slate-300">
                  Cari Transaksi
                </label>

                <div className="relative">

                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />

                  <input
                    type="text"
                    value={bappSearch}
                    onChange={(e) => setBappSearch(e.target.value)}
                    placeholder="ID transaksi / sekolah / NPSN..."
                    className="w-full rounded-lg border border-slate-600 bg-slate-900 py-2.5 pl-10 pr-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-blue-500"
                  />

                </div>

              </div>

              {/* STATUS */}
              <div>

                <label className="mb-2 block text-sm font-medium text-slate-300">
                  Status Setoran
                </label>

                <select
                  value={bappStatusFilter}
                  onChange={(e) => setBappStatusFilter(e.target.value)}
                  className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                >
                  <option value="ALL">
                    Semua Status
                  </option>

                  <option value="BELUM_SETOR">
                    Belum Setor
                  </option>

                  <option value="SUDAH_SETOR">
                    Sudah Setor
                  </option>

                </select>

              </div>

              {/* DIREKTORAT */}
              <div>

                <label className="mb-2 block text-sm font-medium text-slate-300">
                  Direktorat
                </label>

                <select
                  value={bappDirektoratFilter}
                  onChange={(e) =>
                    setBappDirektoratFilter(e.target.value)
                  }
                  className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                >
                  <option value="ALL">
                    Semua Direktorat
                  </option>

                  <option value="PAUD">PAUD</option>
                  <option value="SD">SD</option>
                  <option value="SMP">SMP</option>
                  <option value="SMA">SMA</option>

                </select>

              </div>

            </div>

            {/* REFRESH */}
            <div className="mt-4 flex justify-end">

              <button
                type="button"
                onClick={loadBappSetoran}
                disabled={bappLoading}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
              >

                <RefreshCw
                  className={`h-4 w-4 ${
                    bappLoading ? "animate-spin" : ""
                  }`}
                />

                Refresh

              </button>

            </div>

          </div>

          {/* DATA TABLE BAPP */}
          <div className="overflow-hidden rounded-xl border border-slate-700 bg-[#0d121c]">

            <div className="border-b border-slate-700 px-5 py-4">

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">

                <div>
                  <h3 className="font-bold text-white">
                    Data Setoran BAPP
                  </h3>

                  <p className="mt-1 text-sm text-slate-400">
                    Daftar BAPP yang sudah diterima KORWIL dan telah disetor ke Pusat.
                  </p>
                </div>

                <div className="text-xs text-slate-400">
                  Total{" "}
                  <span className="font-semibold text-white">
                    {Number(bappTotal || 0).toLocaleString("id-ID")}
                  </span>{" "}
                  data
                </div>

              </div>

            </div>

            {bappLoading ? (

              <div className="flex items-center justify-center px-5 py-16 text-sm text-slate-400">

                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />

                Memuat data Setoran BAPP...

              </div>

            ) : bappData.length === 0 ? (

              <div className="px-5 py-16 text-center">

                <FileCheck2 className="mx-auto h-10 w-10 text-slate-600" />

                <div className="mt-3 font-semibold text-slate-300">
                  Belum ada data Setoran BAPP
                </div>

                <p className="mt-1 text-sm text-slate-500">
                  Tidak ada transaksi yang sesuai dengan filter.
                </p>

              </div>

            ) : (

              <>

                <div className="finance-table-scroll max-h-[calc(100vh-430px)] overflow-auto">

                  <table className="min-w-full text-sm"><colgroup><col style={{width:"16%"}}/><col style={{width:"15%"}}/><col style={{width:"8%"}}/><col style={{width:"28%"}}/><col style={{width:"8%"}}/><col style={{width:"11%"}}/><col style={{width:"9%"}}/><col style={{width:"5%"}}/></colgroup><thead className="sticky top-0 z-20 bg-slate-900 text-left text-xs uppercase tracking-wide text-slate-400">
  <tr>
    <th className="px-4 py-3">No. Setoran</th>
    <th className="px-4 py-3">ID Transaksi</th>
    <th className="px-4 py-3">NPSN</th>
    <th className="px-4 py-3">Nama Sekolah</th>
    <th className="px-4 py-3">Provinsi</th>
    <th className="px-4 py-3">Status</th>
    <th className="px-4 py-3">Tgl Setor</th>
    <th className="px-4 py-3 text-center">Aksi</th>
  </tr>
</thead>

                    <tbody className="divide-y divide-slate-800">
  {sortFinanceRowsByTransactionNewest(bappData).map((item, index) => {
    const status = String(item?.status_setoran || item?.status || "BELUM_SETOR").trim().toUpperCase();
    const sudahSetor = status === "SUDAH_SETOR";
    return (
      <tr key={`${item?.id_transaksi || "row"}-${index}`} className="hover:bg-slate-900/60">
        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs font-semibold text-blue-300">{item?.no_setoran || "-"}</td>
        <td className="whitespace-nowrap px-4 py-3 font-semibold text-white">{item?.id_transaksi || "-"}</td>
        <td className="whitespace-nowrap px-4 py-3 text-slate-300">{item?.npsn || "-"}</td>
        <td className="min-w-[220px] px-4 py-3 text-slate-300">{item?.sekolah || item?.nama_sekolah || "-"}</td>
        <td className="whitespace-nowrap px-4 py-3 text-slate-400">{item?.provinsi || item?.propinsi || "-"}</td>
        <td className="whitespace-nowrap px-4 py-3"><span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${sudahSetor ? "bg-emerald-950 text-emerald-400" : "bg-amber-950 text-amber-400"}`}>{sudahSetor ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock3 className="h-3.5 w-3.5" />}{sudahSetor ? "SUDAH SETOR" : "BELUM SETOR"}</span></td>
        <td className="whitespace-nowrap px-4 py-3 text-slate-400">{item?.tanggal_setoran || item?.tanggal_setor || "-"}</td>
        <td className="px-4 py-3 text-center"><button type="button" onClick={() => printBappPusatSetoran(item?.no_setoran)} disabled={!item?.no_setoran} className="inline-flex items-center gap-1 rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"><Printer className="h-3.5 w-3.5" />Print</button></td>
      </tr>
    );
  })}
</tbody>

                  </table>

                </div>

                {/* PAGINATION BAPP */}

                <div className="flex flex-col gap-3 border-t border-slate-700 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">

                  <div className="text-xs text-slate-400">

                    {bappTotal > 0 ? (

                      <>
                        Menampilkan{" "}

                        <span className="font-semibold text-slate-200">
                          {(
                            (bappPage - 1) * bappPerPage + 1
                          ).toLocaleString("id-ID")}
                        </span>

                        {" - "}

                        <span className="font-semibold text-slate-200">
                          {Math.min(
                            bappPage * bappPerPage,
                            bappTotal
                          ).toLocaleString("id-ID")}
                        </span>

                        {" dari "}

                        <span className="font-semibold text-slate-200">
                          {Number(bappTotal).toLocaleString("id-ID")}
                        </span>

                        {" data"}

                      </>

                    ) : (

                      "Tidak ada data"

                    )}

                  </div>

                  <div className="flex flex-wrap items-center gap-2">

                    <select
                      value={bappPerPage}
                      onChange={(e) => {
                        setBappPerPage(Number(e.target.value));
                        setBappPage(1);
                      }}
                      className="rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-xs font-semibold text-white outline-none hover:bg-slate-700"
                    >

                      <option value={20}>
                        20 / halaman
                      </option>

                      <option value={50}>
                        50 / halaman
                      </option>

                      <option value={100}>
                        100 / halaman
                      </option>

                    </select>

                    <button
                      type="button"
                      disabled={
                        bappLoading ||
                        bappPage <= 1
                      }
                      onClick={() => {
                        setBappPage((prev) =>
                          Math.max(prev - 1, 1)
                        );
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >

                      <span className="text-base leading-none">
                        ←
                      </span>

                      Sebelumnya

                    </button>

                    <div className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300">

                      Halaman{" "}

                      <span className="text-white">
                        {bappPage}
                      </span>

                      {" / "}

                      <span className="text-white">
                        {Math.max(
                          Number(bappTotalPages || 0),
                          1
                        )}
                      </span>

                    </div>

                    <button
                      type="button"
                      disabled={
                        bappLoading ||
                        bappTotalPages <= 0 ||
                        bappPage >= bappTotalPages
                      }
                      onClick={() => {
                        setBappPage((prev) =>
                          Math.min(
                            prev + 1,
                            Math.max(
                              Number(bappTotalPages || 1),
                              1
                            )
                          )
                        );
                      }}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >

                      Berikutnya

                      <span className="text-base leading-none">
                        →
                      </span>

                    </button>

                  </div>

                </div>

              </>

            )}

          </div>

          {/* MODAL CATAT SETORAN */}
          {bappModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">

              <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-slate-700 bg-[#0d121c] shadow-2xl">

                {/* HEADER */}
                <div className="flex items-center justify-between border-b border-slate-700 px-5 py-4">

                  <div>
                    <h3 className="font-bold text-white">
                      Catat Setoran BAPP Pusat
                    </h3>

                    <p className="mt-1 text-xs text-slate-400">
                      Pilih ID transaksi yang sudah diterima dari Korda dan siap disetor ke Pusat PYX.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setBappModalOpen(false)}
                    className="text-2xl leading-none text-slate-400 hover:text-white"
                  >
                    ×
                  </button>

                </div>

                {/* BODY */}
                <div className="flex-1 space-y-4 overflow-y-auto p-5">

                  {/* SEARCH */}
                  <div>

                    <label className="mb-2 block text-sm font-medium text-slate-300">
                      Cari NPSN / Sekolah / ID Transaksi
                    </label>

                    <input
                      type="text"
                      value={bappSearch}
                      onChange={(e) =>
                        setBappSearch(e.target.value)
                      }
                      placeholder="Ketik NPSN, nama sekolah, atau ID transaksi..."
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-blue-500"
                    />

                  </div>

                  {/* DIREKTORAT */}
                  <div>

                    <label className="mb-2 block text-sm font-medium text-slate-300">
                      Direktorat
                    </label>

                    <select
                      value={bappForm.direktorat || ""}
                      onChange={(e) =>
                        setBappForm((prev) => ({
                          ...prev,
                          direktorat: e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                    >

                      <option value="">
                        Pilih Direktorat
                      </option>

                      <option value="PAUD">PAUD</option>
                      <option value="SD">SD</option>
                      <option value="SMP">SMP</option>
                      <option value="SMA">SMA</option>

                    </select>

                  </div>

                  {/* INFO SELECTION */}
                  <div className="flex items-center justify-between rounded-lg border border-slate-700 bg-slate-900/70 px-4 py-3">

                    <div>
                      <div className="text-sm font-semibold text-white">
                        Transaksi tersedia
                      </div>

                      <div className="text-xs text-slate-400">
                        Centang ID transaksi yang sudah diterima dari Korda.
                      </div>
                    </div>

                    <div className="rounded-lg bg-blue-600/20 px-3 py-1.5 text-sm font-bold text-blue-400">
                      {selectedBappIds.length} dipilih
                    </div>

                  </div>

                  {/* LIST NPSN */}
                  <div className="overflow-hidden rounded-xl border border-slate-700">

                    {bappAvailable.length === 0 ? (

                      <div className="px-4 py-10 text-center text-sm text-slate-400">
                        Tidak ada transaksi yang tersedia untuk disetor.
                      </div>

                    ) : (

                      <div className="max-h-[420px] overflow-y-auto">

                        {Object.entries(
                          bappAvailable.reduce((groups, item) => {

                            const npsn =
                              String(item.npsn || "TANPA NPSN").trim();

                            if (!groups[npsn]) {
                              groups[npsn] = [];
                            }

                            groups[npsn].push(item);

                            return groups;

                          }, {})
                        ).map(([npsn, items]) => {

                          const ids = items
                            .map((item) =>
                              String(
                                item.id_transaksi || ""
                              ).trim()
                            )
                            .filter(Boolean);

                          const selectedCount = ids.filter((id) =>
                            selectedBappIds.includes(id)
                          ).length;

                          const allSelected =
                            ids.length > 0 &&
                            selectedCount === ids.length;

                          const sekolah =
                            items[0]?.sekolah ||
                            items[0]?.nama_sekolah ||
                            "-";

                          const korwil =
                            items[0]?.korwil || "-";

                          const korda =
                            items[0]?.korda || "-";

                          return (

                            <div
                              key={npsn}
                              className="border-b border-slate-800 last:border-b-0"
                            >

                              {/* NPSN HEADER */}
                              <div className="flex flex-col gap-3 bg-slate-800/60 px-4 py-3 md:flex-row md:items-center md:justify-between">

                                <div className="min-w-0 flex-1">

                                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">

                                    <span className="shrink-0 font-bold text-white">
                                      NPSN {npsn}
                                    </span>

                                    <span className="min-w-0 truncate text-sm font-medium text-slate-300">
                                      {sekolah}
                                    </span>

                                    <span className="shrink-0 rounded-full bg-slate-700 px-2 py-0.5 text-[11px] text-slate-300">
                                      {items.length} transaksi
                                    </span>

                                  </div>

                                  <div className="mt-1 text-xs text-slate-500">
                                    Korwil: {korwil} · Korda: {korda}
                                  </div>

                                </div>

                                <button
                                  type="button"
                                  onClick={() =>
                                    toggleBappNpsn(
                                      npsn,
                                      items
                                    )
                                  }
                                  className="shrink-0 rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700"
                                >
                                  {allSelected
                                    ? "Batal Pilih"
                                    : "Pilih Semua"}
                                </button>

                              </div>

                              {/* ID TRANSAKSI */}
                              <div className="divide-y divide-slate-800">

                                {sortFinanceRowsByTransactionNewest(items).map((item, index) => {

                                  const id =
                                    String(
                                      item.id_transaksi || ""
                                    ).trim();

                                  const checked =
                                    selectedBappIds.includes(id);

                                  return (

                                    <label
                                      key={
                                        id ||
                                        item._id ||
                                        index
                                      }
                                      className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-slate-800/40"
                                    >

                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                          toggleBappId(id)
                                        }
                                        className="h-4 w-4 rounded border-slate-600 bg-slate-900 text-blue-600 focus:ring-blue-500"
                                      />

                                      <div className="min-w-0 flex-1">

                                        <div className="font-mono text-sm font-semibold text-blue-400">
                                          {id}
                                        </div>

                                        <div className="mt-0.5 text-xs text-slate-500">
                                          {item.sekolah ||
                                            item.nama_sekolah ||
                                            "-"}
                                        </div>

                                      </div>

                                      {checked && (
                                        <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-400">
                                          DIPILIH
                                        </span>
                                      )}

                                    </label>

                                  );

                                })}

                              </div>

                            </div>

                          );

                        })}

                      </div>

                    )}

                  </div>

                  {/* TANGGAL */}
                  <div>

                    <label className="mb-2 block text-sm font-medium text-slate-300">
                      Tanggal Setor
                    </label>

                    <input
                      type="date"
                      value={
                        bappForm.tanggal_setor || ""
                      }
                      onChange={(e) =>
                        setBappForm((prev) => ({
                          ...prev,
                          tanggal_setor:
                            e.target.value,
                        }))
                      }
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                    />

                  </div>

                  {/* KETERANGAN */}
                  <div>

                    <label className="mb-2 block text-sm font-medium text-slate-300">
                      Keterangan
                    </label>

                    <textarea
                      rows={3}
                      value={
                        bappForm.keterangan || ""
                      }
                      onChange={(e) =>
                        setBappForm((prev) => ({
                          ...prev,
                          keterangan:
                            e.target.value,
                        }))
                      }
                      placeholder="Keterangan setoran (opsional)..."
                      className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-blue-500"
                    />

                  </div>

                </div>

                {/* FOOTER */}
                <div className="flex flex-col gap-3 border-t border-slate-700 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">

                  <div className="text-sm text-slate-400">

                    <span className="font-semibold text-white">
                      {selectedBappIds.length}
                    </span>{" "}
                    ID transaksi akan disetor

                  </div>

                  <div className="flex justify-end gap-3">

                    <button
                      type="button"
                      onClick={() =>
                        setBappModalOpen(false)
                      }
                      className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700"
                    >
                      Batal
                    </button>

                    <button
                      type="button"
                      onClick={saveBappSetoran}
                      disabled={
                        bappSaving ||
                        selectedBappIds.length === 0 ||
                        !bappForm.direktorat
                      }
                      className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >

                      {bappSaving && (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      )}

                      Setor BAPP

                    </button>

                  </div>

                </div>

              </div>

            </div>
          )}

        </div>
      )}

      {effectiveActiveMenu === "master_fee" && (
        <div className="rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">

          <div className="flex flex-col gap-4 border-b p-5 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-bold">
                MASTER FEE
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Tarif Pusat dan Fee Korda berdasarkan Regional.
              </p>
            </div>

            {isSuperAdmin && (
              <button
                type="button"
                onClick={() =>
                  setAddKordaOpen(true)
                }
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                + Tambah Korda
              </button>
            )}
          </div>

          <div className="finance-table-scroll overflow-x-auto">
            <table className="min-w-[1100px] w-full text-sm">

              <thead>
                <tr className="border-b border-slate-300 bg-slate-50">
                  <th className="px-4 py-3 text-left font-bold">
                    REGIONAL
                  </th>

                  <th className="px-4 py-3 text-right font-bold">
                    TARIF PUSAT
                  </th>

                  {sortFinanceRowsByTransactionNewest(kordaColumns).map(
                    (korda) => (
                      <th
                        key={korda}
                        className="px-4 py-3 text-right font-bold"
                      >
                        KORDA {korda}
                      </th>
                    )
                  )}

                  {isSuperAdmin && (
                    <th className="w-28 px-4 py-3 text-center font-bold">
                      ADD KORDA
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {feeLoading ? (
                  <tr>
                    <td
                      colSpan={
                        3 +
                        kordaColumns.length
                      }
                      className="px-4 py-12 text-center text-slate-500"
                    >
                      Memuat Master Fee...
                    </td>
                  </tr>
                ) : (
                  sortFinanceRowsByTransactionNewest(MASTER_FEE_REGIONS).map(
                    (regional) => {
                      const row =
                        feeMatrix[
                          regional
                        ];

                      const pusat =
                        row?.pusat;

                      const pusatEditing =
                        feeCellEditing?.regional ===
                          regional &&
                        feeCellEditing?.jenis ===
                          "PUSAT";

                      return (
                        <tr
                          key={regional}
                          className="border-b border-slate-200 hover:bg-slate-50"
                        >

                          <td className="px-4 py-4 font-bold">
                            ·{" "}
                            {
                              MASTER_FEE_REGION_LABELS[
                                regional
                              ]
                            }
                          </td>

                          <td className="px-4 py-4 text-right">
                            {isSuperAdmin && pusatEditing ? (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  value={
                                    feeCellValue
                                  }
                                  onChange={(
                                    e
                                  ) =>
                                    setFeeCellValue(
                                      e
                                        .target
                                        .value
                                    )
                                  }
                                  className="w-32 rounded-lg border border-blue-400 px-2 py-1.5 text-right"
                                  autoFocus
                                />

                                <button
                                  type="button"
                                  onClick={
                                    saveFeeCell
                                  }
                                  disabled={
                                    feeSaving
                                  }
                                  className="rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white"
                                >
                                  Simpan
                                </button>
                              </div>
                            ) : (
                              isSuperAdmin ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    startFeeCellEdit(
                                      regional,
                                      "PUSAT",
                                      null,
                                      pusat?.nominal ||
                                        0
                                    )
                                  }
                                  className="font-semibold text-blue-700 hover:underline"
                                >
                                  {rupiah(
                                    pusat?.nominal ||
                                      0
                                  )}
                                </button>
                              ) : (
                                <span className="font-semibold text-slate-700">
                                  {rupiah(
                                    pusat?.nominal ||
                                      0
                                  )}
                                </span>
                              )
                            )}
                          </td>

                          {sortFinanceRowsByTransactionNewest(kordaColumns).map(
                            (korda) => {
                              const fee =
                                row?.korda?.[
                                  korda
                                ];

                              const editing =
                                feeCellEditing?.regional ===
                                  regional &&
                                feeCellEditing?.jenis ===
                                  "KORDA" &&
                                feeCellEditing?.korda ===
                                  korda;

                              return (
                                <td
                                  key={korda}
                                  className="px-4 py-4 text-right"
                                >
                                  {isSuperAdmin && editing ? (
                                    <div className="flex items-center justify-end gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        value={
                                          feeCellValue
                                        }
                                        onChange={(
                                          e
                                        ) =>
                                          setFeeCellValue(
                                            e
                                              .target
                                              .value
                                          )
                                        }
                                        className="w-28 rounded-lg border border-blue-400 px-2 py-1.5 text-right"
                                        autoFocus
                                      />

                                      <button
                                        type="button"
                                        onClick={
                                          saveFeeCell
                                        }
                                        disabled={
                                          feeSaving
                                        }
                                        className="rounded-lg bg-blue-600 px-2.5 py-1.5 text-xs font-semibold text-white"
                                      >
                                        ✓
                                      </button>
                                    </div>
                                  ) : (
                                    isSuperAdmin ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          startFeeCellEdit(
                                            regional,
                                            "KORDA",
                                            korda,
                                            fee?.nominal ||
                                              0
                                          )
                                        }
                                        className="font-semibold text-slate-700 hover:text-blue-700 hover:underline"
                                      >
                                        {rupiah(
                                          fee?.nominal ||
                                            0
                                        )}
                                      </button>
                                    ) : (
                                      <span className="font-semibold text-slate-700">
                                        {rupiah(
                                          fee?.nominal ||
                                            0
                                        )}
                                      </span>
                                    )
                                  )}
                                </td>
                              );
                            }
                          )}

                          <td className="px-4 py-4 text-center">
                            {isSuperAdmin && (
                              <button
                                type="button"
                                onClick={() =>
                                  setAddKordaOpen(
                                    true
                                  )
                                }
                                className="rounded-lg border border-blue-300 px-3 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50"
                              >
                                + Add
                              </button>
                            )}
                          </td>

                        </tr>
                      );
                    }
                  )
                )}
              </tbody>

            </table>
          </div>
        </div>
      )}

      {effectiveActiveMenu === "master_fee" && (
        <MarginSettingsPanel
          api={api}
          setMessage={setMessage}
        />
      )}

      {effectiveActiveMenu === "operasional" && (
        <ExpensePanel
          api={api}
          setMessage={setMessage}
        />
      )}

      {effectiveActiveMenu === "laporan" && (
        <FinanceReportPanel
          api={api}
          setMessage={setMessage}
          reportParams={
            financeRole === "KORWIL"
              ? {
                  korwil: String(user?.korwil || "")
                    .trim()
                    .toUpperCase(),
                }
              : {}
          }
        />
      )}

      {effectiveActiveMenu === "riwayat" && (
        <PaymentHistoryPanel
          api={api}
          setMessage={setMessage}
          onInvoice={(payment) => setSelected(payment)}
        />
      )}

      {(effectiveActiveMenu === "pusat" || effectiveActiveMenu === "korda") && (
        <div className="finance-payment-table-panel rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
        <div className="flex flex-col gap-4 border-b p-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative w-full xl:max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari No. Setoran / ID transaksi, teknisi, Korda..."
              className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={applyTransactionFilters}
              className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
            >
              🔍 Cari
            </button>
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setAppliedSearch("");
                setStatus("ALL");
                setAppliedStatus("ALL");
                setTransactionPage(1);
              }}
              disabled={
                !search &&
                !appliedSearch &&
                status === "ALL" &&
                appliedStatus === "ALL"
              }
              className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X className="mr-1 inline h-4 w-4" />
              Reset
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              ["ALL", "Semua"],
              ["BELUM_DIBAYAR", "Belum Dibayar"],
              ["DIBAYAR_SEBAGIAN", "Sebagian"],
              ["LUNAS", "Lunas"],
            ].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setStatus(value)}
                className={`rounded-lg border px-3 py-2 text-sm font-medium ${
                  status === value
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="finance-table-scroll finance-payment-table-scroll overflow-x-auto">
          <table className="w-full min-w-[1350px] text-sm">
  <thead>
    <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-700">

      {effectiveActiveMenu === "pusat" && (
        <th className="w-12 px-4 py-3">
          <input
            type="checkbox"
            checked={
              (() => {
                const eligible =
                  filtered.filter(
                    (x) =>
                      !bulkPaidTermIds.includes(
                        x.id_transaksi
                      )
                  );

                return (
                  eligible.length > 0 &&
                  eligible.every((x) =>
                    bulkSelectedIds.includes(
                      x.id_transaksi
                    )
                  )
                );
              })()
            }
            disabled={
              filtered.filter(
                (x) =>
                  !bulkPaidTermIds.includes(
                    x.id_transaksi
                  )
              ).length === 0
            }
            onChange={
              toggleSelectAllBulkPayment
            }
          />
        </th>
      )}

      <th className="px-4 py-3">
        {effectiveActiveMenu === "korda" ? "No. Transaksi Payment" : "No. Setoran BAPP"}
      </th>

      <th className="px-4 py-3">
        ID Transaksi
      </th>

      <th className="px-4 py-3">
        Teknisi
      </th>

      <th className="px-4 py-3">
        Korwil
      </th>

      <th className="px-4 py-3">
        Korda
      </th>

      {effectiveActiveMenu === "pusat" ? (
        <>
          <th className="px-4 py-3 text-right">
            Fee Pusat
          </th>

          <th className="px-4 py-3 text-right">
            Dibayar Pusat
          </th>

          <th className="px-4 py-3 text-right">
            Sisa Pusat
          </th>

          <th className="px-4 py-3">
            Status Pusat
          </th>
        </>
      ) : (
        <>
          <th className="px-4 py-3 text-right">
            Fee Korda
          </th>

          <th className="px-4 py-3 text-right">
            Dibayar Korwil
          </th>

          <th className="px-4 py-3 text-right">
            Sisa Fee
          </th>

          <th className="px-4 py-3">
            Status Pembayaran Korda
          </th>
        </>
      )}

      {effectiveActiveMenu === "korda" && (
        <th className="px-4 py-3 text-right">
          Aksi
        </th>
      )}

    </tr>
  </thead>

  <tbody>
    {loading ? (
      <tr>
        <td
          colSpan={
            effectiveActiveMenu === "pusat"
              ? "11"
              : "10"
          }
          className="px-4 py-12 text-center text-slate-500"
        >
          Memuat data...
        </td>
      </tr>
    ) : filtered.length === 0 ? (
      <tr>
        <td
          colSpan={
            effectiveActiveMenu === "pusat"
              ? "11"
              : "10"
          }
          className="px-4 py-12 text-center text-slate-500"
        >
          {effectiveActiveMenu === "pusat"
            ? "Tidak ada BAPP yang sudah disetor ke Pusat."
            : "Tidak ada BAPP yang memenuhi syarat pembayaran ke Korda."
          }
        </td>
      </tr>
    ) : (
      sortFinanceRowsByTransactionNewest(filtered).map((x) => {

        const feePusat = Number(
          x.fee_pusat?.nominal ??
          x.fee_pusat ??
          0
        );

        const dibayarPusat = Number(
          x.total_dibayar_pusat ??
          x.total_diterima_pusat ??
          0
        );

        const sisaPusat = Math.max(
          0,
          feePusat - dibayarPusat
        );

        const statusPusat =
          feePusat > 0 && dibayarPusat >= feePusat
            ? "LUNAS"
            : dibayarPusat > 0
              ? "DIBAYAR_SEBAGIAN"
              : "BELUM_DIBAYAR";

        const feeKorda = Number(
          x.fee_korda?.nominal ??
          x.fee_korda ??
          0
        );

        const dibayarKorwil = Number(
          x.total_dibayar_korda ||
          x.total_dibayar ||
          0
        );

        const sisaFee = Math.max(
          0,
          feeKorda - dibayarKorwil
        );

        const statusKorda =
          feeKorda > 0 && dibayarKorwil >= feeKorda
            ? "LUNAS"
            : dibayarKorwil > 0
              ? "DIBAYAR_SEBAGIAN"
              : "BELUM_DIBAYAR";


        const termAlreadyPaid =
          x.sudah_dibayar === true;

        const checked =
          bulkSelectedIds.includes(
            x.id_transaksi
          );

        return (
          <tr
            key={x.id_transaksi}
            className="border-b border-slate-200 last:border-0 hover:bg-slate-50"
          >

            {effectiveActiveMenu === "pusat" && (
              <td className="px-4 py-3">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={termAlreadyPaid}
                  onChange={() =>
                    toggleBulkPaymentId(
                      x.id_transaksi
                    )
                  }
                />
              </td>
            )}

            <td className="px-4 py-3">
              <div className="font-mono text-xs font-bold text-blue-700">
                {effectiveActiveMenu === "korda"
                  ? (Array.isArray(x.nomor_pembayaran_korda)
                      ? sortFinanceRowsByTransactionNewest(x.nomor_pembayaran_korda).map((nomor, index) => (
                          <div key={nomor || index}>{nomor}</div>
                        ))
                      : x.nomor_pembayaran_korda || "-")
                  : x.no_setoran || "-"}
              </div>
            </td>

            <td className="px-4 py-3">
              <div className="font-semibold">
                {x.id_transaksi}
              </div>

              {x.npsn && (
                <div className="text-xs text-slate-500">
                  NPSN: {x.npsn}
                </div>
              )}
            </td>

            <td className="px-4 py-3">
              <div className="font-semibold">
                {x.teknisi?.nama ||
                  x.teknisi_pusat_nama ||
                  "-"}
              </div>

              <div className="text-xs text-slate-500">
                {x.teknisi?.kode ||
                  x.teknisi_pusat_kode ||
                  "-"}
              </div>
            </td>

            <td className="px-4 py-3 font-semibold">
              {x.korwil || "-"}
            </td>

            <td className="px-4 py-3 font-semibold">
              {x.korda || "-"}
            </td>

            {effectiveActiveMenu === "pusat" ? (
              <>
                <td className="px-4 py-3 text-right">
                  {rupiah(feePusat)}
                </td>

                <td className="px-4 py-3 text-right font-semibold">
                  {rupiah(dibayarPusat)}
                </td>

                <td className="px-4 py-3 text-right">
                  {rupiah(sisaPusat)}
                </td>

                <td className="px-4 py-3">
                  {statusPusat === "LUNAS" ? (
                    <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      LUNAS
                    </span>
                  ) : statusPusat ===
                    "DIBAYAR_SEBAGIAN" ? (
                    <span className="inline-flex rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                      DIBAYAR SEBAGIAN
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      BELUM DIBAYAR
                    </span>
                  )}
                </td>
              </>
            ) : (
              <>
                <td className="px-4 py-3 text-right">
                  {rupiah(feeKorda)}
                </td>

                <td className="px-4 py-3 text-right font-semibold">
                  {rupiah(dibayarKorwil)}
                </td>

                <td className="px-4 py-3 text-right">
                  {rupiah(sisaFee)}
                </td>

                <td className="px-4 py-3">
                  {statusKorda === "LUNAS" ? (
                    <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                      LUNAS
                    </span>
                  ) : statusKorda ===
                    "DIBAYAR_SEBAGIAN" ? (
                    <span className="inline-flex rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700">
                      DIBAYAR SEBAGIAN
                    </span>
                  ) : (
                    <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                      BELUM DIBAYAR
                    </span>
                  )}
                </td>
              </>
            )}

            {effectiveActiveMenu === "korda" && (
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setSelected({
                      ...x,
                      nomor_pembayaran_korda: x.nomor_pembayaran_korda || "-",
                    })}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-700"
                  >
                    <Printer className="h-3.5 w-3.5" />
                    Print
                  </button>
                </div>
              </td>
            )}
          </tr>
        );
      })
    )}
  </tbody>
</table>
        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
          <div className="text-slate-500">
            Menampilkan{" "}
            <span className="font-semibold text-slate-700">
              {transactionTotal === 0
                ? 0
                : (transactionPage - 1) *
                    transactionPerPage +
                  1}
            </span>
            {" - "}
            <span className="font-semibold text-slate-700">
              {Math.min(
                transactionPage *
                  transactionPerPage,
                transactionTotal
              )}
            </span>
            {" dari "}
            <span className="font-semibold text-slate-700">
              {transactionTotal}
            </span>
            {" transaksi"}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={transactionPerPage}
              onChange={(e) => {
                setTransactionPerPage(
                  Number(e.target.value)
                );
                setTransactionPage(1);
              }}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
            >
              <option value={25}>
                25 / halaman
              </option>

              <option value={50}>
                50 / halaman
              </option>

              <option value={100}>
                100 / halaman
              </option>
            </select>

            <button
              type="button"
              disabled={
                transactionPage <= 1 ||
                loading
              }
              onClick={() =>
                setTransactionPage(
                  (prev) =>
                    Math.max(
                      1,
                      prev - 1
                    )
                )
              }
              className="rounded-lg border border-slate-300 px-3 py-2 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ‹
            </button>

            <div className="min-w-[120px] text-center font-semibold text-slate-700">
              Halaman{" "}
              {transactionPage}{" "}
              /{" "}
              {Math.max(
                transactionTotalPages,
                1
              )}
            </div>

            <button
              type="button"
              disabled={
                loading ||
                transactionTotalPages === 0 ||
                transactionPage >=
                  transactionTotalPages
              }
              onClick={() =>
                setTransactionPage(
                  (prev) =>
                    Math.min(
                      transactionTotalPages,
                      prev + 1
                    )
                )
              }
              className="rounded-lg border border-slate-300 px-3 py-2 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              ›
            </button>
          </div>
        </div>

        </div>

        <div className="border-t px-4 py-3 text-sm text-neutral-500">
          Menampilkan {filtered.length} dari{" "}
          {transactions.length} transaksi.
        </div>
      </div>
      )}


      {feeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 text-slate-900 shadow-2xl">
            <div className="mb-5 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">
                  {feeEditing
                    ? "Edit Master Fee"
                    : "Tambah Master Fee"}
                </h3>

                <p className="mt-1 text-sm text-slate-500">
                  Tentukan Korda dan fee untuk daerah.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setFeeModalOpen(false)
                }
                className="rounded-lg border p-2 hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-semibold">
                  Kabupaten / Kota
                </label>

                <input
                  value={feeForm.kabupaten_kota}
                  onChange={(e) =>
                    setFeeForm((v) => ({
                      ...v,
                      kabupaten_kota:
                        e.target.value,
                    }))
                  }
                  placeholder="Contoh: JAKARTA SELATAN"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold">
                  Korda
                </label>

                <input
                  value={feeForm.korda}
                  onChange={(e) =>
                    setFeeForm((v) => ({
                      ...v,
                      korda: e.target.value,
                    }))
                  }
                  placeholder="Nama / kode Korda"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    Fee Pusat
                  </label>

                  <input
                    type="number"
                    min="0"
                    value={feeForm.fee_pusat}
                    onChange={(e) =>
                      setFeeForm((v) => ({
                        ...v,
                        fee_pusat:
                          e.target.value,
                      }))
                    }
                    placeholder="0"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    Fee Korda
                  </label>

                  <input
                    type="number"
                    min="0"
                    value={feeForm.fee_korda}
                    onChange={(e) =>
                      setFeeForm((v) => ({
                        ...v,
                        fee_korda:
                          e.target.value,
                      }))
                    }
                    placeholder="0"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 p-3">
                <input
                  type="checkbox"
                  checked={Boolean(
                    feeForm.active
                  )}
                  onChange={(e) =>
                    setFeeForm((v) => ({
                      ...v,
                      active:
                        e.target.checked,
                    }))
                  }
                  className="h-4 w-4"
                />

                <div>
                  <div className="text-sm font-semibold">
                    Master Fee aktif
                  </div>

                  <div className="text-xs text-slate-500">
                    Fee aktif dapat digunakan untuk transaksi baru.
                  </div>
                </div>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() =>
                  setFeeModalOpen(false)
                }
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50"
              >
                Batal
              </button>

              <button
                type="button"
                onClick={handleFeeSave}
                disabled={feeSaving}
                className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {feeSaving
                  ? "Menyimpan..."
                  : feeEditing
                    ? "Simpan Perubahan"
                    : "Tambah Master Fee"}
              </button>
            </div>
          </div>
        </div>
      )}

      {addKordaOpen && isSuperAdmin && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 text-slate-900 shadow-2xl">

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">
                  Tambah Korda
                </h3>

                <p className="mt-1 text-sm text-slate-500">
                  Korda akan ditambahkan ke seluruh Regional.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  setAddKordaOpen(false)
                }
                className="rounded-lg border border-slate-300 p-2 hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 space-y-4">

              <div>
                <label className="mb-1.5 block text-sm font-semibold">
                  Nama Korda
                </label>

                <input
                  value={newKordaName}
                  onChange={(e) =>
                    setNewKordaName(
                      e.target.value
                    )
                  }
                  placeholder="Contoh: REFFI"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-semibold">
                  Fee Default
                </label>

                <input
                  type="number"
                  min="0"
                  value={
                    newKordaDefaultFee
                  }
                  onChange={(e) =>
                    setNewKordaDefaultFee(
                      e.target.value
                    )
                  }
                  placeholder="100000"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                />

                <div className="mt-1 text-xs text-slate-500">
                  Nilai ini menjadi fee awal untuk semua Regional.
                  Setelah itu setiap Regional bisa diedit sendiri.
                </div>
              </div>

            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAddKordaOpen(false);
                  setNewKordaName("");
                  setNewKordaDefaultFee("");
                }}
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50"
              >
                Batal
              </button>

              <button
                type="button"
                onClick={
                  handleAddKorda
                }
                disabled={
                  feeSaving
                }
                className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {feeSaving
                  ? "Menyimpan..."
                  : "Tambah Korda"}
              </button>
            </div>

          </div>
        </div>
      )}

      {bulkPaymentOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
    <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-slate-700 bg-[#0d121c] shadow-2xl">

      {/* ======================================================
          HEADER
         ====================================================== */}

      <div className="flex items-start justify-between border-b border-slate-700 px-6 py-5">
        <div>
          <h2 className="text-lg font-bold text-white">
            {bulkPaymentMode === "KORDA"
              ? "Tambah Pembayaran Korda"
              : "Tambah Pembayaran Pusat"}
          </h2>

          <p className="mt-1 text-sm text-slate-400">
            {bulkPaymentMode === "KORDA"
              ? "Pilih ID transaksi yang memiliki kewajiban pembayaran ke Korda."
              : "Pilih ID transaksi yang akan dibayar oleh Pusat."}
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setBulkPaymentOpen(false);
            setBulkSelectedIds([]);
            setBulkPaymentSearch("");
            setBulkPage(1);
            setBulkTransactions([]);
            setBulkPaidTermIds([]);
            setBulkTotal(0);
            setBulkTotalPages(0);
          }}
          className="rounded-lg border border-slate-600 p-2 text-slate-300 hover:bg-slate-800"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* ======================================================
          FORM
         ====================================================== */}

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">

        <div className="grid gap-4 md:grid-cols-3">

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-200">
              Termin Pembayaran
            </label>

            <select
              value={bulkTerm}
              onChange={(e) => {
                setBulkTerm(e.target.value);
                setBulkPage(1);
                setBulkSelectedIds([]);
                setBulkPaidTermIds([]);
              }}
              className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            >
              <option value="TERMIN_1">
                Termin 1
              </option>

              <option value="PELUNASAN">
                Pelunasan
              </option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-200">
              Tanggal Pembayaran
            </label>

            <input
              type="date"
              value={bulkPaymentDate}
              onChange={(e) =>
                setBulkPaymentDate(
                  e.target.value
                )
              }
              className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-200">
              Nomor Referensi
            </label>

            <input
              value={bulkReference}
              onChange={(e) =>
                setBulkReference(
                  e.target.value
                )
              }
              placeholder="Contoh: TRF-001"
              className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>

        </div>

        <div>
          <label className="mb-1.5 block text-sm font-semibold text-slate-200">
            Catatan
          </label>

          <textarea
            value={bulkNote}
            onChange={(e) =>
              setBulkNote(e.target.value)
            }
            rows={2}
            placeholder="Catatan pembayaran..."
            className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* ==================================================
            SEARCH + SELECT ALL
           ================================================== */}

        <div className="grid gap-3 md:grid-cols-[280px_minmax(0,1fr)] md:items-end">

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-slate-200">
              No. Setoran BAPP Pusat
            </label>

            <select
              value={
                bulkPaymentSearch.trim().toUpperCase().startsWith("SET-PUSAT-")
                  ? bulkPaymentSearch.trim()
                  : ""
              }
              onChange={(e) => {
                setBulkPaymentSearch(e.target.value);
                setBulkPage(1);
                setBulkSelectedIds([]);
              }}
              className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Semua No. Setoran</option>
              {Array.from(
                new Set(
                  visibleBulkTransactions
                    .map((item) => String(item?.no_setoran || "").trim())
                    .filter(Boolean)
                )
              ).sort().map((noSetoran) => (
                <option key={noSetoran} value={noSetoran}>
                  {noSetoran}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">

          <div className="relative w-full md:max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

            <input
              value={bulkPaymentSearch}
              onChange={(e) => {
                setBulkPaymentSearch(
                  e.target.value
                );
                setBulkPage(1);
              }}
              placeholder="Cari No. Setoran / ID transaksi, teknisi, Korda..."
              className="w-full rounded-lg border border-slate-600 bg-slate-900 py-2.5 pl-10 pr-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          
            {bulkPaymentMode === "KORDA" && (
              <div data-korda-payment-filter="1" className="mb-3 rounded-lg border border-blue-200 bg-blue-50/60 p-3">
                <div className="mb-1 text-sm font-semibold text-slate-800">Pilih Korda</div>
                <div className="mb-2 text-xs text-slate-500">Pilih Korda terlebih dahulu. ID transaksi yang tampil hanya milik Korda tersebut.</div>
                <select
                  value={bulkKordaFilter}
                  onChange={(e) => {
                    setBulkKordaFilter(e.target.value);
                    setBulkSelectedIds([]);
                  }}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">Pilih Korda...</option>
                  {bulkKordaOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
<button disabled={bulkPaymentMode === "KORDA" && !bulkKordaFilter}
            type="button"
            onClick={toggleSelectAllBulkPayment}
            disabled={
              bulkTransactionsLoading ||
              visibleBulkTransactions.filter(
                (item) =>
                  isBulkPaymentEligible(item)
              ).length === 0
            }
            className="whitespace-nowrap rounded-lg border border-blue-500 bg-blue-600/10 px-4 py-2.5 text-sm font-semibold text-blue-400 hover:bg-blue-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Pilih Semua di Halaman
          </button>

        </div>

        </div>

        {/* ==================================================
            LIST ID
           ================================================== */}

        <div className="overflow-hidden rounded-xl border border-slate-700">

          <div className="max-h-[48vh] overflow-auto">

            <table className="min-w-[1050px] w-full text-sm">

              <thead className="sticky top-0 z-10">
                <tr className="border-b border-slate-700 bg-slate-900 text-left text-xs font-bold uppercase tracking-wide text-slate-400">

                  <th className="w-12 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={
                        visibleBulkTransactions.length > 0 &&
                        visibleBulkTransactions
                          .filter((item) => {
                            if (bulkPaymentMode === "KORDA") {
                              return isKordaTermEligible(item);
                            }

                            const termin1SudahDibayar =
                              isBulkPusatTermPaid(item, "TERMIN_1");

                            const pelunasanSudahDibayar =
                              isBulkPusatTermPaid(item, "PELUNASAN");

                            return bulkTerm === "PELUNASAN"
                              ? termin1SudahDibayar && !pelunasanSudahDibayar
                              : !termin1SudahDibayar && !item.sudah_dibayar;
                          })
                          .every((item) =>
                            bulkSelectedIds.includes(
                              item.id_transaksi
                            )
                          )
                      }
                      disabled={
                        bulkTransactionsLoading ||
                        visibleBulkTransactions.filter((item) => {
                          if (bulkPaymentMode === "KORDA") {
                              return isKordaTermEligible(item);
                            }

                          const termin1SudahDibayar =
                            isBulkPusatTermPaid(item, "TERMIN_1");

                          const pelunasanSudahDibayar =
                            isBulkPusatTermPaid(item, "PELUNASAN");

                          return bulkTerm === "PELUNASAN"
                              ? termin1SudahDibayar && !pelunasanSudahDibayar
                              : !termin1SudahDibayar && !item.sudah_dibayar;
                        }).length === 0
                      }
                      onChange={
                        toggleSelectAllBulkPayment
                      }
                    />
                  </th>

                  <th className="px-3 py-3">
                    No. Setoran BAPP
                  </th>

                  <th className="px-3 py-3">
                    ID Transaksi
                  </th>

                  <th className="px-3 py-3">
                    Teknisi
                  </th>

                  <th className="px-3 py-3">
                    Korwil
                  </th>

                  <th className="px-3 py-3">
                    Korda
                  </th>

                  <th className="px-3 py-3">
                    Jadwal
                  </th>

                  <th className="px-3 py-3 text-center">
                    Status Pusat
                  </th>

                  <th className="px-3 py-3 text-center">
                    Status{" "}
                    {bulkTerm === "TERMIN_1"
                      ? "Termin 1"
                      : "Pelunasan"}
                  </th>

                </tr>
              </thead>

              <tbody>

                {bulkTransactionsLoading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-12 text-center text-sm text-slate-400"
                    >
                      Memuat ID transaksi...
                    </td>
                  </tr>
                ) : visibleBulkTransactions.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-4 py-12 text-center text-sm text-slate-400"
                    >
                      Tidak ada ID transaksi.
                    </td>
                  </tr>
                ) : (
                  sortFinanceRowsByTransactionNewest(visibleBulkTransactions).map(
                    (item) => {
                      const alreadyPaid =
                        isBulkPusatTermPaid(item, bulkTerm);

                      const termin1SudahDibayar =
                        isBulkPusatTermPaid(item, "TERMIN_1");

                      const pelunasanSudahDibayar =
                        isBulkPusatTermPaid(item, "PELUNASAN");

                      const paymentEligible =
                        bulkPaymentMode === "KORDA"
                          ? isKordaTermEligible(item)
                          : bulkTerm === "PELUNASAN"
                            ? termin1SudahDibayar && !pelunasanSudahDibayar
                            : !termin1SudahDibayar && !alreadyPaid;

                      const selected =
                        bulkSelectedIds.includes(
                          item.id_transaksi
                        );

                      const modalStatusPusat = String(
                        item?.status_pembayaran_pusat ||
                        (item?.termin_1_pusat_sudah_dibayar === true && item?.pelunasan_pusat_sudah_dibayar === true
                          ? "LUNAS"
                          : item?.termin_1_pusat_sudah_dibayar === true || item?.pelunasan_pusat_sudah_dibayar === true
                            ? "DIBAYAR_SEBAGIAN"
                            : "BELUM_DIBAYAR")
                      ).toUpperCase();

                      return (
                        <tr
                          key={
                            item.id_transaksi
                          }
                          className={`border-b border-slate-800 last:border-b-0 ${
                            !paymentEligible
                              ? "bg-slate-900/40 opacity-50"
                              : "hover:bg-slate-900"
                          }`}
                        >

                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={
                                selected
                              }
                              disabled={
                                !paymentEligible
                              }
                              onChange={() =>
                                toggleBulkPaymentId(
                                  item.id_transaksi
                                )
                              }
                              className="h-4 w-4"
                            />
                          </td>

                          <td className="px-3 py-3">
                            <div className="font-mono text-xs font-bold text-blue-300">
                              {item.no_setoran || "-"}
                            </div>
                          </td>

                          <td className="px-3 py-3">
                            <div className="font-semibold text-white">
                              {
                                item.id_transaksi
                              }
                            </div>

                            {item.npsn && (
                              <div className="text-xs text-slate-500">
                                NPSN:{" "}
                                {item.npsn}
                              </div>
                            )}
                          </td>

                          <td className="px-3 py-3">
                            <div className="font-semibold text-slate-200">
                              {
                                item.teknisi
                                  ?.nama ||
                                "-"
                              }
                            </div>

                            <div className="text-xs text-slate-500">
                              {
                                item.teknisi
                                  ?.kode ||
                                "-"
                              }
                            </div>
                          </td>

                          <td className="px-3 py-3 text-slate-300">
                            {
                              item.korwil ||
                              "-"
                            }
                          </td>

                          <td className="px-3 py-3 font-semibold text-slate-200">
                            {
                              item.korda ||
                              "-"
                            }
                          </td>

                          <td className="px-3 py-3 text-slate-400">
                            {item.jadwal
                              ? String(
                                  item.jadwal
                                ).slice(
                                  0,
                                  10
                                )
                              : "-"}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {modalStatusPusat === "LUNAS" ? (
                              <span className="inline-flex rounded-full border border-emerald-800 bg-emerald-900/40 px-2.5 py-1 text-[11px] font-bold text-emerald-300">
                                LUNAS
                              </span>
                            ) : modalStatusPusat ===
                              "DIBAYAR_SEBAGIAN" ? (
                              <span className="inline-flex rounded-full border border-amber-800 bg-amber-900/40 px-2.5 py-1 text-[11px] font-bold text-amber-300">
                                DIBAYAR SEBAGIAN
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-[11px] font-bold text-slate-300">
                                BELUM DIBAYAR
                              </span>
                            )}
                          </td>

                          <td className="px-3 py-3 text-center">
                            {bulkPaymentMode === "KORDA" ? (
                              bulkTerm === "PELUNASAN" &&
                              item.pelunasan_sudah_dibayar === true ? (
                                <span className="inline-flex rounded-full border border-emerald-800 bg-emerald-900/40 px-2.5 py-1 text-[11px] font-bold text-emerald-300">
                                  SUDAH DIBAYAR
                                </span>
                              ) : (
                                <span className="inline-flex rounded-full border border-blue-800 bg-blue-900/40 px-2.5 py-1 text-[11px] font-bold text-blue-300">
                                  SIAP DIBAYAR
                                </span>
                              )
                            ) : alreadyPaid ? (
                              <span className="inline-flex rounded-full border border-emerald-800 bg-emerald-900/40 px-2.5 py-1 text-[11px] font-bold text-emerald-300">
                                SUDAH DIBAYAR
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-[11px] font-bold text-slate-300">
                                BELUM
                              </span>
                            )}
                          </td>

                        </tr>
                      );
                    }
                  )
                )}

              </tbody>

            </table>

          </div>

          {/* ==================================================
              PAGINATION MODAL
             ================================================== */}

          <div className="shrink-0 flex flex-col gap-3 border-t border-slate-700 bg-slate-950 px-4 py-3 text-xs text-slate-400 md:flex-row md:items-center md:justify-between">

            <div>
              Menampilkan{" "}
              <span className="font-semibold text-white">
                {bulkTotal === 0
                  ? 0
                  : (
                      (bulkPage - 1) *
                      bulkPerPage
                    ) + 1}
              </span>
              {" - "}
              <span className="font-semibold text-white">
                {Math.min(
                  bulkPage *
                    bulkPerPage,
                  bulkTotal
                )}
              </span>
              {" dari "}
              <span className="font-semibold text-white">
                {bulkTotal}
              </span>
              {" ID transaksi"}
            </div>

            <div className="flex items-center gap-2">

              <select
                value={bulkPerPage}
                onChange={(e) => {
                  setBulkPerPage(
                    Number(
                      e.target.value
                    )
                  );
                  setBulkPage(1);
                }}
                className="rounded-lg border border-slate-600 bg-slate-900 px-2.5 py-1.5 text-xs text-white"
              >
                <option value={25}>
                  25 / halaman
                </option>

                <option value={50}>
                  50 / halaman
                </option>

                <option value={100}>
                  100 / halaman
                </option>
              </select>

              <button
                type="button"
                disabled={
                  bulkPage <= 1 ||
                  bulkTransactionsLoading
                }
                onClick={() =>
                  setBulkPage(
                    (prev) =>
                      Math.max(
                        1,
                        prev - 1
                      )
                  )
                }
                className="rounded-lg border border-slate-600 px-2.5 py-1.5 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                ‹
              </button>

              <span className="min-w-[80px] text-center font-semibold text-white">
                {bulkPage}
                {" / "}
                {Math.max(
                  bulkTotalPages,
                  1
                )}
              </span>

              <button
                type="button"
                disabled={
                  bulkTransactionsLoading ||
                  bulkTotalPages === 0 ||
                  bulkPage >=
                    bulkTotalPages
                }
                onClick={() =>
                  setBulkPage(
                    (prev) =>
                      Math.min(
                        bulkTotalPages,
                        prev + 1
                      )
                  )
                }
                className="rounded-lg border border-slate-600 px-2.5 py-1.5 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
              >
                ›
              </button>

            </div>
          </div>

        </div>

        {/* ==================================================
            SELECTED SUMMARY
           ================================================== */}

        <div className="rounded-lg border border-blue-900/60 bg-blue-950/30 px-4 py-3 text-sm text-blue-200">
          ID transaksi dipilih:{" "}
          <span className="font-bold text-white">
            {bulkSelectedIds.length}
          </span>

          <div className="mt-1 text-xs text-blue-300/80">
            Pemilihan tetap tersimpan saat berpindah halaman.
          </div>
        </div>

        {/* ==================================================
            FOOTER
           ================================================== */}

        <div className="flex justify-end gap-2">

          <button
            type="button"
            onClick={() => {
              setBulkPaymentOpen(false);
              setBulkSelectedIds([]);
              setBulkPaymentSearch("");
              setBulkPage(1);
              setBulkTransactions([]);
              setBulkPaidTermIds([]);
            }}
            className="rounded-lg border border-slate-600 px-4 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800"
          >
            Batal
          </button>

          <button
            type="button"
            onClick={handleBulkPayment}
            disabled={
              savingBulkPayment ||
              bulkSelectedIds.length === 0
            }
            className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {savingBulkPayment
              ? "Menyimpan..."
              : `Simpan Pembayaran (${bulkSelectedIds.length})`}
          </button>

        </div>

      </div>
    </div>
  </div>
)}

{statusEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl border border-slate-700 bg-[#0d121c] p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-white">
                  Ubah Status Pembayaran
                </h2>
                <p className="mt-1 text-xs text-slate-400">
                  {statusEditing.id_transaksi}
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (savingManualStatus) return;
                  setStatusEditing(null);
                  setManualStatus("");
                  setManualStatusNote("");
                }}
                className="text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-300">
                  Status Pembayaran
                </label>

                <select
                  value={manualStatus}
                  onChange={(e) => setManualStatus(e.target.value)}
                  className="w-full rounded-lg border border-slate-600 bg-[#111827] px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                >
                  <option value="BELUM_DIBAYAR">
                    Belum Dibayar
                  </option>
                  <option value="DIBAYAR_SEBAGIAN">
                    Dibayar Sebagian
                  </option>
                  <option value="LUNAS">
                    Lunas
                  </option>
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-semibold text-slate-300">
                  Catatan
                </label>

                <textarea
                  value={manualStatusNote}
                  onChange={(e) =>
                    setManualStatusNote(e.target.value)
                  }
                  rows={3}
                  placeholder="Contoh: Status dikonfirmasi manual oleh Korwil."
                  className="w-full resize-none rounded-lg border border-slate-600 bg-[#111827] px-3 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  if (savingManualStatus) return;
                  setStatusEditing(null);
                  setManualStatus("");
                  setManualStatusNote("");
                }}
                disabled={savingManualStatus}
                className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300 hover:bg-slate-800 disabled:opacity-50"
              >
                Batal
              </button>

              <button
                type="button"
                onClick={handleManualStatus}
                disabled={
                  savingManualStatus ||
                  !manualStatus
                }
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {savingManualStatus
                  ? "Menyimpan..."
                  : "Simpan Status"}
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <Invoice
          transaction={selected}
          onClose={() => setSelected(null)}
        />
      )}

      {selectedKordaBatch && (
        <KordaPaymentBatchPrint
          batch={selectedKordaBatch}
          onClose={() => setSelectedKordaBatch(null)}
        />
      )}
    </div>
  );
}





function KordaBatchInvoice({ transaction, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const batchNo = transaction?.nomor_pembayaran_korda || transaction?.nomor_referensi || '-';
  const term = data?.term || transaction?.term || 'TERMIN_1';
  const termLabel = term === 'PELUNASAN' ? 'PELUNASAN 50%' : 'DIBAYAR 50%';
  const rowStatus = term === 'PELUNASAN' ? 'PELUNASAN 50%' : 'DIBAYAR 50%';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(
          '/finance/korda-payment-batch/' + encodeURIComponent(batchNo)
        );
        if (!cancelled) setData(res.data || null);
      } catch (error) {
        console.error('Gagal mengambil batch pembayaran Korda:', error);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (batchNo && batchNo !== '-') load();
    else setLoading(false);

    return () => {
      cancelled = true;
    };
  }, [batchNo]);

  const items = (Array.isArray(data?.items) && data.items.length > 0
    ? data.items
    : [transaction]
  ).filter(Boolean);

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return String(value).slice(0, 10);
    }
  };

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4'>
      <div className='mx-auto flex min-h-full max-w-6xl items-center justify-center'>
        <div className='w-full rounded-xl bg-white shadow-2xl'>
          <div className='flex items-center justify-between border-b px-6 py-4 print:hidden'>
            <div>
              <h2 className='text-lg font-bold text-slate-900'>Invoice Pembayaran Korda</h2>
              <p className='text-xs text-slate-500'>Bukti administrasi pembayaran berdasarkan satu batch Korda.</p>
            </div>
            <div className='flex gap-2'>
              <button type='button' onClick={() => window.print()} className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white'>Print</button>
              <button type='button' onClick={onClose} className='rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700'>Tutup</button>
            </div>
          </div>

          <div className='invoice-print-area p-8 text-neutral-900'>
            <div className='border-b-2 border-neutral-900 pb-4 text-center'>
              <div className='text-2xl font-bold tracking-wide'>INVOICE PEMBAYARAN KORDA</div>
            </div>

            <div className='mt-5 grid grid-cols-2 gap-x-12 gap-y-3 border-b pb-5 text-sm'>
              <div><span className='font-semibold'>No. Transaksi</span> : <span className='font-mono font-bold'>{data?.nomor_transaksi || batchNo}</span></div>
              <div><span className='font-semibold'>Tanggal Pembayaran</span> : {formatDate(data?.tanggal || transaction?.tanggal_pembayaran)}</div>
              <div><span className='font-semibold'>Korwil</span> : {data?.korwil || transaction?.korwil || '-'}</div>
              <div><span className='font-semibold'>Korda</span> : {data?.korda || transaction?.korda || '-'}</div>
              <div><span className='font-semibold'>Termin</span> : <span className='font-bold'>{termLabel}</span></div>
              <div><span className='font-semibold'>Status</span> : <span className='font-bold'>SUDAH DIBAYAR</span></div>
            </div>

            <div className='mt-7 mb-3 text-sm font-bold'>RINCIAN BAPP YANG DIBAYAR</div>

            {loading ? (
              <div className='py-10 text-center text-sm'>Memuat rincian pembayaran...</div>
            ) : (
              <table className='w-full border-collapse text-xs'>
                <thead>
                  <tr className='bg-slate-100'>
                    <th className='border border-slate-400 px-2 py-2'>No.</th>
                    <th className='border border-slate-400 px-2 py-2'>ID TRANSAKSI</th>
                    <th className='border border-slate-400 px-2 py-2'>NPSN</th>
                    <th className='border border-slate-400 px-2 py-2'>NAMA SEKOLAH</th>
                    <th className='border border-slate-400 px-2 py-2'>PROVINSI</th>
                    <th className='border border-slate-400 px-2 py-2'>STATUS PEMBAYARAN</th>
                  </tr>
                </thead>
                <tbody>
                  {sortFinanceRowsByTransactionNewest(items).map((item, index) => (
                    <tr key={item?.id_transaksi || index}>
                      <td className='border border-slate-400 px-2 py-2 text-center'>{index + 1}</td>
                      <td className='border border-slate-400 px-2 py-2 font-mono'>{item?.id_transaksi || '-'}</td>
                      <td className='border border-slate-400 px-2 py-2'>{item?.npsn || '-'}</td>
                      <td className='border border-slate-400 px-2 py-2'>{item?.nama_sekolah || item?.sekolah || '-'}</td>
                      <td className='border border-slate-400 px-2 py-2'>{item?.provinsi || '-'}</td>
                      <td className='border border-slate-400 px-2 py-2 font-semibold'>{rowStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <div className='mt-6 text-sm'>
              <span className='font-semibold'>Jumlah BAPP:</span> {items.length} transaksi
            </div>

            <div className='mt-10 grid grid-cols-2 gap-12 text-center text-sm'>
              <div><div className='mb-16'>Korwil,</div><div className='border-t border-neutral-700 pt-2'>{data?.korwil || transaction?.korwil || '-'}</div></div>
              <div><div className='mb-16'>Korda,</div><div className='border-t border-neutral-700 pt-2'>{data?.korda || transaction?.korda || '-'}</div></div>
            </div>

            <div className='mt-8 border-t pt-4 text-center text-xs text-neutral-500'>
              Dokumen ini merupakan bukti administrasi pembayaran Korda berdasarkan transaksi Finance.
            </div>
          </div>
        </div>
      </div>
      <style>{'@media print { body * { visibility: hidden; } .invoice-print-area, .invoice-print-area * { visibility: visible; } .invoice-print-area { position: absolute; left: 0; top: 0; width: 100%; background: white; } @page { size: A4; margin: 10mm; } }'}</style>
    </div>
  );
}

function PaymentHistoryPanel({
  api,
  setMessage,
  onInvoice,
}) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState("ALL");
  const [search, setSearch] = useState("");
  const [appliedTerm, setAppliedTerm] = useState("ALL");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const loadPayments = async () => {
    try {
      setLoading(true);

      const params = {};

      if (appliedTerm !== "ALL") {
        params.term = appliedTerm;
      }

      const res = await api.get(
        "/finance/reporting/payments",
        { params }
      );

      setPayments(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (err) {
      console.error(err);

      setMessage(
        err?.response?.data?.detail ||
        "Gagal mengambil riwayat pembayaran."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        const params = {};

        if (term !== "ALL") {
          params.term = term;
        }

        const res = await api.get(
          "/finance/reporting/payments",
          { params }
        );

        if (cancelled) return;

        setPayments(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (err) {
        if (cancelled) return;

        console.error(err);

        setMessage(
          err?.response?.data?.detail ||
          "Gagal mengambil riwayat pembayaran."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [api, setMessage, appliedTerm]);

  const filtered = useMemo(() => {
    const q = appliedSearch
      .trim()
      .toLowerCase();

    if (!q) {
      return payments;
    }

    return payments.filter((item) => {
      return [
        item.id_transaksi,
        item.korwil,
        item.korda,
        item.teknisi?.kode,
        item.teknisi?.nama,
        item.term,
        item.jenis_pembayaran,
        item.nomor_referensi,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value)
            .toLowerCase()
            .includes(q)
        );
    });
  }, [payments, appliedSearch]);

  const applyPaymentFilters = () => {
    setPage(1);
    setAppliedSearch(search);
    setAppliedTerm(term);
  };

  const resetPaymentFilters = () => {
    setSearch("");
    setAppliedSearch("");
    setTerm("ALL");
    setAppliedTerm("ALL");
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginatedPayments = filtered.slice(
    (page - 1) * perPage,
    page * perPage
  );

  const totalDibayar = filtered.reduce(
    (sum, item) =>
      sum +
      Number(
        item.pembayaran_pusat || 0
      ),
    0
  );

  const totalMargin = filtered.reduce(
    (sum, item) =>
      sum +
      Number(
        item.margin?.margin_bersih ||
          item.margin_bersih ||
          0
      ),
    0
  );

  const formatDate = (value) => {
    if (!value) return "-";

    try {
      return new Date(
        value
      ).toLocaleDateString(
        "id-ID",
        {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }
      );
    } catch {
      return String(value).slice(
        0,
        10
      );
    }
  };

  const getDistribution = (payment) =>
    payment.margin_distribution ||
    payment.margin?.components ||
    [];

  return (
    <div className="space-y-5">
      <div className="finance-payment-table-panel rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
        <div className="flex flex-col gap-4 border-b p-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <h2 className="text-lg font-bold">
              Riwayat Pembayaran
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Daftar pembayaran Pusat → Korwil dan Korwil → Korda.
            </p>
          </div>

          <button
            type="button"
            onClick={loadPayments}
            disabled={loading}
            className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50"
          >
            {loading
              ? "Memuat..."
              : "Refresh"}
          </button>
        </div>

        <div className="border-b p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

              <input
                value={search}
                onChange={(e) =>
                  setSearch(
                    e.target.value
                  )
                }
                placeholder="Cari ID transaksi, teknisi, Korda..."
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 pl-10 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={applyPaymentFilters}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                >
                  🔍 Cari
                </button>
                <button
                  type="button"
                  onClick={resetPaymentFilters}
                  disabled={!search && !appliedSearch && term === "ALL" && appliedTerm === "ALL"}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <X className="mr-1 inline h-4 w-4" />
                  Reset
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {[
                ["ALL", "Semua"],
                ["TERMIN_1", "Termin 1"],
                ["PELUNASAN", "Pelunasan"],
              ].map(
                ([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() =>
                      setTerm(value)
                    }
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                      term === value
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {label}
                  </button>
                )
              )}
            </div>
          </div>
        </div>

        <div className="finance-table-scroll finance-payment-table-scroll overflow-x-auto">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Memuat riwayat pembayaran...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              Belum ada pembayaran yang sesuai filter.
            </div>
          ) : (
            <table className="min-w-[1500px] w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-slate-700">
                  <th className="px-3 py-3">
                    ID Transaksi
                  </th>

                  <th className="px-3 py-3">
                    Teknisi
                  </th>

                  <th className="px-3 py-3">
                    KORWIL
                  </th>

                  <th className="px-3 py-3">
                    KORDA
                  </th>

                  <th className="px-3 py-3">
                    Jenis Pembayaran
                  </th>

                  <th className="px-3 py-3">
                    Termin
                  </th>

                  <th className="px-3 py-3">
                    Tanggal
                  </th>

                  <th className="px-3 py-3 text-right">
                    Dibayar Pusat
                  </th>

                  <th className="px-3 py-3 text-right">
                    Kewajiban Korda
                  </th>

                  <th className="px-3 py-3 text-right">
                    Margin Bersih
                  </th>

                  <th className="px-3 py-3 text-center">
                    Status
                  </th>

                  <th className="px-3 py-3 text-right">
                    Aksi
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortFinanceRowsByTransactionNewest(paginatedPayments).map(
                  (payment) => {
                    const margin =
                      Number(
                        payment.margin?.margin_bersih ??
                          payment.margin_bersih ??
                          0
                      );

                    const distribution =
                      getDistribution(
                        payment
                      );

                    return (
                      <tr
                        key={payment.id}
                        className="border-b last:border-b-0 hover:bg-slate-50"
                      >
                        <td className="px-3 py-3 font-semibold">
                          {payment.id_transaksi}
                        </td>

                        <td className="px-3 py-3">
                          <div className="font-semibold">
                            {payment.teknisi?.nama ||
                              "-"}
                          </div>

                          <div className="text-xs text-slate-500">
                            {payment.teknisi?.kode ||
                              "-"}
                          </div>
                        </td>

                        <td className="px-3 py-3">
                          {payment.korwil ||
                            "-"}
                        </td>

                        <td className="px-3 py-3">
                          {payment.korda ||
                            "-"}
                        </td>

                        <td className="px-3 py-3">
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${payment.jenis_pembayaran === "PUSAT_KE_KORWIL" ? "bg-violet-100 text-violet-700" : "bg-cyan-100 text-cyan-700"}`}>
                            {payment.jenis_pembayaran === "PUSAT_KE_KORWIL" ? "Pusat → Korwil" : "Korwil → Korda"}
                          </span>
                        </td>

                        <td className="px-3 py-3">
                          <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                            {payment.term ===
                            "TERMIN_1"
                              ? "TERMIN 1"
                              : "PELUNASAN"}
                          </span>
                        </td>

                        <td className="px-3 py-3">
                          {formatDate(
                            payment.tanggal_pembayaran
                          )}
                        </td>

                        <td className="px-3 py-3 text-right font-semibold">
                          {rupiah(payment.jenis_pembayaran === "PUSAT_KE_KORWIL" ? payment.pembayaran_pusat : 0)}
                        </td>

                        <td className="px-3 py-3 text-right">
                          {rupiah(payment.jenis_pembayaran === "KORWIL_KE_KORDA" ? payment.jumlah_dibayar : payment.kewajiban_korda)}
                        </td>

                        <td className="px-3 py-3 text-right font-bold">
                          {payment.jenis_pembayaran === "PUSAT_KE_KORWIL" ? rupiah(margin) : "—"}
                        </td>

                        <td className="px-3 py-3 text-center">
                          <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                            SUDAH DIBAYAR
                          </span>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                onInvoice(
                                  payment
                                )
                              }
                              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                            >
                              Invoice
                            </button>

                          </div>
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="text-slate-600">
            Menampilkan {filtered.length === 0 ? 0 : (page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} dari {filtered.length} pembayaran
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="payment-history-page-size" className="text-slate-600">
              Baris:
            </label>
            <select
              id="payment-history-page-size"
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-700"
            >
              {[10, 25, 50, 100].map((size) => (
                <option key={size} value={size}>{size} / halaman</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-slate-300 px-3 py-2 font-semibold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Halaman sebelumnya"
            >
              ‹
            </button>
            <span className="min-w-24 text-center font-medium text-slate-700">
              Halaman {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              disabled={page >= totalPages}
              className="rounded-lg border border-slate-300 px-3 py-2 font-semibold hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Halaman berikutnya"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}



// ============================================================================
// FINANCE KORDA - SEPARATE PAGE
// ============================================================================


class KordaFinanceErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, info) {
    console.error("KORDA FINANCE RUNTIME ERROR:", error, info);
  }

  render() {
    if (this.state.hasError) {
      const message =
        this.state.error?.message ||
        String(this.state.error || "Unknown React error");

      return (
        <div className="min-h-[400px] bg-slate-950 p-8 text-slate-100">
          <div className="mx-auto max-w-3xl rounded-2xl border border-red-800 bg-red-950/30 p-6">
            <h1 className="text-xl font-bold text-red-400">
              Finance KORDA mengalami error
            </h1>

            <p className="mt-3 text-sm text-slate-300">
              Halaman berhasil dimuat tetapi terjadi error saat React
              menjalankan komponen Finance KORDA.
            </p>

            <pre className="mt-4 overflow-auto rounded-lg bg-black p-4 text-xs text-red-300 whitespace-pre-wrap">
              {message}
            </pre>

            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function KordaFinancePage({
  user,
  api,
  dashboard,
  setDashboard,
  dashboardLoading,
  available,
  setAvailable,
  availableLoading,
  selected,
  setSelected,
  message,
  setMessage,
  loadDashboard,
  loadAvailable,
  getId,
  getNpsn,
  activeMenu,
}) {
  const [searchParams] = useSearchParams();
  const requestedMenu = searchParams.get("menu") || activeMenu;
  const [search, setSearch] = useState("");
  const [kordaBappPage, setKordaBappPage] = useState(1);
  const [kordaBappPerPage, setKordaBappPerPage] = useState(20);
  const [kordaSetoranSearch, setKordaSetoranSearch] = useState("");
  const [appliedKordaSetoranSearch, setAppliedKordaSetoranSearch] = useState("");
  const [kordaSetoranStatusFilter, setKordaSetoranStatusFilter] = useState("ALL");
  const [appliedKordaSetoranStatusFilter, setAppliedKordaSetoranStatusFilter] = useState("ALL");

  const [modalOpen, setModalOpen] = useState(false);
  const [setoranLoading, setSetoranLoading] = useState(false);
  const [setoranResult, setSetoranResult] = useState(null);

  const [noSetoran, setNoSetoran] = useState("");
  const [tanggalSetoran, setTanggalSetoran] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const [selectedKorwil, setSelectedKorwil] = useState("");
  const [selectedDirektorat, setSelectedDirektorat] = useState("");
  const [modalSelected, setModalSelected] = useState([]);
  const [modalSearch, setModalSearch] = useState("");

  // ============================================================
  // COMMAND CENTER SYNC
  // TOTAL BAPP SELESAI harus sama dengan Command Center
  // (Dashboard /dashboard/summary -> totals.selesai).
  // ============================================================
  const [commandCenterSummary, setCommandCenterSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadCommandCenterSummary = async () => {
      try {
        const res = await api.get("/dashboard/summary");

        if (!cancelled) {
          setCommandCenterSummary(res?.data || null);
        }
      } catch (error) {
        console.error(
          "Gagal mengambil summary Command Center untuk Finance KORDA:",
          error
        );

        if (!cancelled) {
          setCommandCenterSummary(null);
        }
      }
    };

    loadCommandCenterSummary();

    return () => {
      cancelled = true;
    };
  }, [api]);

  // Baris yang baru saja berhasil dibuat disimpan lokal agar tabel
  // langsung berubah tanpa menunggu refresh/re-fetch dashboard.
  const [justSubmittedRows, setJustSubmittedRows] = useState([]);

  // Gunakan query URL secara langsung sebagai sumber kebenaran.
  // Ini mencegah state activeMenu lama membuat submenu pembayaran
  // tetap menampilkan halaman Setoran.
  const financeKordaTab =
    user?.role === "KORDA" && requestedMenu === "pembayaran_bapp_korda"
      ? "PEMBAYARAN"
      : "SETORAN";
  const [paymentItems, setPaymentItems] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSearch, setPaymentSearch] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPerPage, setPaymentPerPage] = useState(20);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentTotalPages, setPaymentTotalPages] = useState(0);
  const [paymentStatusCounts, setPaymentStatusCounts] = useState({
    BELUM_DIBAYAR: 0,
    DIBAYAR_SEBAGIAN: 0,
    LUNAS: 0,
  });

  const loadPaymentStatus = async (nextPage = 1) => {
    try {
      setPaymentLoading(true);
      const res = await api.get(
        "/finance/bapp-korda/payment-status",
        {
          params: {
            page: nextPage,
            per_page: paymentPerPage,
            q: paymentSearch.trim() || undefined,
            status: paymentStatusFilter || undefined,
            _t: Date.now(),
          },
        }
      );
      const data = res.data || {};
      setPaymentItems(Array.isArray(data.items) ? data.items : []);
      setPaymentTotal(Number(data.total || 0));
      setPaymentTotalPages(Number(data.total_pages || 0));
      setPaymentPage(Number(data.page || nextPage || 1));
      setPaymentStatusCounts({
        BELUM_DIBAYAR: Number(data.status_counts?.BELUM_DIBAYAR || 0),
        DIBAYAR_SEBAGIAN: Number(data.status_counts?.DIBAYAR_SEBAGIAN || 0),
        LUNAS: Number(data.status_counts?.LUNAS || 0),
      });
    } catch (error) {
      console.error("Gagal mengambil status pembayaran BAPP KORDA:", error);
      setPaymentItems([]);
      setPaymentTotal(0);
      setPaymentTotalPages(0);
    } finally {
      setPaymentLoading(false);
    }
  };

  // ============================================================
  // LOAD
  // ============================================================

  useEffect(() => {
    loadDashboard?.();
    loadAvailable?.();
  }, [loadDashboard, loadAvailable]);

  useEffect(() => {
    if (financeKordaTab !== "PEMBAYARAN") return;
    loadPaymentStatus(1);
  }, [
    financeKordaTab,
    paymentSearch,
    paymentStatusFilter,
    paymentPerPage,
  ]);

  // ============================================================
  // DATA TABEL UTAMA
  // HANYA YANG SUDAH SETOR
  // ============================================================

  useEffect(() => {
    setKordaBappPage(1);
  }, [search]);

  const dashboardData = Array.isArray(dashboard?.data)
    ? dashboard.data
    : Array.isArray(dashboard?.items)
      ? dashboard.items
      : [];

  const totalBappSelesai = Number(
    commandCenterSummary?.totals?.selesai ?? 0
  );

  const bappSudahSetor = dashboardData.filter((item) => {
    const status = String(
      item?.status_review ||
      item?.status_setoran ||
      ""
    ).trim().toUpperCase();

    return status === "DITERIMA_KORWIL" || status === "DITERIMA";
  }).length;

  const bappMenungguReview = dashboardData.filter((item) => {
    const status = String(
      item?.status_review ||
      item?.status_setoran ||
      ""
    ).trim().toUpperCase();

    return (
      status === "MENUNGGU_REVIEW_KORWIL" ||
      status === "MENUNGGU_REVIEW" ||
      status === "SUBMITTED" ||
      status === "DIKIRIM"
    );
  }).length;

  // BAPP BELUM SETOR = BAPP instalasi selesai yang belum berstatus
  // diterima KORWIL dan belum sedang menunggu review.
  const bappBelumSetor = Math.max(
    0,
    totalBappSelesai -
      bappSudahSetor -
      bappMenungguReview
  );

  const submittedData = [
    ...justSubmittedRows,
    ...dashboardData,
  ]
    .filter((item, index, arr) => {
      const id = String(item?.id_transaksi || "").trim();
      if (!id) return true;
      return arr.findIndex(
        (x) => String(x?.id_transaksi || "").trim() === id
      ) === index;
    })
    .filter((item) => {
      const statusReview = String(
        item?.status_review || ""
      ).trim().toUpperCase();

      const statusSetoran = String(
        item?.status_setoran || ""
      ).trim().toUpperCase();

      return (
        statusReview === "MENUNGGU_REVIEW" ||
        statusReview === "MENUNGGU_REVIEW_KORWIL" ||
        statusReview === "SUBMITTED" ||
        statusReview === "DIKIRIM" ||
        statusReview === "DITERIMA" ||
        statusReview === "DITERIMA_KORWIL" ||
        statusReview === "REVISI" ||
        statusReview === "REVISI_KORDA" ||
        statusSetoran === "MENUNGGU_REVIEW" ||
        statusSetoran === "MENUNGGU_REVIEW_KORWIL" ||
        statusSetoran === "SUDAH_SETOR" ||
        statusSetoran === "DIKIRIM"
      );
    })
    .sort((a, b) => {
      const npsnA = String(a?.npsn || "");
      const npsnB = String(b?.npsn || "");

      const npsnCompare = npsnA.localeCompare(
        npsnB,
        undefined,
        { numeric: true }
      );

      if (npsnCompare !== 0) return npsnCompare;

      return String(
        a?.id_transaksi || ""
      ).localeCompare(
        String(b?.id_transaksi || ""),
        undefined,
        { numeric: true }
      );
    });

  const filteredSubmitted = submittedData
    .filter((item) => {
      const status = String(
        item?.status_review ||
        item?.status_setoran ||
        "MENUNGGU_REVIEW"
      ).trim().toUpperCase();

      if (
        appliedKordaSetoranStatusFilter !== "ALL" &&
        status !== appliedKordaSetoranStatusFilter
      ) {
        return false;
      }

      const keyword = appliedKordaSetoranSearch.trim().toLowerCase();

      if (!keyword) return true;

      return (
        String(item?.no_setoran || "").toLowerCase().includes(keyword) ||
        String(item?.id_transaksi || "").toLowerCase().includes(keyword) ||
        String(item?.npsn || "").toLowerCase().includes(keyword) ||
        String(item?.nama_sekolah || item?.sekolah || "").toLowerCase().includes(keyword)
      );
    })
    .sort((a, b) => {
      const transaksiA = String(a?.id_transaksi || "").trim();
      const transaksiB = String(b?.id_transaksi || "").trim();

      return transaksiB.localeCompare(
        transaksiA,
        undefined,
        { numeric: true, sensitivity: "base" }
      );
    });

  const applyKordaSetoranFilters = () => {
    setAppliedKordaSetoranSearch(kordaSetoranSearch);
    setAppliedKordaSetoranStatusFilter(kordaSetoranStatusFilter);
  };

  const kordaBappTotal = filteredSubmitted.length;
  const kordaBappTotalPages = Math.max(1, Math.ceil(kordaBappTotal / kordaBappPerPage));
  const paginatedSubmitted = filteredSubmitted.slice(
    (kordaBappPage - 1) * kordaBappPerPage,
    kordaBappPage * kordaBappPerPage
  );

  // ============================================================
  // DATA KORWIL DARI BAPP AVAILABLE
  // ============================================================

  const korwilList = Array.from(
    new Set(
      (Array.isArray(available) ? available : [])
        .map((item) =>
          String(item?.korwil || "").trim()
        )
        .filter(Boolean)
    )
  ).sort((a, b) =>
    a.localeCompare(b, undefined, {
      numeric: true,
    })
  );

  // ============================================================
  // DATA MODAL
  // HANYA BAPP YANG BELUM SETOR
  // DAN SESUAI KORWIL
  // ============================================================

  const modalData = (
    Array.isArray(available) ? available : []
  )
    .filter((item) => {
      if (!selectedKorwil) return false;

      return (
        String(item?.korwil || "").trim() ===
        selectedKorwil
      );
    })
    .filter((item) => {
      if (!modalSearch.trim()) return true;

      const q = modalSearch.toLowerCase();

      return [
        item?.npsn,
        item?.sekolah,
        item?.nama_sekolah,
        item?.id_transaksi,
        item?.provinsi,
        item?.propinsi,
      ]
        .map((x) =>
          String(x || "").toLowerCase()
        )
        .some((x) => x.includes(q));
    })
    .sort((a, b) => {
      const npsnCompare = String(
        a?.npsn || ""
      ).localeCompare(
        String(b?.npsn || ""),
        undefined,
        { numeric: true }
      );

      if (npsnCompare !== 0) return npsnCompare;

      return String(
        a?.id_transaksi || ""
      ).localeCompare(
        String(b?.id_transaksi || ""),
        undefined,
        { numeric: true }
      );
    });

  // ============================================================
  // BUKA MODAL
  // ============================================================

  const openModal = async () => {
    setMessage("");
    setSetoranResult(null);
    setModalSelected([]);
    setSelectedKorwil("");
    setSelectedDirektorat("");
    setModalSearch("");
    setTanggalSetoran(
      new Date().toISOString().slice(0, 10)
    );

    try {
      const res = await api.get(
        "/finance/bapp-korda/next-no-setoran"
      );

      setNoSetoran(
        res.data?.no_setoran || "AUTO"
      );
    } catch (error) {
      console.error(
        "Gagal mengambil nomor setoran:",
        error
      );

      setNoSetoran("AUTO");
    }

    await loadAvailable?.();

    setModalOpen(true);
  };

  // ============================================================
  // SELECT BAPP
  // ============================================================

  const toggleModalId = (id) => {
    const value = String(id || "").trim();

    if (!value) return;

    setModalSelected((prev) =>
      prev.includes(value)
        ? prev.filter((x) => x !== value)
        : [...prev, value]
    );
  };

  const selectAllModal = () => {
    const ids = modalData
      .map((item) =>
        String(item?.id_transaksi || "").trim()
      )
      .filter(Boolean);

    const allSelected =
      ids.length > 0 &&
      ids.every((id) =>
        modalSelected.includes(id)
      );

    if (allSelected) {
      setModalSelected([]);
    } else {
      setModalSelected(ids);
    }
  };

  // ============================================================
  // SIMPAN SETORAN
  // ============================================================

  const saveSetoran = async () => {
    if (!selectedKorwil) {
      setMessage("Silakan pilih Korwil.");
      return;
    }

    if (!selectedDirektorat) {
      setMessage("Silakan pilih Direktorat.");
      return;
    }

    if (!tanggalSetoran) {
      setMessage("Silakan pilih tanggal setoran.");
      return;
    }

    if (!modalSelected.length) {
      setMessage("Silakan pilih minimal satu BAPP.");
      return;
    }

    const ok = window.confirm(
      `Buat setoran ${noSetoran} untuk ${modalSelected.length} BAPP ke Korwil ${selectedKorwil}?`
    );

    if (!ok) return;

    // Simpan semua nilai yang dibutuhkan SEBELUM state modal di-reset.
    // Ini membuat UI tidak bergantung pada hasil GET setelah POST.
    const selectedIds = sortFinanceRowsByTransactionNewest(modalSelected).map((id) => String(id).trim());
    const savedKorwil = selectedKorwil;
    const savedDirektorat = selectedDirektorat;
    const savedTanggal = tanggalSetoran;
    const savedModalRows = modalData.filter((item) =>
      selectedIds.includes(String(item?.id_transaksi || "").trim())
    );

    let postSucceeded = false;

    try {
      setSetoranLoading(true);
      setMessage("");

      const res = await api.post(
        "/finance/bapp-korda/setoran",
        {
          id_transaksi: selectedIds,
          korwil: savedKorwil,
          direktorat: savedDirektorat,
          tanggal_setoran: savedTanggal,
        }
      );

      const result = res.data || {};
      const savedNoSetoran = result.no_setoran || noSetoran;

      setSetoranResult(result);
      setMessage(
        result.message ||
        `Setoran ${savedNoSetoran} berhasil dibuat.`
      );

      postSucceeded = true;

      // ==========================================================
      // 1. TUTUP MODAL SEGERA SETELAH POST BERHASIL
      //    Tidak menunggu GET, Promise, atau refresh apa pun.
      // ==========================================================
      setModalOpen(false);
      setModalSelected([]);
      setSelectedKorwil("");
      setSelectedDirektorat("");
      setModalSearch("");

      // ==========================================================
      // 2. BENTUK BARIS BARU DARI DATA YANG SUDAH ADA DI MODAL.
      //    Ini adalah sumber UI paling stabil karena pasti memiliki
      //    id_transaksi yang baru saja dipilih user.
      // ==========================================================
      const responseRows = Array.isArray(result.data)
        ? result.data
        : [];

      const optimisticRows =
        sortFinanceRowsByTransactionNewest(savedModalRows).map((item) => ({
          ...item,
          no_setoran: savedNoSetoran,
          korwil: savedKorwil,
          direktorat: savedDirektorat,
          tanggal_setoran: savedTanggal,
          status_setoran: "MENUNGGU_REVIEW_KORWIL",
          status_review: "MENUNGGU_REVIEW_KORWIL",
        }));

      // Jika backend mengembalikan record lengkap, gunakan field
      // backend untuk melengkapi baris yang sama.
      const responseById = new Map(
        responseRows
          .filter((item) => item && typeof item === "object")
          .map((item) => [
            String(item?.id_transaksi || "").trim(),
            item,
          ])
      );

      const finalOptimisticRows = sortFinanceRowsByTransactionNewest(optimisticRows).map((item) => ({
        ...item,
        ...(responseById.get(
          String(item?.id_transaksi || "").trim()
        ) || {}),
        no_setoran:
          responseById.get(
            String(item?.id_transaksi || "").trim()
          )?.no_setoran || savedNoSetoran,
        korwil:
          responseById.get(
            String(item?.id_transaksi || "").trim()
          )?.korwil || savedKorwil,
        direktorat:
          responseById.get(
            String(item?.id_transaksi || "").trim()
          )?.direktorat || savedDirektorat,
        tanggal_setoran:
          responseById.get(
            String(item?.id_transaksi || "").trim()
          )?.tanggal_setoran || savedTanggal,
        status_setoran:
          responseById.get(
            String(item?.id_transaksi || "").trim()
          )?.status_setoran || "MENUNGGU_REVIEW_KORWIL",
        status_review:
          responseById.get(
            String(item?.id_transaksi || "").trim()
          )?.status_review || "MENUNGGU_REVIEW_KORWIL",
      }));

      // ==========================================================
      // 3. UPDATE TABEL KORDA LANGSUNG.
      // ==========================================================
      setJustSubmittedRows((prev) => {
        const byId = new Map();

        [...prev, ...finalOptimisticRows].forEach((item) => {
          const id = String(item?.id_transaksi || "").trim();
          if (id) byId.set(id, item);
        });

        return Array.from(byId.values());
      });

      // Update parent dashboard juga supaya state induk konsisten.
      setDashboard?.((prev) => {
        const current = prev || {};
        const currentData = Array.isArray(current.data)
          ? current.data
          : Array.isArray(current.items)
            ? current.items
            : [];

        const byId = new Map();

        [...currentData, ...finalOptimisticRows].forEach((item) => {
          const id = String(item?.id_transaksi || "").trim();
          if (id) byId.set(id, item);
        });

        const mergedData = Array.from(byId.values());

        return {
          ...current,
          data: mergedData,
          items: mergedData,
        };
      });

      // Hapus BAPP dari daftar AVAILABLE agar tidak muncul lagi
      // sebagai BAPP yang belum disetor.
      setAvailable?.((prev) =>
        (Array.isArray(prev) ? prev : []).filter(
          (item) =>
            !selectedIds.includes(
              String(item?.id_transaksi || "").trim()
            )
        )
      );

      // JANGAN GET ULANG DI SINI.
      // GET lama dapat mengembalikan snapshot sebelum transaksi
      // terlihat dan menimpa state optimistis yang baru saja dibuat.
    } catch (error) {
      console.error("Gagal membuat setoran BAPP:", {
        error,
        status: error?.response?.status,
        data: error?.response?.data,
        selectedIds,
      });

      // Recovery untuk kasus backend sudah menyimpan data tetapi response
      // POST gagal/timeout. Ini penting agar UI tidak mengatakan "gagal"
      // padahal record sudah masuk database.
      try {
        const verifyRes = await api.get(
          "/finance/bapp-korda/dashboard",
          { params: { _t: Date.now() } }
        );

        const verifyPayload = verifyRes?.data || {};
        const verifyRows = Array.isArray(verifyPayload?.data)
          ? verifyPayload.data
          : Array.isArray(verifyPayload?.items)
            ? verifyPayload.items
            : [];

        const selectedIdSet = new Set(selectedIds);
        const recoveredRows = verifyRows.filter((item) =>
          selectedIdSet.has(
            String(item?.id_transaksi || "").trim()
          )
        );

        // Jika seluruh BAPP yang dipilih sudah ada di dashboard,
        // anggap proses berhasil karena database sudah menerima setoran.
        if (
          selectedIds.length > 0 &&
          recoveredRows.length === selectedIds.length
        ) {
          setDashboard?.(verifyPayload);

          setJustSubmittedRows((prev) => {
            const byId = new Map();

            [...prev, ...recoveredRows].forEach((item) => {
              const id = String(item?.id_transaksi || "").trim();
              if (id) byId.set(id, item);
            });

            return Array.from(byId.values());
          });

          setAvailable?.((prev) =>
            (Array.isArray(prev) ? prev : []).filter(
              (item) =>
                !selectedIdSet.has(
                  String(item?.id_transaksi || "").trim()
                )
            )
          );

          setModalOpen(false);
          setModalSelected([]);
          setSelectedKorwil("");
          setSelectedDirektorat("");
          setModalSearch("");
          setMessage(
            "Setoran BAPP berhasil dibuat dan sudah tersimpan."
          );

          return;
        }
      } catch (verifyError) {
        console.warn(
          "Verifikasi setoran setelah response POST gagal:",
          verifyError
        );
      }

      const detail = error?.response?.data?.detail;

      setMessage(
        detail?.message ||
        detail ||
        "Gagal membuat setoran BAPP ke Korwil."
      );
    } finally {
      setSetoranLoading(false);

      // Safety net: kalau POST sudah sukses, modal HARUS tertutup
      // walaupun ada proses state/UI lain yang melempar error.
      if (postSucceeded) {
        setModalOpen(false);
      }
    }
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="finance-korda-page space-y-6 p-6 text-slate-100">

      <style>{`
        .finance-korda-page {
          height: calc(100vh - 88px);
          max-height: calc(100vh - 88px);
          min-height: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        .finance-korda-table-card {
          min-height: 0;
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .finance-korda-table-scroll {
          flex: 1 1 auto;
          min-height: 0;
          height: auto !important;
          max-height: none !important;
          overflow-x: auto !important;
          overflow-y: auto !important;
          overscroll-behavior: contain;
          position: relative;
          scrollbar-width: thin;
          scrollbar-gutter: stable;
        }

        .finance-korda-table-scroll thead {
          position: sticky;
          top: 0;
          z-index: 30;
        }

        .finance-korda-table-scroll thead th {
          position: sticky;
          top: 0;
          z-index: 31;
        }
      `}</style>

      {/* ======================================================
          HEADER
          ====================================================== */}

      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">

        <div>
          <h1 className="text-2xl font-bold">
            Finance KORDA
          </h1>

          <p className="mt-1 text-sm text-slate-400">
            Setoran BAPP dari KORDA ke KORWIL.
          </p>
        </div>

        {financeKordaTab === "SETORAN" && (
          <button
            type="button"
            onClick={openModal}
            disabled={availableLoading || setoranLoading}
            className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ➤&nbsp; Buat Setoran ke Korwil
          </button>
        )}

      </div>

      {/* ======================================================
          MESSAGE
          ====================================================== */}

      {message && (
        <div className="flex items-center justify-between rounded-xl border border-blue-800 bg-blue-950/40 px-4 py-3 text-sm text-blue-300">

          <span>
            {message}
          </span>

          <button
            type="button"
            onClick={() => setMessage("")}
            className="ml-4 font-bold"
          >
            ×
          </button>

        </div>
      )}

      {financeKordaTab === "SETORAN" ? (
        <>
      {/* ======================================================
          SUMMARY
          ====================================================== */}

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-xs text-slate-500">
            TOTAL BAPP SELESAI
          </div>
          <div className="mt-1 text-2xl font-bold text-white">
            {dashboardLoading
              ? "..."
              : totalBappSelesai}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-xs text-slate-500">
            BAPP SUDAH SETOR
          </div>
          <div className="mt-1 text-2xl font-bold text-blue-400">
            {dashboardLoading
              ? "..."
              : bappSudahSetor}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-xs text-slate-500">
            BAPP MENUNGGU REVIEW
          </div>
          <div className="mt-1 text-2xl font-bold text-yellow-400">
            {dashboardLoading
              ? "..."
              : bappMenungguReview}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
          <div className="text-xs text-slate-500">
            BAPP BELUM SETOR
          </div>
          <div className="mt-1 text-2xl font-bold text-slate-300">
            {dashboardLoading
              ? "..."
              : bappBelumSetor}
          </div>
        </div>

      </div>

      {/* ======================================================
          TABEL BAPP SUDAH SETOR
          ====================================================== */}

      <div className="finance-korda-table-card overflow-hidden rounded-2xl border border-slate-800 bg-slate-950">

        <div className="flex flex-col justify-between gap-3 border-b border-slate-800 bg-slate-900/60 p-4 md:flex-row md:items-center">

          <div>
            <h2 className="text-lg font-bold text-white">
              BAPP Yang Sudah Disetor ke Korwil
            </h2>

            <p className="mt-1 text-xs text-slate-500">
              Menampilkan BAPP yang sudah dibuatkan setoran.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">

            <select
              value={kordaSetoranStatusFilter}
              onChange={(e) =>
                setKordaSetoranStatusFilter(e.target.value)
              }
              className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500"
            >
              <option value="ALL">Semua Status</option>
              <option value="MENUNGGU_REVIEW_KORWIL">MENUNGGU REVIEW KORWIL</option>
              <option value="DITERIMA_KORWIL">DITERIMA KORWIL</option>
              <option value="REVISI_KORDA">REVISI - KEMBALI KE KORDA</option>
            </select>

            <input
              type="text"
              value={kordaSetoranSearch}
              onChange={(e) => setKordaSetoranSearch(e.target.value)}
              placeholder="Cari NPSN / sekolah / ID..."
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500 md:w-72"
            />
            <button type="button" onClick={applyKordaSetoranFilters} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">🔍 Cari</button>

            <button
              type="button"
              onClick={() => {
                loadDashboard?.();
                loadAvailable?.();
              }}
              disabled={
                dashboardLoading ||
                availableLoading
              }
              className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50"
            >
              ↻
            </button>

          </div>

        </div>

        {dashboardLoading ? (
          <div className="p-10 text-center text-sm text-slate-500">
            Memuat data...
          </div>
        ) : filteredSubmitted.length === 0 ? (
          <div className="p-12 text-center">

            <div className="text-4xl">
              📄
            </div>

            <div className="mt-3 text-sm font-semibold text-slate-300">
              Belum ada BAPP yang disetor ke Korwil
            </div>

            <div className="mt-1 text-xs text-slate-500">
              BAPP yang belum disetor akan muncul saat tombol
              "Buat Setoran ke Korwil" dipilih.
            </div>

          </div>
        ) : (
          <div className="finance-korda-table-scroll">

            <table className="min-w-full text-sm">

              <thead className="sticky top-0 z-20 bg-slate-900 text-xs uppercase text-slate-400">

                <tr>

                  <th className="px-4 py-3 text-left">
                    No Setoran
                  </th>

                  <th className="px-4 py-3 text-left">
                    ID Transaksi
                  </th>

                  <th className="px-4 py-3 text-left">
                    NPSN
                  </th>

                  <th className="px-4 py-3 text-left">
                    Nama Sekolah
                  </th>

                  <th className="px-4 py-3 text-left">
                    Provinsi
                  </th>

                  <th className="px-4 py-3 text-left">
                    Korwil
                  </th>

                  <th className="px-4 py-3 text-left">
                    Direktorat
                  </th>

                  <th className="px-4 py-3 text-left">
                    Status Instalasi
                  </th>

                  <th className="px-4 py-3 text-left">
                    Status Setoran
                  </th>
                      <th className="px-4 py-3 text-left">
                        CATATAN REVISI
                      </th>

                  <th className="px-4 py-3 text-center">
                    Aksi
                  </th>

                </tr>

              </thead>

              <tbody className="divide-y divide-slate-800">

                {sortFinanceRowsByTransactionNewest(paginatedSubmitted).map((item) => {

                  const status =
                    String(
                      item?.status_review ||
                      item?.status_setoran ||
                      "MENUNGGU_REVIEW"
                    ).toUpperCase();

                  const statusClass =
                    status === "DITERIMA" ||
                    status === "DITERIMA_KORWIL"
                      ? "text-green-400"
                      : status === "REVISI" ||
                        status === "REVISI_KORDA"
                        ? "text-red-400"
                        : "text-yellow-400";

                  const statusLabel =
                    status === "MENUNGGU_REVIEW"
                      ? "MENUNGGU REVIEW"
                      : status === "MENUNGGU_REVIEW_KORWIL"
                        ? "MENUNGGU REVIEW KORWIL"
                        : status === "DITERIMA_KORWIL"
                          ? "DITERIMA KORWIL"
                          : status === "REVISI_KORDA"
                            ? "REVISI - KEMBALI KE KORDA"
                            : status;

                  return (
                    <tr
                      key={String(
                        item?.id_transaksi
                      )}
                      className={`hover:bg-slate-900/70 ${
                        status === "REVISI_KORDA"
                          ? "bg-red-950/20"
                          : ""
                      }`}
                    >

                      <td className="px-4 py-3 font-mono text-xs text-blue-300">
                        {item?.no_setoran || "-"}
                      </td>

                      <td className="px-4 py-3 font-mono text-xs text-blue-300">
                        {item?.id_transaksi || "-"}
                      </td>

                      <td className="px-4 py-3 font-mono text-slate-300">
                        {item?.npsn || "-"}
                      </td>

                      <td className="px-4 py-3 font-medium text-white">
                        {item?.nama_sekolah ||
                          item?.sekolah ||
                          item?.nama ||
                          "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-400">
                        {item?.provinsi ||
                          item?.propinsi ||
                          "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-400">
                        {item?.korwil || "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-400">
                        {item?.direktorat || "-"}
                      </td>

                      <td className="px-4 py-3 text-slate-400">
                        {item?.status_instalasi ||
                          item?.ops?.status ||
                          "-"}
                      </td>

                      <td className={`px-4 py-3 font-semibold ${statusClass}`}>
                        {statusLabel}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {status === "REVISI_KORDA" && item?.catatan_revisi ? (
                          <div className="max-w-[220px] rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                            <div className="mb-1 font-semibold text-red-400">
                              Catatan Revisi
                            </div>
                            <div className="leading-relaxed">
                              {item.catatan_revisi}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-500">-</span>
                        )}
                      </td>

                      <td className="px-4 py-3 text-center">

                        <button
                          type="button"
                          onClick={async () => {
                            const noSetoran =
                              item?.no_setoran || "";

                            if (!noSetoran) {
                              setMessage(
                                "Nomor setoran tidak tersedia."
                              );
                              return;
                            }

                            try {
                              setMessage("");

                              const res = await api.get(
                                `/finance/bapp-korda/setoran/${encodeURIComponent(noSetoran)}`
                              );

                              const result = res?.data || {};

                              const rows =
                                Array.isArray(result?.data)
                                  ? result.data
                                  : Array.isArray(result?.items)
                                    ? result.items
                                    : [];

                              if (!rows.length) {
                                setMessage(
                                  `Data setoran ${noSetoran} tidak ditemukan.`
                                );
                                return;
                              }

                              const escapeHtml = (value) =>
                                String(value ?? "-")
                                  .replace(/&/g, "&amp;")
                                  .replace(/</g, "&lt;")
                                  .replace(/>/g, "&gt;")
                                  .replace(/"/g, "&quot;")
                                  .replace(/'/g, "&#039;");

                              const formatDate = (value) => {
                                if (!value) return "-";

                                const d = new Date(value);

                                if (Number.isNaN(d.getTime())) {
                                  return String(value);
                                }

                                return d.toLocaleDateString(
                                  "id-ID",
                                  {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  }
                                );
                              };

                              const tanggalSetoran =
                                result?.tanggal_setoran ||
                                rows[0]?.tanggal_setoran ||
                                rows[0]?.created_at ||
                                new Date().toISOString();

                              const korda =
                                result?.korda ||
                                rows[0]?.korda ||
                                "-";

                              const korwil =
                                result?.korwil ||
                                rows[0]?.korwil ||
                                "-";

                              const direktorat =
                                result?.direktorat ||
                                rows[0]?.direktorat ||
                                "-";

                              const status =
                                result?.status_review ||
                                result?.status_setoran ||
                                rows[0]?.status_review ||
                                rows[0]?.status_setoran ||
                                "MENUNGGU_REVIEW";

                              const printWindow =
                                window.open(
                                  "",
                                  "_blank",
                                  "width=900,height=1100"
                                );

                              if (!printWindow) {
                                setMessage(
                                  "Popup diblokir browser. Izinkan popup untuk mencetak."
                                );
                                return;
                              }



                              const tableRows = rows
                                .map(
                                  (row, index) => `
                                    <tr>
                                      <td class="no">${index + 1}</td>

                                      <td class="id">
                                        ${escapeHtml(
                                          row?.id_transaksi || "-"
                                        )}
                                      </td>

                                      <td class="npsn">
                                        ${escapeHtml(
                                          row?.npsn ||
                                          row?.NPSN ||
                                          "-"
                                        )}
                                      </td>

                                      <td class="school">
                                        ${escapeHtml(
                                          row?.nama_sekolah ||
                                          row?.sekolah ||
                                          row?.nama ||
                                          "-"
                                        )}
                                      </td>

                                      <td class="province">
                                        ${escapeHtml(
                                          row?.provinsi ||
                                          row?.propinsi ||
                                          "-"
                                        )}
                                      </td>

                                      <td class="korwil">
                                        ${escapeHtml(
                                          row?.korwil ||
                                          korwil ||
                                          "-"
                                        )}
                                      </td>

                                      <td class="direktorat">
                                        ${escapeHtml(
                                          row?.direktorat ||
                                          direktorat ||
                                          "-"
                                        )}
                                      </td>
                                    </tr>
                                  `
                                )
                                .join("");

                              printWindow.document.open();

                              printWindow.document.write(`
                                <!DOCTYPE html>
                                <html lang="id">
                                <head>
                                  <meta charset="UTF-8">

                                  <title>
                                    Bukti Setoran BAPP - ${escapeHtml(noSetoran)}
                                  </title>

                                  <style>

                                    /* ==================================================
                                       A4 PORTRAIT
                                       ================================================== */

                                    @page {
                                      size: A4 portrait;
                                      margin: 0;
                                    }

                                    * {
                                      box-sizing: border-box;
                                    }

                                    html,
                                    body {
                                      margin: 0;
                                      padding: 0;
                                      width: 100%;
                                      min-height: 100%;
                                      font-family:
                                        Arial,
                                        Helvetica,
                                        sans-serif;
                                      color: #111827;
                                      background: #ffffff;
                                      -webkit-print-color-adjust: exact;
                                      print-color-adjust: exact;
                                    }

                                    body {
                                      font-size: 10px;
                                    }

                                    /* ==================================================
                                       HALAMAN
                                       ================================================== */

                                    .page {
                                      position: relative;
                                      width: 210mm;
                                      min-height: 297mm;
                                      padding: 15mm 14mm 18mm 14mm;
                                      margin: 0 auto;
                                      overflow: visible;
                                      background: #ffffff;
                                    }

                                    /* Jangan memaksa semua data masuk 1 halaman.
                                       Browser boleh membuat halaman berikutnya
                                       jika tabel memang tidak cukup. */

                                    .content {
                                      position: relative;
                                      z-index: 2;
                                    }

                                    /* ==================================================
                                       LOGO
                                       ================================================== */

                                    .print-header {
                                      display: block;
                                      margin-bottom: 7mm;
                                    }

                                    .header-info {
                                      padding-top: 1mm;
                                      text-align: center;
                                    }

                                    /* ==================================================
                                       ORNAMEN
                                       ================================================== */

                                    .ornament-top {
                                      position: absolute;
                                      top: 0;
                                      right: 0;
                                      width: 62mm;
                                      height: 46mm;
                                      object-fit: contain;
                                      object-position: top right;
                                      opacity: 0.75;
                                      z-index: 0;
                                      pointer-events: none;
                                    }

                                    .ornament-bottom {
                                      position: absolute;
                                      left: 0;
                                      bottom: 0;
                                      width: 62mm;
                                      height: 52mm;
                                      object-fit: contain;
                                      object-position: bottom left;
                                      opacity: 0.75;
                                      z-index: 0;
                                      pointer-events: none;
                                    }

                                    /* ==================================================
                                       HEADER
                                       ================================================== */

                                    .document-code {
                                      font-size: 7.5px;
                                      color: #64748b;
                                      letter-spacing: 0.2px;
                                      margin-bottom: 3mm;
                                    }

                                     .title {
                                      font-size: 30px;
                                      font-weight: 800;
                                      letter-spacing: 0.2px;
                                      color: #0f172a;
                                      line-height: 1.1;
                                    }

                                    .subtitle {
                                      margin-top: 1.5mm;
                                      font-size: 10px;
                                      font-weight: 500;
                                      color: #334155;
                                    }

                                    .separator {
                                      height: 1.2px;
                                      background: #0f172a;
                                      margin-top: 6mm;
                                      margin-bottom: 5mm;
                                    }

                                    /* ==================================================
                                       SUMMARY
                                       ================================================== */

                                    .summary {
                                      display: grid;
                                      grid-template-columns: 1fr 1fr;
                                      column-gap: 15mm;
                                      row-gap: 2mm;
                                      margin-bottom: 6mm;
                                    }

                                    .summary-column {
                                      display: flex;
                                      flex-direction: column;
                                      gap: 1.7mm;
                                    }

                                    .summary-row {
                                      display: grid;
                                      grid-template-columns: 32mm 1fr;
                                      column-gap: 3mm;
                                      line-height: 1.35;
                                    }

                                    .summary-label {
                                      font-weight: 700;
                                      color: #0f172a;
                                    }

                                    .summary-value {
                                      color: #1e293b;
                                      overflow-wrap: anywhere;
                                    }

                                    /* ==================================================
                                       TABLE
                                       ================================================== */

                                    .table-wrap {
                                      width: 100%;
                                      overflow: visible;
                                    }

                                    table {
                                      width: 100%;
                                      border-collapse: collapse;
                                      table-layout: fixed;
                                      font-size: 8.5px;
                                    }

                                    thead {
                                      display: table-header-group;
                                    }

                                    tbody {
                                      display: table-row-group;
                                    }

                                    tr {
                                      break-inside: avoid;
                                      page-break-inside: avoid;
                                    }

                                    th,
                                    td {
                                      border: 0.6px solid #94a3b8;
                                      padding: 2.4mm 1.5mm;
                                      vertical-align: middle;
                                      overflow-wrap: anywhere;
                                      word-break: break-word;
                                    }

                                    th {
                                      background: #f1f5f9;
                                      color: #0f172a;
                                      font-weight: 700;
                                      text-align: center;
                                    }

                                    td {
                                      color: #1e293b;
                                    }

                                    td.no {
                                      width: 7%;
                                      text-align: center;
                                    }

                                    td.id {
                                      width: 20%;
                                      font-family:
                                        Arial,
                                        Helvetica,
                                        sans-serif;
                                      font-size: 8.5px;
                                      font-weight: 600;
                                    }

                                    td.npsn {
                                      width: 11%;
                                      text-align: center;
                                    }

                                    td.school {
                                      width: 25%;
                                      font-weight: 600;
                                    }

                                    td.province {
                                      width: 18%;
                                    }

                                    td.korwil {
                                      width: 10%;
                                    }

                                    td.direktorat {
                                      width: 9%;
                                      text-align: center;
                                    }

                                    th:nth-child(1) {
                                      width: 7%;
                                    }

                                    th:nth-child(2) {
                                      width: 20%;
                                    }

                                    th:nth-child(3) {
                                      width: 11%;
                                    }

                                    th:nth-child(4) {
                                      width: 25%;
                                    }

                                    th:nth-child(5) {
                                      width: 18%;
                                    }

                                    th:nth-child(6) {
                                      width: 10%;
                                    }

                                    th:nth-child(7) {
                                      width: 9%;
                                    }

                                    /* ==================================================
                                       FOOTER
                                       ================================================== */

                                    .footer {
                                      margin-top: 7mm;
                                      font-size: 8px;
                                      color: #475569;
                                      display: flex;
                                      justify-content: space-between;
                                      gap: 8mm;
                                    }

                                    /* ==================================================
                                       SIGNATURE
                                       ================================================== */

                                    .signature-grid {
                                      margin-top: 13mm;
                                      width: 100%;
                                      display: grid;
                                      grid-template-columns: 1fr 1fr;
                                      column-gap: 30mm;
                                      break-inside: avoid;
                                      page-break-inside: avoid;
                                    }

                                    .signature {
                                      text-align: center;
                                      break-inside: avoid;
                                      page-break-inside: avoid;
                                    }

                                    .signature-title {
                                      font-size: 9px;
                                      font-weight: 700;
                                      color: #0f172a;
                                      line-height: 1.3;
                                      margin-bottom: 1mm;
                                    }

                                    .signature-role {
                                      font-size: 9px;
                                      color: #0f172a;
                                      margin-bottom: 16mm;
                                    }

                                    .signature-line {
                                      border-bottom: 0.7px solid #0f172a;
                                      height: 1px;
                                      margin: 0 auto 2mm auto;
                                      width: 52mm;
                                    }

                                    .signature-name {
                                      font-size: 9px;
                                      font-weight: 700;
                                      color: #0f172a;
                                    }

                                    /* ==================================================
                                       PRINT
                                       ================================================== */

                                    @media print {

                                      html,
                                      body {
                                        width: 210mm;
                                        margin: 0;
                                        padding: 0;
                                      }

                                      .page {
                                        width: 210mm;
                                        min-height: 297mm;
                                        margin: 0;
                                        padding: 15mm 14mm 18mm 14mm;
                                      }

                                      .no-print {
                                        display: none !important;
                                      }

                                      /* Penting:
                                         jangan menggunakan height: 297mm;
                                         supaya halaman berikutnya dapat dibuat
                                         otomatis bila data tabel banyak. */

                                      table {
                                        page-break-inside: auto;
                                      }

                                      thead {
                                        display: table-header-group;
                                      }

                                      tr {
                                        page-break-inside: avoid;
                                        break-inside: avoid;
                                      }

                                    }

                                  </style>
                                </head>

                                <body>

                                  <div class="page">

                                    <!-- ORNAMEN PYXSCREEN -->
                                    <div class="ornament-top"></div>
                                    <div class="ornament-bottom"></div>

                                    <div class="content">

                                      <div class="print-header">


                                        <div class="header-info">
                                          <div class="document-code">
                                            ${escapeHtml(noSetoran)}
                                          </div>

                                          <div class="title">
                                            BUKTI SETORAN BAPP
                                          </div>

                                          <div class="subtitle">
                                            KORDA → KORWIL
                                          </div>
                                        </div>
                                      </div>

                                      <div class="separator"></div>

                                      <!-- SUMMARY -->

                                      <div class="summary">

                                        <div class="summary-column">

                                          <div class="summary-row">
                                            <div class="summary-label">
                                              Nomor Setoran
                                            </div>

                                            <div class="summary-value">
                                              ${escapeHtml(noSetoran)}
                                            </div>
                                          </div>

                                          <div class="summary-row">
                                            <div class="summary-label">
                                              KORDA
                                            </div>

                                            <div class="summary-value">
                                              ${escapeHtml(korda)}
                                            </div>
                                          </div>

                                          <div class="summary-row">
                                            <div class="summary-label">
                                              Direktorat
                                            </div>

                                            <div class="summary-value">
                                              ${escapeHtml(direktorat)}
                                            </div>
                                          </div>

                                          <div class="summary-row">
                                            <div class="summary-label">
                                              Total BAPP
                                            </div>

                                            <div class="summary-value">
                                              ${rows.length}
                                            </div>
                                          </div>

                                        </div>

                                        <div class="summary-column">

                                          <div class="summary-row">
                                            <div class="summary-label">
                                              Tanggal Setoran
                                            </div>

                                            <div class="summary-value">
                                              ${escapeHtml(
                                                formatDate(tanggalSetoran)
                                              )}
                                            </div>
                                          </div>

                                          <div class="summary-row">
                                            <div class="summary-label">
                                              KORWIL
                                            </div>

                                            <div class="summary-value">
                                              ${escapeHtml(korwil)}
                                            </div>
                                          </div>

                                          <div class="summary-row">
                                            <div class="summary-label">
                                              Status
                                            </div>

                                            <div class="summary-value">
                                              ${escapeHtml(status)}
                                            </div>
                                          </div>

                                        </div>

                                      </div>

                                      <!-- TABLE -->

                                      <div class="table-wrap">

                                        <table>

                                          <thead>

                                            <tr>
                                              <th>No</th>
                                              <th>ID Transaksi</th>
                                              <th>NPSN</th>
                                              <th>Nama Sekolah</th>
                                              <th>Provinsi</th>
                                              <th>Korwil</th>
                                              <th>Direktorat</th>
                                            </tr>

                                          </thead>

                                          <tbody>
                                            ${tableRows}
                                          </tbody>

                                        </table>

                                      </div>

                                      <div class="footer">

                                        <div>
                                          Dicetak dari Finance BAPP KORDA
                                        </div>

                                        <div>
                                          ${escapeHtml(
                                            new Date().toLocaleString(
                                              "id-ID"
                                            )
                                          )}
                                        </div>

                                      </div>

                                      <!-- TANDA TERIMA -->

                                      <div class="signature-grid">

                                        <div class="signature">
                                          <div class="signature-title">
                                            Pengirim
                                          </div>
                                          <div class="signature-role">
                                            KORDA
                                          </div>
                                          <div class="signature-line"></div>
                                          <div class="signature-name">
                                            ${escapeHtml(korda)}
                                          </div>
                                        </div>

                                        <div class="signature">
                                          <div class="signature-title">
                                            Penerima
                                          </div>
                                          <div class="signature-role">
                                            KORWIL
                                          </div>
                                          <div class="signature-line"></div>
                                          <div class="signature-name">
                                            ${escapeHtml(korwil)}
                                          </div>
                                        </div>

                                      </div>

                                    </div>

                                  </div>

                                </body>
                                </html>
                              `);

                              printWindow.document.close();

                              printWindow.focus();

                              setTimeout(() => {
                                printWindow.print();

                                setTimeout(() => {
                                  printWindow.close();
                                }, 1000);
                              }, 500);

                            } catch (error) {

                              console.error(
                                "Gagal mencetak BAPP KORDA:",
                                error
                              );

                              setMessage(
                                error?.response?.data?.detail ||
                                "Gagal mengambil data setoran untuk dicetak."
                              );
                            }
                          }}
                          className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-slate-800"
                        >
                          <Printer size={14} className="mr-1 inline" />
                          Print
                        </button>
                      </td>

                    </tr>
                  );
                })}

              </tbody>

            </table>

          </div>
        )}
        {filteredSubmitted.length > 0 && (
          <div data-korda-pagination className="flex flex-col gap-3 border-t border-slate-800 bg-slate-950 px-4 py-3 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-slate-400">
              Menampilkan {((kordaBappPage - 1) * kordaBappPerPage + 1)} - {Math.min(kordaBappPage * kordaBappPerPage, kordaBappTotal)} dari {kordaBappTotal} data
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={kordaBappPerPage}
                onChange={(e) => {
                  setKordaBappPerPage(Number(e.target.value));
                  setKordaBappPage(1);
                }}
                className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200"
              >
                <option value={20}>20 / halaman</option>
                <option value={50}>50 / halaman</option>
                <option value={100}>100 / halaman</option>
              </select>
              <button type="button" disabled={kordaBappPage <= 1} onClick={() => setKordaBappPage((p) => Math.max(1, p - 1))} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
                ← Sebelumnya
              </button>
              <div className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300">
                Halaman {kordaBappPage} / {kordaBappTotalPages}
              </div>
              <button type="button" disabled={kordaBappPage >= kordaBappTotalPages} onClick={() => setKordaBappPage((p) => Math.min(kordaBappTotalPages, p + 1))} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
                Berikutnya →
              </button>
            </div>
          </div>
        )}

      </div>


        </>
      ) : (

        <>
          <div className="finance-korda-table-card overflow-hidden rounded-2xl border border-slate-800 bg-slate-950">
            <div className="border-b border-slate-800 bg-slate-900/60 p-4">
              <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
                <div>
                  <h2 className="text-lg font-bold text-white">Pembayaran BAPP KORDA</h2>
                  <p className="mt-1 text-xs text-slate-500">BAPP yang sudah disetor ke KORWIL.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { setPaymentStatusFilter(""); setPaymentPage(1); }} className={!paymentStatusFilter ? "rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white" : "rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-400"}>
                    Semua {paymentStatusCounts.BELUM_DIBAYAR + paymentStatusCounts.DIBAYAR_SEBAGIAN + paymentStatusCounts.LUNAS}
                  </button>
                  <button type="button" onClick={() => { setPaymentStatusFilter("BELUM_DIBAYAR"); setPaymentPage(1); }} className={paymentStatusFilter === "BELUM_DIBAYAR" ? "rounded-lg bg-yellow-500 px-3 py-2 text-xs font-semibold text-white" : "rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-400"}>
                    Belum Dibayar {paymentStatusCounts.BELUM_DIBAYAR}
                  </button>
                  <button type="button" onClick={() => { setPaymentStatusFilter("DIBAYAR_SEBAGIAN"); setPaymentPage(1); }} className={paymentStatusFilter === "DIBAYAR_SEBAGIAN" ? "rounded-lg bg-orange-500 px-3 py-2 text-xs font-semibold text-white" : "rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-400"}>
                    Dibayar Sebagian {paymentStatusCounts.DIBAYAR_SEBAGIAN}
                  </button>
                  <button type="button" onClick={() => { setPaymentStatusFilter("LUNAS"); setPaymentPage(1); }} className={paymentStatusFilter === "LUNAS" ? "rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white" : "rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-400"}>
                    Lunas {paymentStatusCounts.LUNAS}
                  </button>
                </div>
              </div>

              <div className="mt-4 flex gap-2">
                <input type="text" value={paymentSearch} onChange={(e) => { setPaymentSearch(e.target.value); setPaymentPage(1); }} placeholder="Cari No Setoran / ID Transaksi / Sekolah / Provinsi / Korwil..." className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500" />
                <button type="button" onClick={() => loadPaymentStatus(paymentPage)} disabled={paymentLoading} className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50">↻</button>
              </div>
            </div>

            {paymentLoading ? (
              <div className="p-10 text-center text-sm text-slate-500">Memuat status pembayaran...</div>
            ) : paymentItems.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-4xl">💳</div>
                <div className="mt-3 text-sm font-semibold text-slate-300">Tidak ada data pembayaran BAPP</div>
                <div className="mt-1 text-xs text-slate-500">BAPP yang sudah disetor ke KORWIL akan muncul di sini.</div>
              </div>
            ) : (
              <>
                <div className="finance-korda-table-scroll">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-900 text-xs uppercase text-slate-400">
                      <tr>
                        <th className="px-4 py-3 text-left">No Setoran</th>
                        <th className="px-4 py-3 text-left">ID Transaksi</th>
                        <th className="px-4 py-3 text-left">Sekolah</th>
                        <th className="px-4 py-3 text-left">Provinsi</th>
                        <th className="px-4 py-3 text-left">Korwil</th>
                        <th className="px-4 py-3 text-left">Status Pembayaran</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {sortFinanceRowsByTransactionNewest(paymentItems).map((item) => {
                        const status = String(item?.status_pembayaran || "BELUM_DIBAYAR").toUpperCase();
                        const label = status === "LUNAS" ? "LUNAS" : status === "DIBAYAR_SEBAGIAN" ? "DIBAYAR SEBAGIAN" : "BELUM DIBAYAR";
                        const statusClass = status === "LUNAS" ? "px-4 py-3 font-semibold text-green-400" : status === "DIBAYAR_SEBAGIAN" ? "px-4 py-3 font-semibold text-orange-400" : "px-4 py-3 font-semibold text-yellow-400";
                        return (
                          <tr key={String(item?.id_transaksi || "") + "-" + String(item?.no_setoran || "")} className="hover:bg-slate-900/70">
                            <td className="px-4 py-3 font-mono text-xs text-blue-300">{item?.no_setoran || "-"}</td>
                            <td className="px-4 py-3 font-mono text-xs text-blue-300">{item?.id_transaksi || "-"}</td>
                            <td className="px-4 py-3 font-medium text-white">{item?.sekolah || "-"}</td>
                            <td className="px-4 py-3 text-slate-400">{item?.provinsi || "-"}</td>
                            <td className="px-4 py-3 text-slate-400">{item?.korwil || "-"}</td>
                            <td className={statusClass}>{label}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex flex-col gap-3 border-t border-slate-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-xs text-slate-400">
                    {paymentTotal > 0 ? <>Menampilkan <span className="font-semibold text-slate-200">{((paymentPage - 1) * paymentPerPage + 1).toLocaleString("id-ID")}</span> - <span className="font-semibold text-slate-200">{Math.min(paymentPage * paymentPerPage, paymentTotal).toLocaleString("id-ID")}</span> dari <span className="font-semibold text-slate-200">{paymentTotal.toLocaleString("id-ID")}</span> data</> : "Tidak ada data"}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={paymentPerPage} onChange={(e) => { setPaymentPerPage(Number(e.target.value)); setPaymentPage(1); }} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200">
                      <option value={20}>20 / halaman</option>
                      <option value={50}>50 / halaman</option>
                      <option value={100}>100 / halaman</option>
                    </select>
                    <button type="button" disabled={paymentLoading || paymentPage <= 1} onClick={() => { const next = Math.max(1, paymentPage - 1); setPaymentPage(next); loadPaymentStatus(next); }} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 disabled:opacity-40">← Sebelumnya</button>
                    <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-xs font-semibold text-slate-300">Halaman <span className="text-white">{paymentPage}</span> / <span className="text-white">{paymentTotalPages}</span></div>
                    <button type="button" disabled={paymentLoading || paymentPage >= paymentTotalPages} onClick={() => { const next = Math.min(paymentTotalPages, paymentPage + 1); setPaymentPage(next); loadPaymentStatus(next); }} className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 disabled:opacity-40">Berikutnya →</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ======================================================
          INCOMING BAPP DARI KORDA
          KHUSUS KORWIL / SUPER ADMIN / FINANCE
          ====================================================== */}




      {/* ======================================================
          MODAL BUAT SETORAN
          ====================================================== */}

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4">

          <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">

            {/* HEADER */}

            <div className="border-b border-slate-800 bg-slate-900 p-6">

              <div className="flex items-start justify-between gap-4">

                <div>

                  <div className="text-xs font-bold uppercase tracking-wider text-blue-400">
                    SETORAN BAPP KORDA → KORWIL
                  </div>

                  <h2 className="mt-1 text-xl font-bold text-white">
                    Buat Setoran BAPP
                  </h2>

                </div>

                <button
                  type="button"
                  onClick={() => {
                    if (!setoranLoading) {
                      setModalOpen(false);
                    }
                  }}
                  className="text-2xl leading-none text-slate-400 hover:text-white"
                >
                  ×
                </button>

              </div>

              {/* FORM HEADER */}

              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-4">

                <div>

                  <label className="mb-2 block text-xs font-semibold text-slate-400">
                    No Setoran
                  </label>

                  <input
                    type="text"
                    value={noSetoran}
                    readOnly
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 font-mono text-sm font-bold text-blue-300"
                  />

                </div>

                <div>

                  <label className="mb-2 block text-xs font-semibold text-slate-400">
                    Tanggal Setoran
                  </label>

                  <input
                    type="date"
                    value={tanggalSetoran}
                    onChange={(e) =>
                      setTanggalSetoran(
                        e.target.value
                      )
                    }
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                  />

                </div>

                <div>

                  <label className="mb-2 block text-xs font-semibold text-slate-400">
                    Korwil
                  </label>

                  <select
                    value={selectedKorwil}
                    onChange={(e) => {
                      setSelectedKorwil(
                        e.target.value
                      );
                      setModalSelected([]);
                    }}
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                  >

                    <option value="">
                      Pilih Korwil
                    </option>

                    {sortFinanceRowsByTransactionNewest(korwilList).map((korwil) => (
                      <option
                        key={korwil}
                        value={korwil}
                      >
                        {korwil}
                      </option>
                    ))}

                  </select>

                </div>

                <div>

                  <label className="mb-2 block text-xs font-semibold text-slate-400">
                    Direktorat
                  </label>

                  <select
                    value={selectedDirektorat}
                    onChange={(e) =>
                      setSelectedDirektorat(
                        e.target.value
                      )
                    }
                    className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white outline-none focus:border-blue-500"
                  >

                    <option value="">
                      Pilih Direktorat
                    </option>

                    <option value="PAUD">
                      PAUD
                    </option>

                    <option value="SD">
                      SD
                    </option>

                    <option value="SMP">
                      SMP
                    </option>

                    <option value="SMA">
                      SMA
                    </option>

                  </select>

                </div>

              </div>

            </div>

            {/* BODY */}

            <div className="min-h-0 flex-1 overflow-hidden">

              <div className="flex flex-col gap-3 border-b border-slate-800 bg-slate-950 p-4 md:flex-row md:items-center md:justify-between">

                <div>

                  <div className="text-sm font-semibold text-white">
                    BAPP Tersedia untuk Disetor
                  </div>

                  <div className="mt-1 text-xs text-slate-500">

                    {selectedKorwil
                      ? `Menampilkan BAPP milik ${selectedKorwil}`
                      : "Pilih Korwil terlebih dahulu"}

                  </div>

                </div>

                <div className="flex gap-2">

                  <input
                    type="text"
                    value={modalSearch}
                    onChange={(e) =>
                      setModalSearch(
                        e.target.value
                      )
                    }
                    placeholder="Cari NPSN / sekolah / ID..."
                    disabled={!selectedKorwil}
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white outline-none focus:border-blue-500 disabled:opacity-40 md:w-72"
                  />

                  <button
                    type="button"
                    onClick={selectAllModal}
                    disabled={
                      !selectedKorwil ||
                      modalData.length === 0
                    }
                    className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-slate-800 disabled:opacity-40"
                  >
                    Pilih Semua
                  </button>

                </div>

              </div>

              <div className="max-h-[48vh] overflow-auto">

                {!selectedKorwil ? (

                  <div className="p-12 text-center text-sm text-slate-500">
                    Pilih Korwil untuk menampilkan BAPP yang tersedia.
                  </div>

                ) : availableLoading ? (

                  <div className="p-12 text-center text-sm text-slate-500">
                    Memuat BAPP...
                  </div>

                ) : modalData.length === 0 ? (

                  <div className="p-12 text-center">

                    <div className="text-3xl">
                      ✓
                    </div>

                    <div className="mt-3 text-sm font-semibold text-slate-300">
                      Tidak ada BAPP yang tersedia
                    </div>

                    <div className="mt-1 text-xs text-slate-500">
                      Semua BAPP untuk Korwil ini sudah masuk setoran.
                    </div>

                  </div>

                ) : (

                  <table className="min-w-full text-sm">

                    <thead className="sticky top-0 z-10 bg-slate-900 text-xs uppercase text-slate-400">

                      <tr>

                        <th className="w-12 px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={
                              modalData.length > 0 &&
                              modalData.every((item) =>
                                modalSelected.includes(
                                  String(
                                    item?.id_transaksi || ""
                                  )
                                )
                              )
                            }
                            onChange={selectAllModal}
                          />
                        </th>

                        <th className="px-4 py-3 text-left">
                          ID Transaksi
                        </th>

                        <th className="px-4 py-3 text-left">
                          NPSN
                        </th>

                        <th className="px-4 py-3 text-left">
                          Nama Sekolah
                        </th>

                        <th className="px-4 py-3 text-left">
                          Provinsi
                        </th>

                        <th className="px-4 py-3 text-left">
                          Status Instalasi
                        </th>

                      </tr>

                    </thead>

                    <tbody className="divide-y divide-slate-800">

                      {sortFinanceRowsByTransactionNewest(modalData).map((item) => {

                        const id = String(
                          item?.id_transaksi || ""
                        );

                        const checked =
                          modalSelected.includes(id);

                        return (
                          <tr
                            key={id}
                            className={
                              checked
                                ? "bg-blue-950/30"
                                : "hover:bg-slate-900/70"
                            }
                          >

                            <td className="px-4 py-3 text-center">

                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() =>
                                  toggleModalId(id)
                                }
                              />

                            </td>

                            <td className="px-4 py-3 font-mono text-xs text-blue-300">
                              {id || "-"}
                            </td>

                            <td className="px-4 py-3 font-mono text-slate-300">
                              {item?.npsn || "-"}
                            </td>

                            <td className="px-4 py-3 font-medium text-white">
                              {item?.nama_sekolah ||
                                item?.sekolah ||
                                "-"}
                            </td>

                            <td className="px-4 py-3 text-slate-400">
                              {item?.provinsi ||
                                item?.propinsi ||
                                "-"}
                            </td>

                            <td className="px-4 py-3 text-slate-400">
                              {item?.status_instalasi ||
                                item?.ops?.status ||
                                "SELESAI"}
                            </td>

                          </tr>
                        );
                      })}

                    </tbody>

                  </table>

                )}

              </div>

            </div>

            {/* FOOTER */}

            <div className="flex flex-col justify-between gap-4 border-t border-slate-800 bg-slate-900/60 p-5 md:flex-row md:items-center">

              <div className="text-sm text-slate-400">

                <span className="font-semibold text-white">
                  {modalSelected.length}
                </span>{" "}
                BAPP dipilih

                {selectedKorwil && (
                  <>
                    {" · "}
                    Korwil:{" "}
                    <span className="font-semibold text-blue-300">
                      {selectedKorwil}
                    </span>
                  </>
                )}

                {selectedDirektorat && (
                  <>
                    {" · "}
                    Direktorat:{" "}
                    <span className="font-semibold text-blue-300">
                      {selectedDirektorat}
                    </span>
                  </>
                )}

              </div>

              <div className="flex justify-end gap-3">

                <button
                  type="button"
                  disabled={setoranLoading}
                  onClick={() =>
                    setModalOpen(false)
                  }
                  className="rounded-lg border border-slate-700 bg-slate-900 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                >
                  Batal
                </button>

                <button
                  type="button"
                  disabled={
                    !selectedKorwil ||
                    !selectedDirektorat ||
                    !tanggalSetoran ||
                    modalSelected.length === 0 ||
                    setoranLoading
                  }
                  onClick={saveSetoran}
                  className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {setoranLoading
                    ? "Menyimpan..."
                    : `Buat Setoran (${modalSelected.length})`}
                </button>

              </div>

            </div>

          </div>

        </div>
      )}

    </div>
  );
}


function FinanceReportPanel({ api, setMessage, reportParams = {} }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadReport = async () => {
    try {
      setLoading(true);

      const res = await api.get("/finance/reporting", { params: reportParams });

      setReport(res.data || null);
    } catch (err) {
      console.error(err);

      setMessage(
        err?.response?.data?.detail ||
        "Gagal mengambil laporan Finance."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        const res = await api.get("/finance/reporting", { params: reportParams });

        if (cancelled) return;

        setReport(res.data || null);
      } catch (err) {
        if (cancelled) return;

        console.error(err);

        setMessage(
          err?.response?.data?.detail ||
          "Gagal mengambil laporan Finance."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [api, setMessage]);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-700 bg-white p-10 text-center text-sm text-slate-500">
        Memuat laporan Finance...
      </div>
    );
  }

  if (!report) {
    return (
      <div className="rounded-xl border border-slate-700 bg-white p-10 text-center text-sm text-slate-500">
        Data laporan belum tersedia.
      </div>
    );
  }

  const distribution =
    report.distribution_final || [];

  const generalExpenses =
    report.biaya_operasional_umum_detail || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">
            Laporan Finance
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Rekap pembayaran, margin, biaya operasional,
            dan pembagian margin KORWIL.
          </p>
        </div>

        <button
          type="button"
          onClick={loadReport}
          className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          Refresh Laporan
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Pembayaran Pusat"
          value={rupiah(
            report.total_pembayaran_pusat
          )}
        />

        <SummaryCard
          title="Kewajiban Korda"
          value={rupiah(
            report.total_kewajiban_korda
          )}
        />

        <SummaryCard
          title="Margin Kotor"
          value={rupiah(
            report.total_margin_kotor
          )}
        />

        <SummaryCard
          title="Margin Bersih KORWIL"
          value={rupiah(
            report.margin_bersih_korwil
          )}
        />
      </div>

      <div className="rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
        <div className="border-b p-4">
          <h3 className="font-bold">
            Ringkasan Margin
          </h3>
        </div>

        <div className="grid gap-4 p-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-xs uppercase text-slate-500">
              Margin Kotor
            </div>
            <div className="mt-2 text-lg font-bold">
              {rupiah(
                report.total_margin_kotor
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-xs uppercase text-slate-500">
              Operasional Transaksi
            </div>
            <div className="mt-2 text-lg font-bold">
              {rupiah(
                report.total_biaya_operasional_transaksi
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-xs uppercase text-slate-500">
              Operasional Umum
            </div>
            <div className="mt-2 text-lg font-bold">
              {rupiah(
                report.total_biaya_operasional_umum
              )}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 p-4">
            <div className="text-xs uppercase text-slate-500">
              Margin Bersih Transaksi
            </div>
            <div className="mt-2 text-lg font-bold">
              {rupiah(
                report.margin_bersih_korwil
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
        <div className="border-b p-4">
          <h3 className="font-bold">
            Pembagian Margin KORWIL
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Margin bersih KORWIL dibagi sesuai Master
            Pembagian Margin aktif.
          </p>
        </div>

        <div className="finance-table-scroll overflow-x-auto p-4">
          {distribution.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Belum ada Master Pembagian Margin aktif
              untuk KORWIL ini.
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-slate-700">
                  <th className="px-3 py-3">
                    Komponen
                  </th>

                  <th className="px-3 py-3 text-right">
                    Persentase
                  </th>

                  <th className="px-3 py-3 text-right">
                    Nominal
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortFinanceRowsByTransactionNewest(distribution).map(
                  (item, index) => (
                    <tr
                      key={`${item.nama}-${index}`}
                      className="border-b last:border-b-0"
                    >
                      <td className="px-3 py-3 font-semibold">
                        {item.nama}
                      </td>

                      <td className="px-3 py-3 text-right">
                        {item.persentase}%
                      </td>

                      <td className="px-3 py-3 text-right font-bold">
                        {rupiah(
                          item.nominal
                        )}
                      </td>
                    </tr>
                  )
                )}
              </tbody>

              <tfoot>
                <tr className="bg-slate-50 font-bold">
                  <td className="px-3 py-3">
                    TOTAL
                  </td>

                  <td className="px-3 py-3 text-right">
                    {distribution.reduce(
                      (sum, item) =>
                        sum +
                        Number(
                          item.persentase || 0
                        ),
                      0
                    )}%
                  </td>

                  <td className="px-3 py-3 text-right">
                    {rupiah(
                      distribution.reduce(
                        (sum, item) =>
                          sum +
                          Number(
                            item.nominal || 0
                          ),
                        0
                      )
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
        <div className="border-b p-4">
          <h3 className="font-bold">
            Operasional Umum KORWIL
          </h3>

          <p className="mt-1 text-sm text-slate-500">
            Biaya tanpa ID transaksi seperti gaji admin,
            entertainment, internet, dan biaya umum lainnya.
          </p>
        </div>

        <div className="p-4">
          {generalExpenses.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Belum ada biaya operasional umum.
            </div>
          ) : (
            <div className="finance-table-scroll overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-slate-700">
                    <th className="px-3 py-3">
                      Tanggal
                    </th>

                    <th className="px-3 py-3">
                      Biaya
                    </th>

                    <th className="px-3 py-3">
                      Kategori
                    </th>

                    <th className="px-3 py-3 text-right">
                      Nominal
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortFinanceRowsByTransactionNewest(generalExpenses).map(
                    (item, index) => (
                      <tr
                        key={`${item.id || "expense"}-${index}`}
                        className="border-b last:border-b-0"
                      >
                        <td className="px-3 py-3">
                          {item.tanggal
                            ? String(
                                item.tanggal
                              ).slice(0, 10)
                            : "-"}
                        </td>

                        <td className="px-3 py-3">
                          <div className="font-semibold">
                            {item.nama || "-"}
                          </div>

                          {item.catatan && (
                            <div className="mt-1 text-xs text-slate-500">
                              {item.catatan}
                            </div>
                          )}
                        </td>

                        <td className="px-3 py-3">
                          {String(
                            item.kategori ||
                              "LAINNYA"
                          ).replaceAll(
                            "_",
                            " "
                          )}
                        </td>

                        <td className="px-3 py-3 text-right font-semibold">
                          {rupiah(
                            item.nominal
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>

                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <td
                      colSpan="3"
                      className="px-3 py-3 text-right"
                    >
                      TOTAL
                    </td>

                    <td className="px-3 py-3 text-right">
                      {rupiah(
                        report.total_biaya_operasional_umum
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExpensePanel({ api, setMessage }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(true);
  const [form, setForm] = useState({
    nama: "",
    kategori: "LAINNYA",
    nominal: "",
    tanggal: new Date().toISOString().slice(0, 10),
    id_transaksi: "",
    catatan: "",
  });

  const categories = [
    "GAJI_ADMIN",
    "ENTERTAINMENT",
    "TRANSPORT",
    "AKOMODASI",
    "ATK",
    "INTERNET",
    "OPERASIONAL",
    "LAINNYA",
  ];

  const loadExpenses = async () => {
    try {
      setLoading(true);

      const res = await api.get("/finance/expenses");

      setExpenses(
        Array.isArray(res.data)
          ? res.data
          : []
      );
    } catch (err) {
      console.error(err);

      setMessage(
        err?.response?.data?.detail ||
        "Gagal mengambil biaya operasional."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        const res = await api.get("/finance/expenses");

        if (cancelled) return;

        setExpenses(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (err) {
        if (cancelled) return;

        console.error(err);

        setMessage(
          err?.response?.data?.detail ||
          "Gagal mengambil biaya operasional."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [api, setMessage]);

  const resetForm = () => {
    setEditing(null);
    setFormOpen(false);

    setForm({
      nama: "",
      kategori: "LAINNYA",
      nominal: "",
      tanggal: new Date().toISOString().slice(0, 10),
      id_transaksi: "",
      catatan: "",
    });
  };

  const openAdd = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (expense) => {
    setEditing(expense);
    setFormOpen(true);

    setForm({
      nama: expense.nama || "",
      kategori: expense.kategori || "LAINNYA",
      nominal: expense.nominal ?? "",
      tanggal: expense.tanggal
        ? String(expense.tanggal).slice(0, 10)
        : new Date().toISOString().slice(0, 10),
      id_transaksi: expense.id_transaksi || "",
      catatan: expense.catatan || "",
    });
  };

  const saveExpense = async () => {
    if (!form.nama.trim()) {
      setMessage("Nama biaya wajib diisi.");
      return;
    }

    const nominal = Number(form.nominal);

    if (!Number.isFinite(nominal) || nominal <= 0) {
      setMessage("Nominal harus lebih besar dari 0.");
      return;
    }

    try {
      setSaving(true);

      const tanggal = String(form.tanggal || "").trim();

    if (!tanggal) {
      setMessage("Tanggal biaya wajib diisi.");
      return;
    }

    const payload = {
      nama: form.nama.trim(),
      kategori: String(form.kategori || "LAINNYA").trim().toUpperCase(),
      nominal,
      tanggal,
      id_transaksi: form.id_transaksi.trim() || null,
      catatan: form.catatan.trim() || null,
      status: "AKTIF",
    };

      if (editing?.id) {
        await api.put(
          `/finance/expenses/${editing.id}`,
          payload
        );

        setMessage(
          "Biaya operasional berhasil diperbarui."
        );
      } else {
        await api.post(
          "/finance/expenses",
          payload
        );

        setMessage(
          "Biaya operasional berhasil ditambahkan."
        );
      }

      resetForm();
      await loadExpenses();
    } catch (err) {
      console.error("Gagal menyimpan biaya operasional:", err);

      const detail = err?.response?.data?.detail;
      const detailMessage =
        typeof detail === "string"
          ? detail
          : detail?.message ||
            err?.response?.data?.message ||
            err?.message;

      setMessage(
        detailMessage ||
        "Gagal menyimpan biaya operasional."
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteExpense = async (expense) => {
    if (
      !window.confirm(
        `Hapus biaya "${expense.nama}"?`
      )
    ) {
      return;
    }

    try {
      await api.delete(
        `/finance/expenses/${expense.id}`
      );

      await loadExpenses();

      setMessage(
        "Biaya operasional berhasil dihapus."
      );
    } catch (err) {
      console.error(err);

      setMessage(
        err?.response?.data?.detail ||
        "Gagal menghapus biaya operasional."
      );
    }
  };

  const total = expenses
    .filter(
      (x) =>
        String(x.status || "AKTIF").toUpperCase() ===
        "AKTIF"
    )
    .reduce(
      (sum, x) =>
        sum + Number(x.nominal || 0),
      0
    );

  const totalUmum = expenses
    .filter(
      (x) =>
        String(x.status || "AKTIF").toUpperCase() ===
          "AKTIF" &&
        !x.id_transaksi
    )
    .reduce(
      (sum, x) =>
        sum + Number(x.nominal || 0),
      0
    );

  const totalTransaksi = expenses
    .filter(
      (x) =>
        String(x.status || "AKTIF").toUpperCase() ===
          "AKTIF" &&
        x.id_transaksi
    )
    .reduce(
      (sum, x) =>
        sum + Number(x.nominal || 0),
      0
    );

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
        <SummaryCard
          title="Total Operasional"
          value={rupiah(total)}
        />

        <SummaryCard
          title="Operasional Umum"
          value={rupiah(totalUmum)}
        />

        <SummaryCard
          title="Operasional Transaksi"
          value={rupiah(totalTransaksi)}
        />
      </div>

      <div className="rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
        <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-bold">
              Biaya Operasional
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Biaya umum tidak perlu memiliki ID transaksi.
              Biaya yang terkait transaksi dapat dihubungkan
              dengan ID BAPP/transaksi.
            </p>
          </div>

          <button
            type="button"
            onClick={openAdd}
            className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            + Tambah Biaya
          </button>
        </div>

        <div className="p-4">
          {formOpen ? (
            <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="font-bold">
                    {editing
                      ? "Edit Biaya Operasional"
                      : "Tambah Biaya Operasional"}
                  </h3>

                  <p className="mt-1 text-xs text-slate-500">
                    Kosongkan ID transaksi untuk biaya umum
                    seperti gaji admin, entertainment, internet,
                    dan biaya operasional Korwil lainnya.
                  </p>
                </div>

                {editing && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-lg font-bold text-slate-400 hover:text-slate-700"
                  >
                    ×
                  </button>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    Nama Biaya
                  </label>

                  <input
                    value={form.nama}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        nama: e.target.value,
                      })
                    }
                    placeholder="Contoh: Gaji Admin Agustus"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    Kategori
                  </label>

                  <select
                    value={form.kategori}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        kategori: e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {sortFinanceRowsByTransactionNewest(categories).map((item) => (
                      <option
                        key={item}
                        value={item}
                      >
                        {item.replaceAll("_", " ")}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    Nominal
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.nominal}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        nominal: e.target.value,
                      })
                    }
                    placeholder="0"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    Tanggal
                  </label>

                  <input
                    type="date"
                    value={form.tanggal}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        tanggal: e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    ID Transaksi / BAPP
                    <span className="ml-1 text-xs font-normal text-slate-400">
                      (opsional)
                    </span>
                  </label>

                  <input
                    value={form.id_transaksi}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        id_transaksi: e.target.value,
                      })
                    }
                    placeholder="Kosongkan jika biaya umum"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold">
                    Catatan
                  </label>

                  <input
                    value={form.catatan}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        catatan: e.target.value,
                      })
                    }
                    placeholder="Keterangan tambahan"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:bg-slate-100"
                >
                  Batal
                </button>

                <button
                  type="button"
                  onClick={saveExpense}
                  disabled={saving}
                  className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving
                    ? "Menyimpan..."
                    : editing
                      ? "Simpan Perubahan"
                      : "Simpan Biaya"}
                </button>
              </div>
            </div>
          ) : null}

          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">
              Memuat biaya operasional...
            </div>
          ) : expenses.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Belum ada biaya operasional.
            </div>
          ) : (
            <div className="finance-table-scroll overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-slate-700">
                    <th className="px-3 py-3">
                      Tanggal
                    </th>
                    <th className="px-3 py-3">
                      Biaya
                    </th>
                    <th className="px-3 py-3">
                      Kategori
                    </th>
                    <th className="px-3 py-3">
                      ID Transaksi
                    </th>
                    <th className="px-3 py-3 text-right">
                      Nominal
                    </th>
                    <th className="px-3 py-3 text-center">
                      Status
                    </th>
                    <th className="px-3 py-3 text-right">
                      Aksi
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortFinanceRowsByTransactionNewest(expenses).map((expense) => (
                    <tr
                      key={expense.id}
                      className="border-b last:border-b-0"
                    >
                      <td className="px-3 py-3">
                        {expense.tanggal
                          ? String(
                              expense.tanggal
                            ).slice(0, 10)
                          : "-"}
                      </td>

                      <td className="px-3 py-3">
                        <div className="font-semibold">
                          {expense.nama}
                        </div>

                        {expense.catatan && (
                          <div className="mt-1 text-xs text-slate-500">
                            {expense.catatan}
                          </div>
                        )}
                      </td>

                      <td className="px-3 py-3">
                        {String(
                          expense.kategori || "LAINNYA"
                        ).replaceAll("_", " ")}
                      </td>

                      <td className="px-3 py-3 font-mono text-xs">
                        {expense.id_transaksi || (
                          <span className="text-slate-400">
                            Umum KORWIL
                          </span>
                        )}
                      </td>

                      <td className="px-3 py-3 text-right font-semibold">
                        {rupiah(expense.nominal)}
                      </td>

                      <td className="px-3 py-3 text-center">
                        <span
                          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                            String(
                              expense.status || "AKTIF"
                            ).toUpperCase() === "AKTIF"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {String(
                            expense.status || "AKTIF"
                          ).toUpperCase()}
                        </span>
                      </td>

                      <td className="px-3 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              openEdit(expense)
                            }
                            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              deleteExpense(expense)
                            }
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                          >
                            Hapus
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <td
                      colSpan="4"
                      className="px-3 py-3 text-right"
                    >
                      TOTAL AKTIF
                    </td>

                    <td className="px-3 py-3 text-right">
                      {rupiah(total)}
                    </td>

                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MarginSettingsPanel({ api, setMessage }) {
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [selectedMaster, setSelectedMaster] = useState(null);
  const [components, setComponents] = useState([]);

  const normalizeComponents = (rows) =>
    (Array.isArray(rows) ? rows : []).map(
      (item, index) => ({
        nama: item?.nama || "",
        persentase: Number(
          item?.persentase ?? 0
        ),
        aktif: item?.aktif !== false,
        urutan:
          Number(item?.urutan || 0) ||
          index + 1,
      })
    );

  const loadSettings = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        "/finance/margin-settings"
      );

      const rows = Array.isArray(res.data)
        ? res.data
        : [];

      setMasters(rows);

      if (selectedMaster?.id) {
        const refreshed = rows.find(
          (item) =>
            item.id === selectedMaster.id
        );

        if (refreshed) {
          setSelectedMaster(refreshed);
          setComponents(
            normalizeComponents(
              refreshed.components
            )
          );
        }
      }
    } catch (err) {
      console.error(err);

      setMessage(
        err?.response?.data?.detail ||
        "Gagal mengambil konfigurasi pembagian margin."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);

        const res = await api.get(
          "/finance/margin-settings"
        );

        if (cancelled) return;

        setMasters(
          Array.isArray(res.data)
            ? res.data
            : []
        );
      } catch (err) {
        if (cancelled) return;

        console.error(err);

        setMessage(
          err?.response?.data?.detail ||
          "Gagal mengambil konfigurasi pembagian margin."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [api, setMessage]);

  const openMaster = (master) => {
    setSelectedMaster(master);
    setComponents(
      normalizeComponents(
        master?.components
      )
    );
  };

  const startNew = () => {
    setSelectedMaster(null);

    setComponents([
      {
        nama: "Anggota 1",
        persentase: 20,
        aktif: true,
        urutan: 1,
      },
      {
        nama: "Anggota 2",
        persentase: 20,
        aktif: true,
        urutan: 2,
      },
      {
        nama: "Anggota 3",
        persentase: 20,
        aktif: true,
        urutan: 3,
      },
      {
        nama: "Anggota 4",
        persentase: 15,
        aktif: true,
        urutan: 4,
      },
      {
        nama: "Anggota 5",
        persentase: 15,
        aktif: true,
        urutan: 5,
      },
      {
        nama: "Dana Darurat",
        persentase: 10,
        aktif: true,
        urutan: 6,
      },
    ]);
  };

  const cancelEdit = () => {
    setSelectedMaster(null);
    setComponents([]);
  };

  const addComponent = () => {
    setComponents((prev) => [
      ...prev,
      {
        nama: "",
        persentase: 0,
        aktif: true,
        urutan: prev.length + 1,
      },
    ]);
  };

  const updateComponent = (
    index,
    field,
    value
  ) => {
    setComponents((prev) =>
      sortFinanceRowsByTransactionNewest(prev).map((item, i) =>
        i === index
          ? {
              ...item,
              [field]:
                field === "persentase"
                  ? value
                  : value,
            }
          : item
      )
    );
  };

  const removeComponent = (index) => {
    setComponents((prev) =>
      prev
        .filter((_, i) => i !== index)
        .map((item, i) => ({
          ...item,
          urutan: i + 1,
        }))
    );
  };

  const total = components
    .filter(
      (item) => item.aktif !== false
    )
    .reduce(
      (sum, item) =>
        sum + Number(
          item.persentase || 0
        ),
      0
    );

  const totalRounded =
    Math.round(total * 100) / 100;

  const remaining =
    Math.round(
      (100 - totalRounded) * 100
    ) / 100;

  const isValid =
    components.length > 0 &&
    components.every((item) => {
      const pct = Number(
        item.persentase
      );

      return (
        item.nama.trim() !== "" &&
        Number.isFinite(pct) &&
        pct >= 0 &&
        pct <= 100
      );
    }) &&
    totalRounded === 100;

  const saveMaster = async () => {
    if (components.length === 0) {
      setMessage("Minimal harus ada satu komponen.");
      return;
    }

    if (!isValid) {
      setMessage(
        `Total pembagian harus 100%. Saat ini ${totalRounded}%.`
      );
      return;
    }

    const payload = {
      aktif: true,
      components: sortFinanceRowsByTransactionNewest(components).map(
        (item, index) => ({
          nama: item.nama.trim(),
          persentase: Number(item.persentase || 0),
          aktif: item.aktif !== false,
          urutan: index + 1,
        })
      ),
    };

    try {
      setSaving(true);

      if (selectedMaster?.id) {
        await api.put(
          `/finance/margin-settings/${selectedMaster.id}`,
          payload
        );
        setMessage(
          "Master pembagian margin global berhasil diperbarui."
        );
      } else {
        await api.post(
          "/finance/margin-settings",
          payload
        );
        setMessage(
          "Master pembagian margin global berhasil dibuat dan berlaku untuk semua KORWIL."
        );
      }

      cancelEdit();
      await loadSettings();
    } catch (err) {
      console.error(err);
      setMessage(
        err?.response?.data?.detail ||
        "Gagal menyimpan master pembagian margin."
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteMaster = async (master) => {
    if (!master?.id) return;

    if (
      !window.confirm(
        `Hapus Master Pembagian Margin global untuk semua KORWIL?`
      )
    ) {
      return;
    }

    try {
      await api.delete(
        `/finance/margin-settings/${master.id}`
      );

      if (
        selectedMaster?.id ===
        master.id
      ) {
        cancelEdit();
      }

      await loadSettings();

      setMessage(
        "Master pembagian margin berhasil dihapus."
      );
    } catch (err) {
      console.error(err);

      setMessage(
        err?.response?.data?.detail ||
        "Gagal menghapus master pembagian margin."
      );
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
        <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-lg font-bold">
              Master Pembagian Margin
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Satu Master Pembagian Margin untuk seluruh KORWIL.
              Persentase aktif harus tepat 100%.
            </p>
          </div>

          <button
            type="button"
            onClick={startNew}
            className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            + Buat Master Margin
          </button>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="py-10 text-center text-sm text-slate-500">
              Memuat master margin...
            </div>
          ) : masters.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
              Belum ada Master Pembagian Margin.
            </div>
          ) : (
            <div className="finance-table-scroll overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-slate-700">
                    <th className="px-3 py-3">
                      SCOPE
                    </th>
                    <th className="px-3 py-3">
                      Komponen
                    </th>
                    <th className="px-3 py-3 text-right">
                      Total %
                    </th>
                    <th className="px-3 py-3 text-center">
                      Status
                    </th>
                    <th className="px-3 py-3 text-right">
                      Aksi
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortFinanceRowsByTransactionNewest(masters).map((master) => {
                    const rows =
                      normalizeComponents(
                        master.components
                      );

                    const activeTotal =
                      rows
                        .filter(
                          (item) =>
                            item.aktif !== false
                        )
                        .reduce(
                          (sum, item) =>
                            sum +
                            Number(
                              item.persentase || 0
                            ),
                          0
                        );

                    const rounded =
                      Math.round(
                        activeTotal * 100
                      ) / 100;

                    return (
                      <tr
                        key={master.id}
                        className="border-b last:border-b-0"
                      >
                        <td className="px-3 py-3 font-bold">
                          SEMUA KORWIL
                        </td>

                        <td className="px-3 py-3">
                          <div className="space-y-1">
                            {sortFinanceRowsByTransactionNewest(rows).map(
                              (item, index) => (
                                <div
                                  key={`${master.id}-${index}`}
                                  className="text-xs"
                                >
                                  <span className="font-semibold">
                                    {item.nama}
                                  </span>

                                  <span className="ml-2 text-slate-500">
                                    {item.persentase}%
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-3 text-right font-bold">
                          {rounded}%
                        </td>

                        <td className="px-3 py-3 text-center">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              master.aktif === false
                                ? "bg-slate-100 text-slate-500"
                                : rounded === 100
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-amber-100 text-amber-700"
                            }`}
                          >
                            {master.aktif === false
                              ? "NONAKTIF"
                              : rounded === 100
                                ? "AKTIF"
                                : "TOTAL ≠ 100%"}
                          </span>
                        </td>

                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                openMaster(master)
                              }
                              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                deleteMaster(master)
                              }
                              className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                            >
                              Hapus
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {(selectedMaster ||
        components.length > 0) && (
        <div className="rounded-xl border border-slate-700 bg-white text-slate-900 shadow-sm">
          <div className="border-b p-4">
            <h3 className="font-bold">
              {selectedMaster
                ? `Edit Master Pembagian Margin Global`
                : "Buat Master Pembagian Margin"}
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Total komponen aktif wajib tepat 100%.
            </p>
          </div>

          <div className="p-4">
            <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
              <div className="font-semibold">Berlaku untuk semua KORWIL</div>
              <div className="mt-1">
                Master ini dibuat satu kali oleh SUPER_ADMIN dan otomatis digunakan
                sebagai pembagian margin aktif untuk seluruh KORWIL.
              </div>
            </div>

            <div
              className={`mb-4 rounded-lg border px-4 py-3 ${
                totalRounded === 100
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : "border-amber-300 bg-amber-50 text-amber-700"
              }`}
            >
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm font-semibold">
                  Total Aktif:{" "}
                  <span className="text-lg">
                    {totalRounded}%
                  </span>
                </div>

                <div className="text-sm">
                  {remaining === 0
                    ? "Sudah 100%"
                    : remaining > 0
                      ? `Sisa ${remaining}%`
                      : `Kelebihan ${Math.abs(
                          remaining
                        )}%`}
                </div>
              </div>
            </div>

            <div className="finance-table-scroll overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-slate-700">
                    <th className="w-12 px-3 py-3">
                      #
                    </th>

                    <th className="px-3 py-3">
                      Komponen
                    </th>

                    <th className="w-48 px-3 py-3">
                      Persentase
                    </th>

                    <th className="w-28 px-3 py-3 text-center">
                      Aktif
                    </th>

                    <th className="w-24 px-3 py-3 text-right">
                      Aksi
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortFinanceRowsByTransactionNewest(components).map(
                    (item, index) => (
                      <tr
                        key={index}
                        className="border-b last:border-b-0"
                      >
                        <td className="px-3 py-3 text-slate-500">
                          {index + 1}
                        </td>

                        <td className="px-3 py-3">
                          <input
                            value={item.nama}
                            onChange={(e) =>
                              updateComponent(
                                index,
                                "nama",
                                e.target.value
                              )
                            }
                            placeholder="Anggota 1 / Dana Darurat"
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </td>

                        <td className="px-3 py-3">
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={
                                item.persentase
                              }
                              onChange={(e) =>
                                updateComponent(
                                  index,
                                  "persentase",
                                  e.target.value
                                )
                              }
                              className="w-full rounded-lg border border-slate-300 px-3 py-2 pr-8 text-right text-sm outline-none focus:ring-2 focus:ring-blue-500"
                            />

                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                              %
                            </span>
                          </div>
                        </td>

                        <td className="px-3 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={
                              item.aktif !==
                              false
                            }
                            onChange={(e) =>
                              updateComponent(
                                index,
                                "aktif",
                                e.target.checked
                              )
                            }
                            className="h-4 w-4"
                          />
                        </td>

                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            onClick={() =>
                              removeComponent(
                                index
                              )
                            }
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                          >
                            Hapus
                          </button>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={addComponent}
              className="mt-4 rounded-lg border border-blue-200 px-4 py-2 text-sm font-semibold text-blue-600 hover:bg-blue-50"
            >
              + Tambah Komponen
            </button>

            <div className="mt-5 flex flex-col justify-between gap-3 border-t pt-4 md:flex-row md:items-center">
              <div className="text-sm text-slate-500">
                Contoh komponen tambahan:
                <span className="ml-1 font-semibold">
                  Dana Darurat, Bonus, Cadangan,
                  Operasional Internal
                </span>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold hover:bg-slate-50"
                >
                  Batal
                </button>

                <button
                  type="button"
                  onClick={saveMaster}
                  disabled={
                    saving || !isValid
                  }
                  className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving
                    ? "Menyimpan..."
                    : "Simpan Master"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, value }) {
  return (
    <div className="rounded-xl border border-slate-700 bg-white p-4 text-slate-900 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {title}
      </div>

      <div className="mt-2 text-xl font-bold text-slate-900">
        {value}
      </div>
    </div>
  );
}


class FinanceRenderErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('Finance render error:', error, info);
  }

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || String(this.state.error || 'Unknown render error');
      return (
        <div className="min-h-screen bg-slate-950 p-8 text-white">
          <div className="mx-auto max-w-3xl rounded-xl border border-red-500/40 bg-red-950/40 p-6">
            <h1 className="text-xl font-bold text-red-300">Finance gagal ditampilkan</h1>
            <p className="mt-3 text-sm text-red-100">Terjadi error pada komponen Finance. Data tidak diubah.</p>
            <pre className="mt-4 overflow-auto rounded-lg bg-black/40 p-4 text-xs text-red-200">{message}</pre>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function Finance() {
  return (
    <FinanceRenderErrorBoundary>
      <FinancePage />
    </FinanceRenderErrorBoundary>
  );
}
